import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Zahlen-Formatierung für große Zahlen
export const formatNumber = (num: number): string => {
  // NaN und unendliche Werte abfangen
  if (isNaN(num) || !isFinite(num)) {
    console.warn('formatNumber: NaN oder unendlicher Wert erkannt, verwende 0');
    return '0';
  }
  
  try {
    // Negatives Vorzeichen speichern
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    
    // Für kleine Zahlen unter 1000, zeige sie normal an
    if (absNum < 1000) {
      // Für Dezimalzahlen unter 1000, maximal 2 Nachkommastellen
      if (absNum % 1 !== 0) {
        const formatted = parseFloat(absNum.toFixed(2)).toString();
        return isNegative ? `-${formatted}` : formatted;
      }
      return isNegative ? `-${absNum.toString()}` : absNum.toString();
    }
  
  // Definiere alle Suffixe mit ihren Potenzen
  const suffixes = [
    { value: 1e3, suffix: 'K' },      // Tausend
    { value: 1e6, suffix: 'M' },      // Million
    { value: 1e9, suffix: 'B' },      // Milliarde
    { value: 1e12, suffix: 'T' },     // Billion
    { value: 1e15, suffix: 'Q' },     // Quadrillion
    { value: 1e18, suffix: 'Qi' },    // Quintillion
    { value: 1e21, suffix: 'Sx' },    // Sextillion
    { value: 1e24, suffix: 'Sp' },    // Septillion
    { value: 1e27, suffix: 'Oc' },    // Octillion
    { value: 1e30, suffix: 'No' },    // Nonillion
    { value: 1e33, suffix: 'Dc' },    // Decillion
    { value: 1e36, suffix: 'UDc' },   // Undecillion
    { value: 1e39, suffix: 'DDc' },   // Duodecillion
    { value: 1e42, suffix: 'TDc' },   // Tredecillion
    { value: 1e45, suffix: 'QaDc' },  // Quattuordecillion
    { value: 1e48, suffix: 'QiDc' },  // Quindecillion
    { value: 1e51, suffix: 'SxDc' },  // Sexdecillion
    { value: 1e54, suffix: 'SpDc' },  // Septendecillion
    { value: 1e57, suffix: 'OcDc' },  // Octodecillion
    { value: 1e60, suffix: 'NoDc' },  // Novemdecillion
    { value: 1e63, suffix: 'Vg' },    // Vigintillion
    { value: 1e66, suffix: 'UVg' },   // Unvigintillion
    { value: 1e69, suffix: 'DVg' },   // Duovigintillion
    { value: 1e72, suffix: 'TVg' },   // Trevigintillion
    { value: 1e75, suffix: 'QaVg' },  // Quattuorvigintillion
    { value: 1e78, suffix: 'QiVg' },  // Quinvigintillion
    { value: 1e81, suffix: 'SxVg' },  // Sexvigintillion
    { value: 1e84, suffix: 'SpVg' },  // Septenvigintillion
    { value: 1e87, suffix: 'OcVg' },  // Octovigintillion
    { value: 1e90, suffix: 'NoVg' },  // Novemvigintillion
    { value: 1e93, suffix: 'Tg' },    // Trigintillion
    { value: 1e96, suffix: 'UTg' },   // Untrigintillion
    { value: 1e99, suffix: 'DTg' },   // Duotrigintillion
    { value: 1e102, suffix: 'TTg' },  // Tretrigintillion
    { value: 1e105, suffix: 'QaTg' }, // Quattuortrigintillion
    { value: 1e108, suffix: 'QiTg' }, // Quintrigintillion
    { value: 1e111, suffix: 'SxTg' }, // Sextrigintillion
    { value: 1e114, suffix: 'SpTg' }, // Septentrigintillion
    { value: 1e117, suffix: 'OcTg' }, // Octotrigintillion
    { value: 1e120, suffix: 'NoTg' }, // Novemtrigintillion
    { value: 1e123, suffix: 'Qa' },   // Quadragintillion
    { value: 1e126, suffix: 'UQa' },  // Unquadragintillion
    { value: 1e129, suffix: 'DQa' },  // Duoquadragintillion
    { value: 1e132, suffix: 'TQa' },  // Trequadragintillion
    { value: 1e135, suffix: 'QaQa' }, // Quattuorquadragintillion
    { value: 1e138, suffix: 'QiQa' }, // Quinquadragintillion
    { value: 1e141, suffix: 'SxQa' }, // Sexquadragintillion
    { value: 1e144, suffix: 'SpQa' }, // Septenquadragintillion
    { value: 1e147, suffix: 'OcQa' }, // Octoquadragintillion
    { value: 1e150, suffix: 'NoQa' }, // Novemquadragintillion
    { value: 1e153, suffix: 'Qn' },   // Quinquagintillion
    { value: 1e156, suffix: 'UQn' },  // Unquinquagintillion
    { value: 1e159, suffix: 'DQn' },  // Duoquinquagintillion
    { value: 1e162, suffix: 'TQn' },  // Trequinquagintillion
    { value: 1e165, suffix: 'QaQn' }, // Quattuorquinquagintillion
    { value: 1e168, suffix: 'QiQn' }, // Quinquinquagintillion
    { value: 1e171, suffix: 'SxQn' }, // Sexquinquagintillion
    { value: 1e174, suffix: 'SpQn' }, // Septenquinquagintillion
    { value: 1e177, suffix: 'OcQn' }, // Octoquinquagintillion
    { value: 1e180, suffix: 'NoQn' }, // Novemquinquagintillion
    { value: 1e183, suffix: 'Sx' },   // Sexagintillion
    { value: 1e186, suffix: 'USx' },  // Unsexagintillion
    { value: 1e189, suffix: 'DSx' },  // Duosexagintillion
    { value: 1e192, suffix: 'TSx' },  // Tresexagintillion
    { value: 1e195, suffix: 'QaSx' }, // Quattuorsexagintillion
    { value: 1e198, suffix: 'QiSx' }, // Quinsexagintillion
    { value: 1e201, suffix: 'SxSx' }, // Sexsexagintillion
    { value: 1e204, suffix: 'SpSx' }, // Septensexagintillion
    { value: 1e207, suffix: 'OcSx' }, // Octosexagintillion
    { value: 1e210, suffix: 'NoSx' }, // Novemsexagintillion
    { value: 1e213, suffix: 'Sp' },   // Septuagintillion
    { value: 1e216, suffix: 'USp' },  // Unseptuagintillion
    { value: 1e219, suffix: 'DSp' },  // Duoseptuagintillion
    { value: 1e222, suffix: 'TSp' },  // Treseptuagintillion
    { value: 1e225, suffix: 'QaSp' }, // Quattuorseptuagintillion
    { value: 1e228, suffix: 'QiSp' }, // Quinseptuagintillion
    { value: 1e231, suffix: 'SxSp' }, // Sexseptuagintillion
    { value: 1e234, suffix: 'SpSp' }, // Septenseptuagintillion
    { value: 1e237, suffix: 'OcSp' }, // Octoseptuagintillion
    { value: 1e240, suffix: 'NoSp' }, // Novemseptuagintillion
    { value: 1e243, suffix: 'Oc' },   // Octogintillion
    { value: 1e246, suffix: 'UOc' },  // Unoctogintillion
    { value: 1e249, suffix: 'DOc' },  // Duooctogintillion
    { value: 1e252, suffix: 'TOc' },  // Treoctogintillion
    { value: 1e255, suffix: 'QaOc' }, // Quattuoroctogintillion
    { value: 1e258, suffix: 'QiOc' }, // Quinoctogintillion
    { value: 1e261, suffix: 'SxOc' }, // Sexoctogintillion
    { value: 1e264, suffix: 'SpOc' }, // Septenoctogintillion
    { value: 1e267, suffix: 'OcOc' }, // Octooctogintillion
    { value: 1e270, suffix: 'NoOc' }, // Novemoctogintillion
    { value: 1e273, suffix: 'No' },   // Nonagintillion
    { value: 1e276, suffix: 'UNo' },  // Unnonagintillion
    { value: 1e279, suffix: 'DNo' },  // Duononagintillion
    { value: 1e282, suffix: 'TNo' },  // Trenonagintillion
    { value: 1e285, suffix: 'QaNo' }, // Quattuornonagintillion
    { value: 1e288, suffix: 'QiNo' }, // Quinnonagintillion
    { value: 1e291, suffix: 'SxNo' }, // Sexnonagintillion
    { value: 1e294, suffix: 'SpNo' }, // Septennonagintillion
    { value: 1e297, suffix: 'OcNo' }, // Octononagintillion
    { value: 1e300, suffix: 'NoNo' }, // Novemnonagintillion
    { value: 1e303, suffix: 'Ce' },   // Centillion
  ];
  
  // Finde das passende Suffix (rückwärts durchsuchen für größte Zahl)
  for (let i = suffixes.length - 1; i >= 0; i--) {
    if (absNum >= suffixes[i].value) {
      const value = absNum / suffixes[i].value;
      // Verwende maximal 2 Nachkommastellen und entferne unnötige Nullen
      const formatted = parseFloat(value.toFixed(2)).toString();
      const result = formatted + suffixes[i].suffix;
      return isNegative ? `-${result}` : result;
    }
  }
  
  // Fallback für extrem große Zahlen (über Centillion hinaus)
  if (absNum >= 1e306) {
    const value = absNum / 1e303;
    const formatted = parseFloat(value.toFixed(2)).toString();
    const result = formatted + 'Ce+';
    return isNegative ? `-${result}` : result;
  }
  
  const result = absNum.toString();
  return isNegative ? `-${result}` : result;
  } catch (error) {
    console.error('Fehler in formatNumber:', error);
    return '0';
  }
};

// Begleiter-System
export interface Companion {
  id: string;
  name: string;
  type: 'legendary' | 'epic' | 'rare' | 'common';
  icon: string;
  description: string;
  abilities: CompanionAbility[];
  discoveredAt: string; // Datum der Entdeckung
  // Training und Aktivitäten
  level?: number; // Begleiter-Level (Standard: 1)
  experience?: number; // Begleiter-Erfahrung
  isTraining?: boolean; // Gerade im Training
  trainingStartTime?: string; // Wann Training gestartet
  trainingDuration?: number; // Training-Dauer in Minuten
  isOnJourney?: boolean; // Auf Reise
  journeyType?: 'treasure' | 'renovation' | 'experience'; // Art der Reise
  journeyStartTime?: string; // Wann Reise gestartet
  journeyDuration?: number; // Reise-Dauer in Stunden
  journeyArea?: keyof GameState['gardenAreas']; // Für Renovierungs-Reisen
  journeyStartLevel?: number; // Level des Bereichs beim Start der Renovierungs-Reise
  // Arena-Kampf System
  battleLevel?: number; // Kampf-Level (Standard: 1)
  battleExperience?: number; // Kampf-Erfahrung
}

export interface CompanionAbility {
  type: 'clickMultiplier' | 'coinPerSecond' | 'gemChance' | 'xpBonus' | 'gardenBonus' | 'autoClickPower' | 'upgradeDiscount' | 'prestigeBonus' | 'criticalHit' | 'healing' | 'shield' | 'counterAttack' | 'lifeSteal' | 'armorPenetration';
  value: number;
  description: string;
}

export interface GameState {
  // Ressourcen
  coins: number;
  gems: number;
  experience: number; // Neue Ressource
  
  // Prestige-System
  prestige: {
    level: number;
    points: number;
    totalPoints: number;
    permanentBonuses: {
      clickMultiplier: number;
      incomeMultiplier: number;
      xpMultiplier: number;
      startingCoins: number;
      gemChance: number;
      autoClickerEfficiency: number;
      upgradeDiscount: number;
      gardenEfficiency: number;
    };
    lastPrestigeDate: string;
    totalPrestiges: number;
    // Mega-Prestige System
    megaPrestige: {
      level: number;
      totalPointsSpent: number;
      globalMultiplier: number; // 1% pro Million Punkte
      lastMegaPrestigeDate: string;
    };
  };
  
  // Clicker-System
  clickPower: number;
  autoClickPower: number;
  autoClickInterval: number;
  
  // Premium Auto-Klicker
  premiumAutoClicker: {
    isActive: boolean;
    costPerSecond: number; // Diamanten pro Sekunde
    lastTickTime: number; // Für präzise Timing
    clicksPerSecond: number; // Einstellbare Klicks pro Sekunde (1 Klick = 1000 Diamanten/s)
  };
  
  // Erweiterte Power-Ups
  powerUps: {
    goldRush: { isActive: boolean; timeLeft: number; endTime?: number }; // Doppelte Münzen
    speedBoost: { isActive: boolean; timeLeft: number; endTime?: number }; // Schnellere Auto-Clicks
    luckyCharm: { isActive: boolean; timeLeft: number; endTime?: number }; // Chance auf Edelsteine
    // Neue Power-Ups
    megaMultiplier: { isActive: boolean; timeLeft: number; endTime?: number }; // 5x Klick-Multiplier
    gemStorm: { isActive: boolean; timeLeft: number; endTime?: number }; // Edelstein-Regen
    timeWarp: { isActive: boolean; timeLeft: number; endTime?: number }; // 10x Geschwindigkeit
    gardenBlessing: { isActive: boolean; timeLeft: number; endTime?: number }; // 3x Garten-Boni
    clickFrenzy: { isActive: boolean; timeLeft: number; endTime?: number }; // Auto-Klicks werden zu Mega-Klicks
    diamondMidas: { isActive: boolean; timeLeft: number; endTime?: number }; // Alles wird zu Edelsteinen
    infiniteEnergy: { isActive: boolean; timeLeft: number; endTime?: number }; // Unendliche Klick-Power
    cosmicBoost: { isActive: boolean; timeLeft: number; endTime?: number }; // Alle Boni x10
    dragonPower: { isActive: boolean; timeLeft: number; endTime?: number }; // Legendärer Drachen-Boost
    experienceRush: { isActive: boolean; timeLeft: number; endTime?: number }; // 100x EP-Bonus
  };
  
  // Erweiterte Upgrades
  upgrades: {
    shovelLevel: number;
    autoDiggerLevel: number;
    fertilizer: number;
    wateringCan: number;
    // Neue Upgrades
    magicSeeds: number; // Mehr XP pro Renovierung
    gardenGnome: number; // Zufällige Boni
    rainMaker: number; // Automatische Wassering
    composter: number; // Recycelt Münzen zu Edelsteinen
    // Weitere Werkzeuge
    goldDetector: number; // Findet versteckte Münzen
    timeAccelerator: number; // Beschleunigt alle Prozesse
    luckCharm: number; // Erhöht Glückschancen
    autoHarvester: number; // Sammelt automatisch Ressourcen
    megaMultiplier: number; // Verstärkt alle Multiplikatoren
    crystalGenerator: number; // Erzeugt Edelsteine über Zeit
    quantumShovel: number; // Klicks wirken mehrfach
    gardenBot: number; // Automatische Gartenarbeit
    // Garten-spezifische Werkzeuge
    plantWhisperer: number; // Verstärkt Gartenbereiche
    seedVault: number; // Lagert seltene Samen
    soilAnalyzer: number; // Optimiert Bodenqualität
    weatherStation: number; // Kontrolliert Wetter für Boni
    beeHive: number; // Bestäubung für Bonuserträge
    treeFarm: number; // Pflanzt automatisch Bäume
    flowerPower: number; // Blumen geben Glücks-Boni
    herbGarden: number; // Kräuter für Heilung und Boni
    butterflyGarden: number; // Schmetterlinge für Schönheitsboni
    rockGarden: number; // Steine für Stabilität und Ruhe
    waterFeature: number; // Wasserspiele für Entspannung
    gardenLighting: number; // Beleuchtung für Nachtarbeit
    [key: string]: number; // Für prozedurale Werkzeuge
  };
  
  // Prozedurale Werkzeuge
  proceduralTools: {
    [key: string]: {
      name: string;
      description: string;
      baseCost: number;
      effect: string;
      discoveredAt: string;
      baseEffect: number;
      effectType: string;
    };
  };
  
  // Prozedurale Klick-Werkzeuge
  proceduralClickTools: {
    [key: string]: {
      name: string;
      description: string;
      baseCost: number;
      effect: string;
      discoveredAt: string;
      baseEffect: number;
      effectType: string;
    };
  };
  
  // Gartenbereiche mit neuen Features
  gardenAreas: {
    lawn: { level: number; maxLevel: number; lastBonus: number };
    flowerBed: { level: number; maxLevel: number; lastBonus: number };
    pond: { level: number; maxLevel: number; lastBonus: number };
    terrace: { level: number; maxLevel: number; lastBonus: number };
    greenhouse: { level: number; maxLevel: number; lastBonus: number };
    fountain: { level: number; maxLevel: number; lastBonus: number };
    // Basis Bereiche
    orchard: { level: number; maxLevel: number; lastBonus: number }; // Obstgarten
    veggiePatch: { level: number; maxLevel: number; lastBonus: number }; // Gemüsebeet
    // Erweiterte Bereiche
    roseGarden: { level: number; maxLevel: number; lastBonus: number }; // Rosengarten  
    japaneseGarden: { level: number; maxLevel: number; lastBonus: number }; // Zen-Garten
    wildflowerMeadow: { level: number; maxLevel: number; lastBonus: number }; // Wildblumenwiese
    sculptureGarden: { level: number; maxLevel: number; lastBonus: number }; // Skulpturengarten
    // Premium Bereiche
    enchantedGrove: { level: number; maxLevel: number; lastBonus: number }; // Verzauberter Hain
    crystalCave: { level: number; maxLevel: number; lastBonus: number }; // Kristallhöhle
    moonlightGarden: { level: number; maxLevel: number; lastBonus: number }; // Mondschein-Garten
    dragonLair: { level: number; maxLevel: number; lastBonus: number }; // Drachenhort
    // Klick-fokussierte Bereiche
    goldMine: { level: number; maxLevel: number; lastBonus: number }; // Goldmine - Münzen pro Klick
    treasureVault: { level: number; maxLevel: number; lastBonus: number }; // Schatzkammer - Münzen pro Klick
    alchemyLab: { level: number; maxLevel: number; lastBonus: number }; // Alchemie-Labor - Münzen pro Klick
    magicForge: { level: number; maxLevel: number; lastBonus: number }; // Magische Schmiede - Münzen pro Klick
  };
  
  // 3-Gewinnt Status
  matchThreeGame: {
    isUnlocked: boolean;
    currentLevel: number;
    completedLevels: number[];
    highScore: number;
    consecutiveWins: number; // Für Bonus-Multiplier
    powerUpsUnlocked: string[];
  };
  

  
  // Erweiterte Achievements
  achievements: {
    totalClicks: number;
    totalCoinsEarned: number;
    areasRenovated: number;
    matchThreeWins: number;
    // Neue Achievements
    upgradesPurchased: number;
    powerUpsUsed: number;
    perfectMatchThreeGames: number;
    dailyStreakRecord: number;
    totalExperienceGained: number;
  };
  
  // Spieler-Level-System
  playerLevel: number;
  
  // Begleiter-System
  companions: {
    discovered: Companion[];
    active: Companion[]; // Bis zu 3 aktive Begleiter
    pendingCards: Companion[] | null; // Für die Kartenauswahl nach Prestige
    freeDrawLastUsed: string | null; // Letzter kostenloser Zug
    freeDrawAvailable: boolean; // Ob kostenloser Zug verfügbar ist
    freePremiumSpinLastUsed: string | null; // Letzter kostenloser Premium-Spin
    freePremiumSpinAvailable: boolean; // Ob kostenloser Premium-Spin verfügbar ist
    freePremiumSpinActive: boolean; // Ob gerade ein kostenloser Premium-Spin aktiv ist
    training: string[]; // IDs der Begleiter im Training
    research: {
      // Forschungs-Projekte
      projects: {
        [projectId: string]: {
          id: string;
          name: string;
          description: string;
          cost: number;
          duration: number; // in Minuten
          isActive: boolean;
          startTime?: string;
          bonusType: 'attack' | 'defense' | 'hp' | 'experience' | 'abilityChance';
          bonusValue: number;
          completed: boolean;
        };
      };
      // Globale Boni (additiv zu allen Begleitern)
      globalBonuses: {
        attack: number;
        defense: number;
        hp: number;
        experience: number;
        abilityChance: number;
      };
      // Skill-Tree-Reset-System
      skillTreeReset: {
        resetCount: number; // Anzahl der Resets
        permanentBonuses: { // Permanente Boni aus vorherigen Resets
          attack: number;
          defense: number;
          hp: number;
          experience: number;
          abilityChance: number;
        };
        lastResetDate: string | null;
      };
    };
    
    // Schmiede-System
    forge: {
      // Erz-Ressourcen
      ore: {
        iron: number;
        steel: number;
        mithril: number;
        adamantium: number;
      };
      // Rüstungen
      armor: {
        [armorId: string]: {
          id: string;
          name: string;
          type: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield';
          material: 'iron' | 'steel' | 'mithril' | 'adamantium';
          level: number; // Level der Rüstung (1, 2, 3, etc.)
          defense: number;
          durability: number;
          maxDurability: number;
          equippedBy?: string; // Begleiter-ID
          isCrafting: boolean;
          craftingStartTime?: string;
          craftingDuration: number; // in Minuten
        };
      };
      // Waffen
      weapons: {
        [weaponId: string]: {
          id: string;
          name: string;
          type: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff';
          material: 'iron' | 'steel' | 'mithril' | 'adamantium';
          level: number; // Level der Waffe (1, 2, 3, etc.)
          attack: number;
          durability: number;
          maxDurability: number;
          equippedBy?: string; // Begleiter-ID
          isCrafting: boolean;
          craftingStartTime?: string;
          craftingDuration: number; // in Minuten
        };
      };
      // Erz-Abbau
      mining: {
        activeMiners: {
          [companionId: string]: {
            startTime: string;
            duration: number; // in Minuten
            oreType: 'iron' | 'steel' | 'mithril' | 'adamantium';
            efficiency: number; // Basierend auf Begleiter-Level
          };
        };
      };
    };
    arena: {
      level: number; // Aktuelles Arena-Level
      difficultyMultiplier: number; // Schwierigkeits-Multiplikator (1.0 = normal, 1.01 = 1% schwerer, etc.)
      resetCount: number; // Anzahl der Arena-Resets
    };
    conquest: {
      activeConquests: {
        [companionId: string]: {
          startTime: string; // Wann die Eroberung gestartet wurde
          duration: number; // Dauer in Minuten
          area: string; // Name des eroberten Gebiets
        };
      };
      territoryConquestCounts: { [territoryName: string]: number }; // Wie oft jedes Territorium erobert wurde
    };
  };
  
  // Einstellungen
  settings: {
    musicEnabled: boolean;
    soundEnabled: boolean;
    autoSaveEnabled: boolean;
    notifications: boolean;
    performanceMonitorEnabled: boolean; // Performance-Monitor aktivieren/deaktivieren
  };

  // Premium-System
  globalMultipliers: {
    totalMultiplier: number; // Gesamter Multiplikator durch Opferungen
    sacrificeCount: number; // Anzahl der Opferungen
    [key: string]: number;
  };
  
  // Video-Werbung System
  videoRewards: {
    isActive: boolean; // Ob gerade ein Video-Werbung läuft
    accumulatedTime: number; // Angesammelte Zeit in Minuten (max 120)
    lastVideoTime: string | null; // Zeitpunkt des letzten Videos
    videoCooldown: number; // Cooldown in Minuten zwischen Videos
  };
  
  // Börse-System
  stockMarket: {
    portfolio: Array<{
      id: string;
      stockId: string;
      shares: number;
      currency: 'coins' | 'gems';
    }>;
    tradeHistory: Array<{
      id: string;
      stockId: string;
      type: 'buy' | 'sell';
      amount: number;
      price: number;
      timestamp: string;
      currency: 'coins' | 'gems';
    }>;
    // Aktuelle Aktienpreise (persistent gespeichert) - Optimiert für Speicherplatz
    stockPrices: {
      [stockId: string]: {
        currentPrice: number;
        previousPrice: number;
        change: number;
        changePercent: number;
        volume: number;
        high24h: number;
        low24h: number;
        marketCap: number;
        // Reduzierte Preisverlauf-Daten für Speicherplatz-Optimierung
        priceHistory: Array<{ timestamp: number; price: number }>;
        lastUpdate: number;
        // Zusätzliche Metadaten für bessere Performance
        volatility: number;
        trend: 'up' | 'down' | 'stable';
      };
    };
    // Globales Aktienmarkt-System für alle Spieler
    globalMarket: {
      totalSharesOwned: { [stockId: string]: number }; // Gesamte Anteile aller Spieler pro Aktie
      virtualPlayers: Array<{
        id: string;
        name: string;
        portfolio: Array<{
          stockId: string;
          shares: number;
          currency: 'coins' | 'gems';
        }>;
        lastTradeTime: string;
        budget: {
          coins: number;
          gems: number;
        };
        tradingStyle: 'aggressive' | 'conservative';
        riskTolerance: number; // 0-1, höher = risikofreudiger
      }>;
      marketActivity: Array<{
        id: string;
        stockId: string;
        type: 'buy' | 'sell';
        amount: number;
        price: number;
        timestamp: string;
        currency: 'coins' | 'gems';
        playerType: 'real' | 'virtual';
      }>;
    };
  };
  
  // Actions
  addCoins: (amount: number) => void;
  addGems: (amount: number) => void;
  addExperience: (amount: number) => void;
  ensureLevelCompatibility: () => void;
  spendCoins: (amount: number) => boolean;
  spendGems: (amount: number) => boolean;
  upgradeClickPower: () => void;
  upgradeAutoClicker: () => void;
  purchaseUpgrade: (upgradeType: keyof GameState['upgrades']) => boolean;
  purchaseUpgradeBulk: (upgradeType: keyof GameState['upgrades'], quantity: number) => { success: boolean; actualPurchases: number; totalCost: number };
  renovateArea: (area: keyof GameState['gardenAreas']) => boolean;
  renovateAreaBulk: (area: keyof GameState['gardenAreas'], quantity: number) => { success: boolean; actualPurchases: number; totalCost: number };
  unlockMatchThree: () => void;
  completeMatchThreeLevel: (level: number, score: number, isPerfect?: boolean) => void;
  incrementClick: () => void;
  activatePowerUp: (powerUpType: keyof GameState['powerUps'], duration: number) => void;
  updatePowerUps: () => void;

  calculatePlayerLevel: () => void;
  getUpgradeCost: (upgradeType: string, currentLevel: number) => number;
  getAreaBonus: (area: keyof GameState['gardenAreas']) => number;
  // Neue Hilfsfunktionen
  getTotalCoinsPerSecond: () => number;
  getAutoGraberBonusFromStocks: () => number;
  getClickMultiplier: () => number;
  getLevelProgress: () => { currentXP: number; nextLevelXP: number; progress: number };
  repairGardenAreas: () => void;
  
        // Prestige-Funktionen
      canPrestige: () => boolean;
      calculatePrestigePoints: () => number;
      performPrestige: () => void;
      purchasePrestigeUpgrade: (upgradeType: keyof GameState['prestige']['permanentBonuses']) => boolean;
      purchasePrestigeUpgradeBulk: (upgradeType: keyof GameState['prestige']['permanentBonuses'], quantity: number) => { success: boolean; actualPurchases: number; totalCost: number };
      getPrestigeUpgradeCost: (upgradeType: keyof GameState['prestige']['permanentBonuses'], currentLevel: number) => number;
      
      // Prozedurale Werkzeug-Generierung
      generateProceduralTool: () => { key: string; name: string; description: string; baseCost: number; effect: string; baseEffect: number; effectType: string };
      addProceduralTool: (tool: { key: string; name: string; description: string; baseCost: number; effect: string; baseEffect: number; effectType: string }) => void;
      
      // Prozedurale Klick-Werkzeug-Generierung
      generateProceduralClickTool: () => { key: string; name: string; description: string; baseCost: number; effect: string; baseEffect: number; effectType: string };
      addProceduralClickTool: (tool: { key: string; name: string; description: string; baseCost: number; effect: string; baseEffect: number; effectType: string }) => void;
  
  // Mega-Prestige-Funktionen
  canMegaPrestige: () => boolean;
  performMegaPrestige: () => void;
  getMegaPrestigeMultiplier: () => number;
  
  // Begleiter-Funktionen
  generateCompanionCards: () => Companion[];
  checkAndUnlockDolfjinmann: () => void;
  checkAndUnlockSteve: () => void;
  checkAndUpgradeSteve: () => void;
  startCompanionCardDraw: (cost: number) => boolean;
  selectCompanionCard: (companion: Companion) => boolean;
  setActiveCompanion: (companion: Companion, slot?: number) => void;
  removeActiveCompanion: (slot: number) => void;
  removeAllActiveCompanions: () => void;
  getCompanionBonus: (abilityType: CompanionAbility['type']) => number;
  addCompanionToCollection: (companion: Companion) => void;
  upgradeCompanions: (type: Companion['type'], specificIds?: string[]) => boolean;
  upgradeToMysticDivine: (specificIds?: string[]) => boolean;
  getCompanionsByType: (type: Companion['type']) => Companion[];
  
  // Begleiter Training & Reisen
  startCompanionTraining: (companionId: string, durationMinutes: number) => boolean;
  completeCompanionTraining: (companionId: string) => void;
  startCompanionJourney: (companionId: string, journeyType: 'treasure' | 'renovation' | 'experience', durationHours: number, area?: keyof GameState['gardenAreas']) => boolean;
  completeCompanionJourney: (companionId: string) => void;
  updateCompanionActivities: () => void;
  getCompanionLevel: (companion: Companion) => number;
  getCompanionExperience: (companion: Companion) => number;
  getCompanionExperienceForNextLevel: (level: number) => number;
  canCompanionLevelUp: (companion: Companion) => boolean;
  levelUpCompanion: (companionId: string) => boolean;
  
  // Formatierung
  formatNumber: (num: number) => string;
  
  // Begleiter-Slot-System
  getMaxCompanionSlots: () => number;
  
  // Kostenloser Begleiter-Zug
  canUseFreeCompanionDraw: () => boolean;
  useFreeCompanionDraw: () => void;
  
        // Kostenloser Premium-Spin
      canUseFreePremiumSpin: () => boolean;
      useFreePremiumSpin: () => void;
      setFreePremiumSpinActive: (active: boolean) => void;
      
      // Cooldown-Überprüfung
      updateCooldowns: () => void;
      
      // Premium Auto-Klicker
      togglePremiumAutoClicker: () => void;
      updatePremiumAutoClicker: () => void;
      setPremiumAutoClickerSpeed: (clicksPerSecond: number) => void;
      
      // Premium-Shop
      setGlobalMultiplier: (multiplier: number) => void;
      
        // Opfer-System
  sacrificeCompanion: (companionId: string, multiplier?: number) => void;
  
  // Arena-Kampf-System
  updateCompanionBattleStats: (companionId: string, battleLevel: number, battleExperience: number) => void;
  updateCompanionNormalStats: (companionId: string, level: number, experience: number) => void;
  addCompanionAbility: (companionId: string, ability: CompanionAbility) => void;
  
        // Training-System
      getMaxTrainingCapacity: () => number;
      addToTraining: (companionId: string) => void;
      removeFromTraining: (companionId: string) => void;
      getTrainingCost: () => number;
      canAffordTraining: () => boolean;
      updateTraining: () => void;
      
      // Forschungs-System
      startResearch: (projectId: string) => boolean;
      completeResearch: (projectId: string) => void;
      updateResearch: () => void;
      getResearchProjects: () => Array<{
        id: string;
        name: string;
        description: string;
        cost: number;
        duration: number;
        isActive: boolean;
        startTime?: string;
        bonusType: 'attack' | 'defense' | 'hp' | 'experience' | 'abilityChance';
        bonusValue: number;
        completed: boolean;
      }>;
      getGlobalBonuses: () => {
        attack: number;
        defense: number;
        hp: number;
        experience: number;
        abilityChance: number;
      };

      // Skill-Tree-Reset-Funktionen
      canResetSkillTree: () => boolean;
      resetSkillTree: () => boolean;
      getSkillTreeResetInfo: () => {
        resetCount: number;
        permanentBonuses: {
          attack: number;
          defense: number;
          hp: number;
          experience: number;
          abilityChance: number;
        };
        lastResetDate: string | null;
        canReset: boolean;
      };

      // Schmiede-System
      startMining: (companionId: string, oreType: 'iron' | 'steel' | 'mithril' | 'adamantium', duration: number) => boolean;
      completeMining: (companionId: string) => void;
      removeFromMining: (companionId: string) => void;
      updateMining: () => void;
      updateContinuousMining: () => void;
      getMiningProgress: (companionId: string) => number;
      isCompanionMining: (companionId: string) => boolean;
      
      craftArmor: (armorType: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => boolean;
      completeArmorCrafting: (armorId: string) => void;
      updateArmorCrafting: () => void;
      getArmorCraftingProgress: (armorId: string) => number;
      
      equipArmor: (armorId: string, companionId: string) => boolean;
      unequipArmor: (armorId: string) => void;
      getCompanionArmor: (companionId: string) => Array<{
        id: string;
        name: string;
        type: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield';
        material: 'iron' | 'steel' | 'mithril' | 'adamantium';
        defense: number;
        durability: number;
        maxDurability: number;
      }>;
      
      // Waffen-System
      craftWeapon: (weaponType: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => boolean;
      completeWeaponCrafting: (weaponId: string) => void;
      updateWeaponCrafting: () => void;
      getWeaponCraftingProgress: (weaponId: string) => number;
      
      equipWeapon: (weaponId: string, companionId: string) => boolean;
      unequipWeapon: (weaponId: string) => void;
      getCompanionWeapons: (companionId: string) => Array<{
        id: string;
        name: string;
        type: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff';
        material: 'iron' | 'steel' | 'mithril' | 'adamantium';
        attack: number;
        durability: number;
        maxDurability: number;
      }>;
      
      // Gegenstände gegen Erfahrung eintauschen
      tradeItemForExperience: (itemId: string, itemType: 'armor' | 'weapon', companionId: string, xpType?: 'normal' | 'arena') => boolean;
      getItemExperienceValue: (item: any, itemType: 'armor' | 'weapon') => number;
      addCompanionExperience: (companionId: string, experience: number) => void;
      addCompanionBattleExperience: (companionId: string, battleExperience: number) => void;
      getCraftableWeapons: () => Array<{
        type: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff';
        material: 'iron' | 'steel' | 'mithril' | 'adamantium';
        name: string;
        description: string;
        attack: number;
        durability: number;
        cost: {
          iron: number;
          steel: number;
          mithril: number;
          adamantium: number;
        };
        craftingTime: number;
      }>;
      
      getOreCounts: () => {
        iron: number;
        steel: number;
        mithril: number;
        adamantium: number;
      };
      getCraftableArmor: () => Array<{
        type: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield';
        material: 'iron' | 'steel' | 'mithril' | 'adamantium';
        name: string;
        description: string;
        defense: number;
        durability: number;
        cost: {
          iron: number;
          steel: number;
          mithril: number;
          adamantium: number;
        };
        craftingTime: number;
      }>;
      
      // Haltbarkeits-Reduktion nach Arena-Kämpfen
      reduceArmorDurability: (armorId: string, amount: number) => void;
      reduceWeaponDurability: (weaponId: string, amount: number) => void;
      
      // Reset-Funktionen für Rüstungen und Waffen
      resetArmorLevel: (armorType: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => void;
      resetWeaponLevel: (weaponType: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => void;
  
        // Arena-System
      getArenaLevel: () => number;
      setArenaLevel: (level: number) => void;
      getArenaDifficultyMultiplier: () => number;
      getArenaResetCount: () => number;
      canResetArena: () => boolean;
      resetArena: (selectedCompanionIds?: string[]) => void;
  
  // Eroberungs-System
  startConquest: (companionId: string, area: string, duration: number) => boolean;
  completeConquest: (companionId: string) => void;
  getConquestTimeRemaining: (companionId: string) => number;
  isCompanionOnConquest: (companionId: string) => boolean;
  updateConquests: () => void;
  
  // Spiel zurücksetzen
  resetGame: () => void;
  
        // Einstellungen
      toggleMusic: () => void;
      
        // Offline-Einkommen
  updatePlayerActivity: () => void;
  clearOfflineIncome: () => void;
      
        // Video-Werbung System
  startVideoReward: () => boolean;
  completeVideoReward: () => void;
  updateVideoRewardTime: () => void;
  canWatchVideo: () => boolean;
  getVideoRewardTimeRemaining: () => number;
  getAccumulatedVideoTime: () => number;
  getMaxVideoTime: () => number;
  
  // Leaderboard System
  submitToLeaderboard: (username: string) => Promise<boolean>;
  getLeaderboardRank: (username: string, sortBy?: string) => Promise<number | null>;
  
  // Börse-System
  addStockToPortfolio: (stockId: string, shares: number, currency: 'coins' | 'gems') => void;
  removeStockFromPortfolio: (stockId: string, shares: number, currency: 'coins' | 'gems') => void;
  addTradeToHistory: (stockId: string, type: 'buy' | 'sell', amount: number, price: number, currency: 'coins' | 'gems') => void;
  getStockPortfolio: () => Array<{
    id: string;
    stockId: string;
    shares: number;
    currency: 'coins' | 'gems';
  }>;
  getStockTradeHistory: () => Array<{
    id: string;
    stockId: string;
    type: 'buy' | 'sell';
    amount: number;
    price: number;
    timestamp: string;
    currency: 'coins' | 'gems';
  }>;

  // Globale Aktienmarkt-Funktionen
  getGlobalSharesOwned: (stockId: string) => number;
  updateGlobalSharesOwned: (stockId: string, amount: number, type: 'buy' | 'sell') => void;
  addGlobalMarketActivity: (stockId: string, type: 'buy' | 'sell', amount: number, price: number, currency: 'coins' | 'gems', playerType: 'real' | 'virtual') => void;
  getVirtualPlayers: () => Array<{
    id: string;
    name: string;
    portfolio: Array<{
      stockId: string;
      shares: number;
      currency: 'coins' | 'gems';
    }>;
    lastTradeTime: string;
    budget: {
      coins: number;
      gems: number;
    };
    tradingStyle: 'aggressive' | 'conservative';
    riskTolerance: number;
  }>;

  addVirtualPlayer: (player: {
    id: string;
    name: string;
    portfolio: Array<{
      stockId: string;
      shares: number;
      currency: 'coins' | 'gems';
    }>;
    lastTradeTime: string;
    budget: {
      coins: number;
      gems: number;
    };
    tradingStyle: 'aggressive' | 'conservative';
    riskTolerance: number;
  }) => void;
  updateVirtualPlayerPortfolio: (playerId: string, stockId: string, shares: number, currency: 'coins' | 'gems') => void;

  // Aktienpreis-Hilfsfunktionen
  updateStockPrice: (stockId: string, priceData: {
    currentPrice: number;
    previousPrice: number;
    change: number;
    changePercent: number;
    volume: number;
    high24h: number;
    low24h: number;
    marketCap: number;
    priceHistory: Array<{ timestamp: number; price: number }>;
  }) => void;
  getStockPrice: (stockId: string) => {
    currentPrice: number;
    previousPrice: number;
    change: number;
    changePercent: number;
    volume: number;
    high24h: number;
    low24h: number;
    marketCap: number;
    priceHistory: Array<{ timestamp: number; price: number }>;
    lastUpdate: number;
    volatility: number;
    trend: 'up' | 'down' | 'stable';
  } | null;
  getAllStockPrices: () => { [stockId: string]: {
    currentPrice: number;
    previousPrice: number;
    change: number;
    changePercent: number;
    volume: number;
    high24h: number;
    low24h: number;
    marketCap: number;
    priceHistory: Array<{ timestamp: number; price: number }>;
    lastUpdate: number;
    volatility: number;
    trend: 'up' | 'down' | 'stable';
  } };
  initializeStockPrices: (stockData: Array<{
    id: string;
    currentPrice: number;
    previousPrice: number;
    change: number;
    changePercent: number;
    volume: number;
    high24h: number;
    low24h: number;
    marketCap: number;
    priceHistory: Array<{ timestamp: number; price: number }>;
  }>) => void;

  // Offline-Einkommen System
  lastSaveTime: number;
  offlineEndTime: number; // Endzeitpunkt für präzise Offline-Berechnung
  lastActivityTime: number; // Letzte Aktivität des Spielers
  offlineIncome: number;
  offlineGems: number;
  offlineCompanion: Companion | null;
  
  // Performance-Cache für hohe Level
  _coinsPerSecondCache?: {
    key: string;
    value: number;
    timestamp: number;
  };
  _clickMultiplierCache?: {
    key: string;
    value: number;
    timestamp: number;
  };
}

const initialGardenAreas = {
  lawn: { level: 0, maxLevel: 1000, lastBonus: 0 },
  flowerBed: { level: 0, maxLevel: 1000, lastBonus: 0 },
  pond: { level: 0, maxLevel: 1000, lastBonus: 0 },
  terrace: { level: 0, maxLevel: 1000, lastBonus: 0 },
  greenhouse: { level: 0, maxLevel: 1000, lastBonus: 0 },
  fountain: { level: 0, maxLevel: 1000, lastBonus: 0 },
  orchard: { level: 0, maxLevel: 1000, lastBonus: 0 },
  veggiePatch: { level: 0, maxLevel: 1000, lastBonus: 0 },
  // Erweiterte Bereiche
  roseGarden: { level: 0, maxLevel: 1000, lastBonus: 0 },
  japaneseGarden: { level: 0, maxLevel: 1000, lastBonus: 0 },
  wildflowerMeadow: { level: 0, maxLevel: 1000, lastBonus: 0 },
  sculptureGarden: { level: 0, maxLevel: 1000, lastBonus: 0 },
  // Premium Bereiche
  enchantedGrove: { level: 0, maxLevel: 1000, lastBonus: 0 },
  crystalCave: { level: 0, maxLevel: 1000, lastBonus: 0 },
  moonlightGarden: { level: 0, maxLevel: 1000, lastBonus: 0 },
  dragonLair: { level: 0, maxLevel: 1000, lastBonus: 0 },
  // Klick-fokussierte Bereiche
  goldMine: { level: 0, maxLevel: 1000, lastBonus: 0 },
  treasureVault: { level: 0, maxLevel: 1000, lastBonus: 0 },
  alchemyLab: { level: 0, maxLevel: 1000, lastBonus: 0 },
  magicForge: { level: 0, maxLevel: 1000, lastBonus: 0 },
};

// Reparatur-Funktion für fehlende Gartenbereiche
const repairGardenAreas = (gardenAreas: any) => {
  const repairedAreas = { ...gardenAreas };
  let hasChanges = false;
  
  Object.keys(initialGardenAreas).forEach(areaKey => {
    if (!repairedAreas[areaKey]) {
      repairedAreas[areaKey] = initialGardenAreas[areaKey as keyof typeof initialGardenAreas];
      hasChanges = true;
    }
  });
  
  return { repairedAreas, hasChanges };
};

const getExperienceForLevel = (level: number) => {
  let totalXP = 0;
  
  for (let l = 1; l < level; l++) {
    // Kontinuierliches exponentielles Level-System ohne Neustart
    let baseXP: number;
    
    if (l >= 1000000) {
      // Ab Level 1.000.000: Ultra-Mega Progression mit exponentieller Steigerung
      baseXP = Math.floor(1000000000 * Math.pow(1.1, l - 1000000));
    } else if (l >= 100000) {
      // Ab Level 100.000: Mega Progression mit exponentieller Steigerung
      baseXP = Math.floor(100000000 * Math.pow(1.05, l - 100000));
    } else if (l >= 10000) {
      // Ab Level 10.000: Super Progression mit exponentieller Steigerung
      baseXP = Math.floor(10000000 * Math.pow(1.03, l - 10000));
    } else if (l >= 1000) {
      // Ab Level 1.000: Erweiterte Progression
      baseXP = Math.floor(1000000 * Math.pow(1.02, l - 1000));
    } else if (l >= 100) {
      // Ab Level 100: Kontinuierliche Progression ohne Sprung
      baseXP = Math.floor(100000 * Math.pow(1.015, l - 100));
    } else {
      // Level 1-99: Normale Progression mit exponentieller Steigerung
      baseXP = Math.floor(100 * Math.pow(1.1, l - 1));
    }
    totalXP += baseXP;
  }
  
  return totalXP;
};

// Begleiter-Generator-Funktionen
const generateDolfjinmann = (): Companion => {
  // Alle verfügbaren Fähigkeitstypen
  const allAbilityTypes = [
    'clickMultiplier', 'coinPerSecond', 'gemChance', 'xpBonus', 
    'gardenBonus', 'autoClickPower', 'upgradeDiscount', 'prestigeBonus',
    'criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'
  ];
  
  const abilities = [];
  const usedTypes = new Set();
  
  // Dolfjinmann hat genau 12 Fähigkeiten (alle verfügbaren Typen)
  for (let i = 0; i < 12; i++) {
    let abilityType;
    do {
      abilityType = allAbilityTypes[Math.floor(Math.random() * allAbilityTypes.length)];
    } while (usedTypes.has(abilityType));
    
    usedTypes.add(abilityType);
    
    // Mystische Werte (1000x stärker als normale, 20x stärker als göttliche)
    let value;
    let multiplier = 1000;
    
    switch (abilityType) {
      case 'clickMultiplier':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'coinPerSecond':
        value = Math.floor(2000 + Math.random() * 4000) * multiplier;
        break;
      case 'gemChance':
        value = Math.floor(200 + Math.random() * 300) * multiplier;
        break;
      case 'xpBonus':
        value = Math.floor(2000 + Math.random() * 3000) * multiplier;
        break;
      case 'gardenBonus':
        value = Math.floor(4000 + Math.random() * 5000) * multiplier;
        break;
      case 'autoClickPower':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'upgradeDiscount':
        value = Math.floor(400 + Math.random() * 500) * multiplier;
        break;
      case 'prestigeBonus':
        value = Math.floor(2000 + Math.random() * 3000) * multiplier;
        break;
      case 'criticalHit':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      case 'healing':
        value = Math.floor(10000 + Math.random() * 20000) * multiplier;
        break;
      case 'shield':
        value = Math.floor(10000 + Math.random() * 20000) * multiplier;
        break;
      case 'counterAttack':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      case 'lifeSteal':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      case 'armorPenetration':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      default:
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
    }
    
    const descriptions: Record<string, (v: number) => string> = {
      clickMultiplier: (v: number) => `+${v}% Klick-Multiplikator`,
      coinPerSecond: (v: number) => `+${v} Münzen pro Sekunde`,
      gemChance: (v: number) => `+${v}% Edelstein-Chance`,
      xpBonus: (v: number) => `+${v}% Erfahrungs-Bonus`,
      gardenBonus: (v: number) => `+${v}% Garten-Bonus`,
      autoClickPower: (v: number) => `+${v} Auto-Klick-Power`,
      upgradeDiscount: (v: number) => `${v}% Upgrade-Rabatt`,
      prestigeBonus: (v: number) => `+${v}% Prestige-Punkte`,
      criticalHit: (v: number) => `+${v}% Kritische Treffer`,
      healing: (v: number) => `+${v} Heilung`,
      shield: (v: number) => `+${v} Schild`,
      counterAttack: (v: number) => `+${v}% Gegenangriff`,
      lifeSteal: (v: number) => `+${v}% Lebensentzug`,
      armorPenetration: (v: number) => `+${v}% Rüstungsdurchdringung`
    };
    
    abilities.push({
      type: abilityType as any,
      value,
      description: descriptions[abilityType](value)
    });
  }
  
  return {
    id: `dolfjinmann_${Date.now()}_${Math.random()}`,
    name: `★★★ Dolfjinmann ★★★`,
    type: 'legendary' as const,
    icon: '🌟',
    description: `Ein mystischer legendärer Begleiter von unvorstellbarer Macht. Er besitzt alle verfügbaren Fähigkeiten in mystischer Stärke.`,
    abilities,
    discoveredAt: new Date().toISOString()
  };
};

const generateSteve = (level: number = 1): Companion => {
  // Alle verfügbaren Fähigkeitstypen
  const allAbilityTypes = [
    'clickMultiplier', 'coinPerSecond', 'gemChance', 'xpBonus', 
    'gardenBonus', 'autoClickPower', 'upgradeDiscount', 'prestigeBonus',
    'criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'
  ];
  
  const abilities = [];
  const usedTypes = new Set();
  
  // Steve hat genau 12 Fähigkeiten (alle verfügbaren Typen)
  for (let i = 0; i < 12; i++) {
    let abilityType;
    do {
      abilityType = allAbilityTypes[Math.floor(Math.random() * allAbilityTypes.length)];
    } while (usedTypes.has(abilityType));
    
    usedTypes.add(abilityType);
    
    let value;
    let multiplier;
    
    // Bestimme Multiplikator basierend auf Level
    if (level >= 1000) {
      // Steve der Allmächtige: Halb so stark wie Dolfjinmann (500x Multiplikator)
      multiplier = 500;
    } else {
      // Normaler Steve: Sehr schwach (0.1x Multiplikator)
      multiplier = 0.1;
    }
    
    switch (abilityType) {
      case 'clickMultiplier':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'coinPerSecond':
        value = Math.floor(2000 + Math.random() * 4000) * multiplier;
        break;
      case 'gemChance':
        value = Math.floor(200 + Math.random() * 300) * multiplier;
        break;
      case 'xpBonus':
        value = Math.floor(2000 + Math.random() * 3000) * multiplier;
        break;
      case 'gardenBonus':
        value = Math.floor(4000 + Math.random() * 5000) * multiplier;
        break;
      case 'autoClickPower':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'upgradeDiscount':
        value = Math.floor(400 + Math.random() * 500) * multiplier;
        break;
      case 'prestigeBonus':
        value = Math.floor(2000 + Math.random() * 3000) * multiplier;
        break;
      case 'criticalHit':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      case 'healing':
        value = Math.floor(10000 + Math.random() * 20000) * multiplier;
        break;
      case 'shield':
        value = Math.floor(10000 + Math.random() * 20000) * multiplier;
        break;
      case 'counterAttack':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      case 'lifeSteal':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      case 'armorPenetration':
        value = Math.floor(500 + Math.random() * 1000) * multiplier;
        break;
      default:
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
    }
    
    const descriptions: Record<string, (v: number) => string> = {
      clickMultiplier: (v: number) => `+${v}% Klick-Multiplikator`,
      coinPerSecond: (v: number) => `+${v} Münzen pro Sekunde`,
      gemChance: (v: number) => `+${v}% Edelstein-Chance`,
      xpBonus: (v: number) => `+${v}% Erfahrungs-Bonus`,
      gardenBonus: (v: number) => `+${v}% Garten-Bonus`,
      autoClickPower: (v: number) => `+${v} Auto-Klick-Power`,
      upgradeDiscount: (v: number) => `${v}% Upgrade-Rabatt`,
      prestigeBonus: (v: number) => `+${v}% Prestige-Punkte`,
      criticalHit: (v: number) => `+${v}% Kritische Treffer`,
      healing: (v: number) => `+${v} Heilung`,
      shield: (v: number) => `+${v} Schild`,
      counterAttack: (v: number) => `+${v}% Gegenangriff`,
      lifeSteal: (v: number) => `+${v}% Lebensentzug`,
      armorPenetration: (v: number) => `+${v}% Rüstungsdurchdringung`
    };
    
    abilities.push({
      type: abilityType as any,
      value,
      description: descriptions[abilityType](value)
    });
  }
  
  const isAllmighty = level >= 1000;
  
  return {
    id: `steve_${Date.now()}_${Math.random()}`,
    name: isAllmighty ? `★★★ Steve der Allmächtige ★★★` : `Steve`,
    type: 'legendary' as const,
    icon: isAllmighty ? '⚡' : '👤',
    description: isAllmighty 
      ? `Steve hat seine wahre Macht erreicht! Er ist jetzt halb so stark wie Dolfjinmann und besitzt alle verfügbaren Fähigkeiten in göttlicher Stärke.`
      : `Ein mysteriöser Begleiter namens Steve. Er scheint sehr schwach zu sein, aber wer weiß, was in ihm steckt...`,
    abilities,
    level,
    experience: 0,
    discoveredAt: new Date().toISOString()
  };
};

const companionNames = [
  // Natur-Namen
  'Luna', 'Felix', 'Sage', 'Ivy', 'Moss', 'Fern', 'Willow', 'Cedar', 'Rowan', 'Hazel',
  'Jasper', 'Ember', 'Storm', 'River', 'Sky', 'Dawn', 'Dusk', 'Star', 'Moon', 'Sun',
  'Petal', 'Thorn', 'Leaf', 'Root', 'Bloom', 'Sprout', 'Bud', 'Branch', 'Bark', 'Seed',
  'Crystal', 'Gem', 'Pearl', 'Ruby', 'Jade', 'Opal', 'Amber', 'Gold', 'Silver', 'Copper',
  'Phoenix', 'Dragon', 'Griffin', 'Unicorn', 'Pegasus', 'Sphinx', 'Chimera', 'Hydra', 'Kraken', 'Leviathan',
  
  // Magische Namen
  'Zephyr', 'Nova', 'Aurora', 'Vega', 'Orion', 'Atlas', 'Nyx', 'Helios', 'Selene', 'Eos',
  'Aether', 'Gaia', 'Chronos', 'Nemesis', 'Tyche', 'Iris', 'Echo', 'Narcissus', 'Daphne', 'Apollo',
  'Artemis', 'Hermes', 'Athena', 'Zeus', 'Hera', 'Poseidon', 'Hades', 'Demeter', 'Hestia', 'Ares',
  
  // Elementar-Namen
  'Blaze', 'Frost', 'Thunder', 'Aqua', 'Terra', 'Ventus', 'Ignis', 'Glacies', 'Fulmen', 'Mare',
  'Terra', 'Aer', 'Lux', 'Umbra', 'Tempus', 'Spiritus', 'Vita', 'Mors', 'Pax', 'Bellum',
  
  // Mystische Namen
  'Mystic', 'Enigma', 'Riddle', 'Puzzle', 'Cipher', 'Oracle', 'Prophet', 'Seer', 'Vision', 'Dream',
  'Shadow', 'Phantom', 'Specter', 'Wraith', 'Banshee', 'Poltergeist', 'Apparition', 'Ethereal', 'Astral', 'Cosmic',
  
  // Tier-Namen
  'Fang', 'Claw', 'Talon', 'Paw', 'Whisker', 'Tail', 'Wing', 'Beak', 'Fin', 'Scale',
  'Fur', 'Feather', 'Shell', 'Antler', 'Horn', 'Tusk', 'Trunk', 'Snout', 'Muzzle', 'Jaw',
  
  // Edelstein-Namen
  'Diamond', 'Sapphire', 'Emerald', 'Topaz', 'Garnet', 'Amethyst', 'Citrine', 'Onyx', 'Jasper', 'Agate',
  'Quartz', 'Obsidian', 'Malachite', 'Lapis', 'Turquoise', 'Coral', 'Pearl', 'Moonstone', 'Sunstone', 'Stardust',
  
  // Fantasy-Namen
  'Eldrin', 'Thorin', 'Gandalf', 'Merlin', 'Morgana', 'Nimue', 'Vivienne', 'Lancelot', 'Arthur', 'Guinevere',
  'Excalibur', 'Caliburn', 'Durandal', 'Gram', 'Tyrfing', 'Dainsleif', 'Hrunting', 'Nægling', 'Hrotti', 'Lævateinn',
  
  // Kosmische Namen
  'Nebula', 'Galaxy', 'Comet', 'Meteor', 'Asteroid', 'Planet', 'Satellite', 'Orbit', 'Gravity', 'Void',
  'Quantum', 'Neutron', 'Proton', 'Electron', 'Photon', 'Atom', 'Molecule', 'Element', 'Isotope', 'Fusion',
  
  // Alchemistische Namen
  'Elixir', 'Potion', 'Tincture', 'Essence', 'Extract', 'Concoction', 'Brew', 'Draught', 'Philter', 'Tonic',
  'Amalgam', 'Alloy', 'Compound', 'Mixture', 'Solution', 'Suspension', 'Emulsion', 'Colloid', 'Gel', 'Aerosol',
  
  // Runen-Namen
  'Algiz', 'Berkano', 'Dagaz', 'Ehwaz', 'Fehu', 'Gebo', 'Hagalaz', 'Ingwaz', 'Jera', 'Kaunan',
  'Laguz', 'Mannaz', 'Naudiz', 'Othala', 'Pertho', 'Raidho', 'Sowilo', 'Teiwaz', 'Uruz', 'Wunjo',
  
  // Mythologische Namen
  'Thor', 'Odin', 'Freya', 'Loki', 'Frigg', 'Baldr', 'Tyr', 'Heimdall', 'Vidar', 'Vali',
  'Sif', 'Idun', 'Nanna', 'Gefjon', 'Eir', 'Var', 'Vor', 'Syn', 'Hlin', 'Snotra',
  
  // Japanische Namen
  'Hikari', 'Yuki', 'Akira', 'Haru', 'Natsu', 'Aki', 'Fuyu', 'Kaze', 'Mizu', 'Hi',
  'Tsuki', 'Hoshi', 'Tsubasa', 'Kokoro', 'Yume', 'Ai', 'Yuuki', 'Sakura', 'Momiji', 'Ume',
  
  // Nordische Namen
  'Bjorn', 'Erik', 'Gunnar', 'Harald', 'Ivar', 'Jarl', 'Knut', 'Leif', 'Magnus', 'Olaf',
  'Ragnar', 'Sigurd', 'Thorsten', 'Ulf', 'Vidar', 'Wulf', 'Yngvar', 'Zigurd', 'Asbjorn', 'Brandr',
  
  // Keltische Namen
  'Aiden', 'Bran', 'Cian', 'Dara', 'Ewan', 'Finn', 'Gareth', 'Hugh', 'Ian', 'Keir',
  'Liam', 'Maeve', 'Niamh', 'Oisin', 'Padraig', 'Quinn', 'Rory', 'Sean', 'Tiernan', 'Uilleam',
  
  // Arabische Namen
  'Ahmad', 'Bilal', 'Dawud', 'Faris', 'Hassan', 'Ibrahim', 'Jamal', 'Khalil', 'Malik', 'Nasir',
  'Omar', 'Qasim', 'Rashid', 'Salim', 'Tariq', 'Umar', 'Wahid', 'Yusuf', 'Zaid', 'Amir',
  
  // Chinesische Namen
  'Bai', 'Chen', 'Dong', 'Feng', 'Gao', 'Hui', 'Jian', 'Kang', 'Li', 'Ming',
  'Ning', 'Peng', 'Qiang', 'Rong', 'Shan', 'Tao', 'Wei', 'Xiang', 'Yong', 'Zhen',
  
  // Indische Namen
  'Arjun', 'Bharat', 'Chandra', 'Devi', 'Esha', 'Firoz', 'Ganesh', 'Hari', 'Indra', 'Jaya',
  'Kali', 'Lakshmi', 'Maya', 'Narayan', 'Om', 'Priya', 'Rama', 'Sita', 'Tara', 'Uma',
  
  // Ägyptische Namen
  'Amun', 'Bastet', 'Horus', 'Isis', 'Khnum', 'Maat', 'Nephthys', 'Osiris', 'Ptah', 'Ra',
  'Sekhmet', 'Thoth', 'Wadjet', 'Anubis', 'Hathor', 'Khonsu', 'Neith', 'Sobek', 'Taweret', 'Wepwawet',
  
  // Griechische Namen
  'Achilles', 'Bellerophon', 'Cassandra', 'Dionysus', 'Electra', 'Fates', 'Gorgon', 'Hecate', 'Icarus', 'Jason',
  'Kronos', 'Leda', 'Medusa', 'Narcissus', 'Odysseus', 'Perseus', 'Rhea', 'Sisyphus', 'Theseus', 'Zeus',
  
  // Römische Namen
  'Augustus', 'Brutus', 'Caesar', 'Drusus', 'Fabius', 'Gaius', 'Hadrian', 'Julius', 'Lucius', 'Marcus',
  'Nero', 'Octavian', 'Pompey', 'Quintus', 'Romulus', 'Sextus', 'Tiberius', 'Titus', 'Valerius', 'Vespasian'
];

const companionIcons = [
  // Haustiere & Tiere
  '🐱', '🐶', '🦊', '🐺', '🐻', '🐨', '🐼', '🦁', '🐯', '🐸',
  '🦋', '🐝', '🐞', '🦜', '🦅', '🦉', '🐧', '🐙', '🐠', '🐟',
  '🐢', '🦎', '🐍', '🐊', '🦕', '🦖', '🐘', '🦛', '🦘', '🐨',
  '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🦆', '🦅',
  '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌',
  '🐞', '🐜', '🦗', '🕷️', '🕸️', '🦂', '🐢', '🐍', '🦎', '🦖',
  
  // Magische & Mystische
  '🌟', '⭐', '✨', '💫', '🔮', '💎', '🏆', '👑', '🎭', '🎨',
  '🧙‍♂️', '🧙‍♀️', '🧚‍♂️', '🧚‍♀️', '🧝‍♂️', '🧝‍♀️', '🦸‍♂️', '🦸‍♀️', '🧞‍♂️', '🧞‍♀️',
  '🧟‍♂️', '🧟‍♀️', '🧛‍♂️', '🧛‍♀️', '🧜‍♂️', '🧜‍♀️', '🧚‍♂️', '🧚‍♀️', '🧙‍♂️', '🧙‍♀️',
  '🔮', '⚗️', '🧪', '⚡', '🔥', '❄️', '💧', '🌪️', '🌊', '🌋',
  '🌈', '☀️', '🌙', '⭐', '🌟', '✨', '💫', '☄️', '🌠', '🌌',
  
  // Pflanzen & Natur
  '🌸', '🌺', '🌻', '🌼', '🌷', '🌹', '🥀', '🌱', '🌲', '🌳',
  '🌴', '🌵', '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🌺',
  '🌻', '🌼', '🌷', '🌹', '🥀', '🌱', '🌲', '🌳', '🌴', '🌵',
  '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🌱', '🌲', '🌳',
  
  // Edelsteine & Kristalle
  '💎', '🔮', '💍', '👑', '🏆', '🥇', '🥈', '🥉', '💎', '🔮',
  '💍', '👑', '🏆', '🥇', '🥈', '🥉', '💎', '🔮', '💍', '👑',
  '🏆', '🥇', '🥈', '🥉', '💎', '🔮', '💍', '👑', '🏆', '🥇',
  
  // Fantasy & Mythologie
  '🐉', '🦄', '🦅', '🦁', '🐯', '🐺', '🐻', '🐨', '🐼', '🦊',
  '🐱', '🐶', '🐸', '🐵', '🐔', '🐧', '🦆', '🦅', '🦉', '🦇',
  '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜',
  
  // Kosmische & Wissenschaft
  '🌌', '🌠', '☄️', '⭐', '🌟', '✨', '💫', '🌙', '☀️', '🌍',
  '🌎', '🌏', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒', '🌓', '🌔',
  '🌙', '☀️', '⭐', '🌟', '✨', '💫', '🌠', '☄️', '🌌', '🌍',
  
  // Alchemie & Magie
  '⚗️', '🧪', '🔮', '⚡', '🔥', '❄️', '💧', '🌪️', '🌊', '🌋',
  '🌈', '☀️', '🌙', '⭐', '🌟', '✨', '💫', '☄️', '🌠', '🌌',
  '⚗️', '🧪', '🔮', '⚡', '🔥', '❄️', '💧', '🌪️', '🌊', '🌋',
  
  // Runen & Symbole
  '⚔️', '🛡️', '🏹', '🗡️', '⚔️', '🛡️', '🏹', '🗡️', '⚔️', '🛡️',
  '🏹', '🗡️', '⚔️', '🛡️', '🏹', '🗡️', '⚔️', '🛡️', '🏹', '🗡️',
  '⚔️', '🛡️', '🏹', '🗡️', '⚔️', '🛡️', '🏹', '🗡️', '⚔️', '🛡️',
  
  // Elemente & Naturgewalten
  '🔥', '❄️', '💧', '🌪️', '🌊', '🌋', '⚡', '🌩️', '🌨️', '🌧️',
  '☀️', '🌙', '⭐', '🌟', '✨', '💫', '☄️', '🌠', '🌌', '🌍',
  '🔥', '❄️', '💧', '🌪️', '🌊', '🌋', '⚡', '🌩️', '🌨️', '🌧️',
  
  // Mystische Wesen
  '🧙‍♂️', '🧙‍♀️', '🧚‍♂️', '🧚‍♀️', '🧝‍♂️', '🧝‍♀️', '🦸‍♂️', '🦸‍♀️', '🧞‍♂️', '🧞‍♀️',
  '🧟‍♂️', '🧟‍♀️', '🧛‍♂️', '🧛‍♀️', '🧜‍♂️', '🧜‍♀️', '🧚‍♂️', '🧚‍♀️', '🧙‍♂️', '🧙‍♀️',
  '🧟‍♂️', '🧟‍♀️', '🧛‍♂️', '🧛‍♀️', '🧜‍♂️', '🧜‍♀️', '🧚‍♂️', '🧚‍♀️', '🧙‍♂️', '🧙‍♀️',
  
  // Technologie & Zukunft
  '🤖', '👾', '🎮', '🕹️', '💻', '🖥️', '📱', '📲', '⌚', '📟',
  '📠', '🖨️', '🖱️', '⌨️', '🖥️', '💻', '📱', '📲', '⌚', '📟',
  '📠', '🖨️', '🖱️', '⌨️', '🖥️', '💻', '📱', '📲', '⌚', '📟',
  
  // Musik & Kunst
  '🎵', '🎶', '🎼', '🎤', '🎧', '🎷', '🎸', '🎹', '🎺', '🎻',
  '🥁', '🪘', '🪕', '🪗', '🎤', '🎧', '🎷', '🎸', '🎹', '🎺',
  '🎻', '🥁', '🪘', '🪕', '🪗', '🎤', '🎧', '🎷', '🎸', '🎹',
  
  // Sport & Aktivität
  '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
  '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🥅', '⛳', '🪁',
  '🏹', '🎣', '🤿', '🥊', '🥋', '🎽', '🛹', '🛷️', '⛸️', '🥌',
  
  // Essen & Trinken
  '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈',
  '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🥑', '🥦', '🥬',
  '🥒', '🌶️', '🫑', '🌽', '🥕', '🫒', '🧄', '🧅', '🥔', '🍠',
  
  // Emotionen & Gesichter
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇',
  '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚',
  '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩',
  '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣',
  
  // Hände & Gesten
  '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞',
  '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍',
  '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝',
  '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶', '👂', '🦻', '👃',
  
  // Kleidung & Accessoires
  '👕', '👖', '👗', '👘', '👙', '👚', '👛', '👜', '👝', '🛍️',
  '🎒', '🩴', '👞', '👟', '🥾', '🥿', '👠', '👡', '🩰', '👢',
  '👑', '👒', '🎩', '🎓', '🧢', '⛑️', '🪖', '👕', '👖', '👗',
  
  // Werkzeuge & Objekte
  '🔨', '🪛', '🔧', '🔩', '⚙️', '🗜️', '⚖️', '🦯', '🔗', '⛓️',
  '🧰', '🧲', '⚗️', '🧪', '🧫', '🧬', '🔬', '🔭', '📡', '💉',
  '🩸', '💊', '🩹', '🩺', '🩻', '🚪', '🪟', '🪞', '🪟', '🛏️',
  
  // Transport & Fahrzeuge
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐',
  '🚚', '🚛', '🚜', '🛴', '🚲', '🛵', '🏍️', '🚨', '🚔', '🚍',
  '🚘', '🚖', '🚡', '🚠', '🚟', '🚃', '🚋', '🚞', '🚝', '🚄',
  
  // Gebäude & Orte
  '🏠', '🏡', '🏘️', '🏚️', '🏗️', '🏭', '🏢', '🏬', '🏣', '🏤',
  '🏥', '🏦', '🏨', '🏪', '🏫', '🏩', '💒', '⛪', '🕌', '🛕',
  '🕍', '⛩️', '🕋', '⛲', '⛺', '🌁', '🌃', '🏙️', '🌄', '🌅',
  
  // Zeit & Kalender
  '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙',
  '🕚', '🕛', '🕜', '🕝', '🕞', '🕟', '🕠', '🕡', '🕢', '🕣',
  '🕤', '🕥', '🕦', '🕧', '⌚', '⌛', '⏰', '⏱️', '⏲️', '🕰️',
  
  // Wetter & Klima
  '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '🌨️',
  '🌩️', '🌨️', '🌩️', '🌨️', '🌩️', '🌨️', '🌩️', '🌨️', '🌩️', '🌨️',
  '🌩️', '🌨️', '🌩️', '🌨️', '🌩️', '🌨️', '🌩️', '🌨️', '🌩️', '🌨️',
  
  // Zusätzliche mystische Symbole
  '☯️', '☮️', '✝️', '☪️', '✡️', '🔯', '🕎', '☸️', '☦️', '🛐',
  '⚛️', '🕉️', '✴️', '💟', '❣️', '💕', '💞', '💓', '💗', '💖',
  '💘', '💝', '💔', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤',
  '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘'
];

const abilityDescriptions = {
  clickMultiplier: (value: number) => `+${value}% Münzen pro Klick`,
  coinPerSecond: (value: number) => `+${value} Münzen pro Sekunde`,
  gemChance: (value: number) => `+${value}% Edelstein-Chance`,
  xpBonus: (value: number) => `+${value}% Erfahrung`,
  gardenBonus: (value: number) => `+${value}% Garten-Boni`,
  autoClickPower: (value: number) => `+${value} Auto-Klick-Power`,
  upgradeDiscount: (value: number) => `${value}% Upgrade-Rabatt`,
  prestigeBonus: (value: number) => `+${value}% Prestige-Punkte`,
  // Kampf-spezifische Fähigkeiten
  criticalHit: (value: number) => `${value}% Kritische Treffer-Chance`,
  healing: (value: number) => `${value} HP Heilung pro Runde`,
  shield: (value: number) => `${value} Schadenswiderstand`,
  counterAttack: (value: number) => `${value}% Gegenangriff-Chance`,
  lifeSteal: (value: number) => `${value}% Lebensentzug`,
  armorPenetration: (value: number) => `${value} Rüstungsdurchdringung`
};

const generateMysticDivineCompanion = (): Companion => {
  const mysticNames = [
    'Mystisch Göttlicher Kaiser',
    'Omnipotenter Mystischer Herrscher', 
    'Universeller Mystischer Meister',
    'Kosmischer Mystischer Titan',
    'Ewiger Mystischer Wächter',
    'Allmächtiger Mystischer Sage',
    'Transzendenter Mystischer Gott',
    'Unendlicher Mystischer Schöpfer'
  ];
  
  const mysticIcons = ['🔮', '🌌', '⚡', '🔱', '🌟', '💫', '✨', '🎭'];
  
  const name = mysticNames[Math.floor(Math.random() * mysticNames.length)];
  const icon = mysticIcons[Math.floor(Math.random() * mysticIcons.length)];

  // Mystisch göttliche Begleiter haben 8 Fähigkeiten (mehr als normale göttliche)
  const abilityCount = 8;
  
  const abilityTypes = [
    'clickMultiplier', 'coinPerSecond', 'gemChance', 'xpBonus', 
    'gardenBonus', 'autoClickPower', 'upgradeDiscount', 'prestigeBonus',
    'criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'
  ];

  const abilities = [];
  const usedTypes = new Set();

  for (let i = 0; i < abilityCount; i++) {
    let abilityType;
    do {
      abilityType = abilityTypes[Math.floor(Math.random() * abilityTypes.length)];
    } while (usedTypes.has(abilityType));

    usedTypes.add(abilityType);

    // Mystisch göttliche Werte sind 10x stärker als normale göttliche
    let value;
    let multiplier = 500; // 5000% stärker als normale Begleiter!

    switch (abilityType) {
      case 'clickMultiplier':
        value = Math.floor(500 + Math.random() * 1500) * multiplier;
        break;
      case 'coinPerSecond':
        value = Math.floor(1000 + Math.random() * 3000) * multiplier;
        break;
      case 'gemChance':
        value = Math.floor(100 + Math.random() * 200) * multiplier;
        break;
      case 'xpBonus':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'gardenBonus':
        value = Math.floor(2000 + Math.random() * 3000) * multiplier;
        break;
      case 'autoClickPower':
        value = Math.floor(500 + Math.random() * 1500) * multiplier;
        break;
      case 'upgradeDiscount':
        value = Math.floor(200 + Math.random() * 300) * multiplier;
        break;
      case 'prestigeBonus':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'criticalHit':
        value = Math.floor(50 + Math.random() * 100) * multiplier;
        break;
      case 'healing':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'shield':
        value = Math.floor(1000 + Math.random() * 2000) * multiplier;
        break;
      case 'counterAttack':
        value = Math.floor(50 + Math.random() * 100) * multiplier;
        break;
      case 'lifeSteal':
        value = Math.floor(50 + Math.random() * 100) * multiplier;
        break;
      case 'armorPenetration':
        value = Math.floor(50 + Math.random() * 100) * multiplier;
        break;
      default:
        value = Math.floor(1000 * multiplier);
    }

    const descriptions: Record<typeof abilityType, (v: number) => string> = {
      clickMultiplier: (v: number) => `+${v}% Münzen pro Klick`,
      coinPerSecond: (v: number) => `+${v} Münzen pro Sekunde`,
      gemChance: (v: number) => `+${v}% Edelstein-Chance`,
      xpBonus: (v: number) => `+${v}% Erfahrung`,
      gardenBonus: (v: number) => `+${v}% Garten-Boni`,
      autoClickPower: (v: number) => `+${v} Auto-Klick-Power`,
      upgradeDiscount: (v: number) => `${v}% Upgrade-Rabatt`,
      prestigeBonus: (v: number) => `+${v}% Prestige-Punkte`,
      criticalHit: (v: number) => `+${v}% Kritische Treffer`,
      healing: (v: number) => `+${v} Heilung`,
      shield: (v: number) => `+${v} Schild`,
      counterAttack: (v: number) => `+${v}% Gegenangriff`,
      lifeSteal: (v: number) => `+${v}% Lebensraub`,
      armorPenetration: (v: number) => `+${v}% Rüstungsdurchschlag`
    };

    abilities.push({
      type: abilityType as CompanionAbility['type'],
      value,
      description: descriptions[abilityType](value)
    });
  }

  return {
    id: `mystic-divine-${Date.now()}-${Math.random()}`,
    name: `★★★ ${name} ★★★`,
    type: 'legendary',
    icon,
    description: 'Ein mystisch göttlich legendärer Begleiter von unvorstellbarer Macht. Erschaffen durch die Fusion von 50 göttlich legendären Begleitern.',
    abilities,
    discoveredAt: new Date().toISOString()
  };
};

const generateUpgradedCompanion = (type: Companion['type'], isLegendaryUpgrade: boolean = false): Companion => {
  const data = {
    common: {
      names: ['Felix', 'Luna', 'Max', 'Bella', 'Charlie', 'Daisy', 'Rocky', 'Mia', 'Buddy', 'Lily'],
      icons: ['🐱', '🐶', '🐹', '🐰', '🐸', '🐤', '🐧', '🦔', '🐨', '🐼']
    },
    rare: {
      names: ['Kristall-Kätzchen', 'Silber-Wolf', 'Gold-Fuchs', 'Saphir-Eule', 'Rubin-Drache', 'Smaragd-Tiger', 'Perlen-Delfin', 'Opal-Schmetterling'],
      icons: ['💎', '🦊', '🦉', '🐅', '🐬', '🦋', '🦄', '🐉']
    },
    epic: {
      names: ['Mystischer Wächter', 'Arkaner Meister', 'Elementar-Geist', 'Himmlischer Bote', 'Magischer Beschützer', 'Uralter Weiser', 'Kosmischer Wanderer'],
      icons: ['🧙‍♂️', '🔮', '⚡', '🌟', '🔥', '❄️', '🌙', '☀️']
    },
    legendary: {
      names: isLegendaryUpgrade 
        ? ['Göttlicher Kaiser', 'Omnipotenter Herrscher', 'Universeller Meister', 'Kosmischer Titan', 'Ewiger Wächter', 'Allmächtiger Sage']
        : ['Drachen-Kaiser', 'Phoenix-Königin', 'Kristall-Meister', 'Stern-Wächter', 'Mondschein-Sage', 'Sonnen-Herrscher'],
      icons: isLegendaryUpgrade 
        ? ['👑', '🌌', '⚡', '🔱', '🌟', '💫']
        : ['🐉', '🔥', '⚡', '🌟', '💫', '🌙']
    }
  };

  const companionData = data[type];
  const name = companionData.names[Math.floor(Math.random() * companionData.names.length)];
  const icon = companionData.icons[Math.floor(Math.random() * companionData.icons.length)];

  // Anzahl der Fähigkeiten
  const abilityCount = {
    common: 1,
    rare: 2,
    epic: 3,
    legendary: isLegendaryUpgrade ? 6 : 4 // Göttliche haben 6 Fähigkeiten!
  }[type];

  const abilityTypes = [
    'clickMultiplier', 'coinPerSecond', 'gemChance', 'xpBonus', 
    'gardenBonus', 'autoClickPower', 'upgradeDiscount', 'prestigeBonus'
  ];

  const abilities = [];
  const usedTypes = new Set();

  for (let i = 0; i < abilityCount; i++) {
    let abilityType;
    do {
      abilityType = abilityTypes[Math.floor(Math.random() * abilityTypes.length)];
    } while (usedTypes.has(abilityType));

    usedTypes.add(abilityType);

    // Verstärkte Werte für Upgrades
    let value;
    let multiplier = {
      common: 1.5, // 50% stärker als normal
      rare: 2.5,   // 150% stärker
      epic: 4,     // 300% stärker
      legendary: isLegendaryUpgrade ? 50 : 6 // Normale: 500%, Göttliche: 5000% stärker!
    }[type];

    switch (abilityType) {
      case 'clickMultiplier':
        value = Math.floor((isLegendaryUpgrade ? 50 : 5) + Math.random() * (isLegendaryUpgrade ? 150 : 15)) * multiplier;
        break;
      case 'coinPerSecond':
        value = Math.floor((isLegendaryUpgrade ? 100 : 3) + Math.random() * (isLegendaryUpgrade ? 300 : 12)) * multiplier;
        break;
      case 'gemChance':
        value = Math.floor((isLegendaryUpgrade ? 10 : 1) + Math.random() * (isLegendaryUpgrade ? 20 : 4)) * multiplier;
        break;
      case 'xpBonus':
        value = Math.floor((isLegendaryUpgrade ? 100 : 10) + Math.random() * (isLegendaryUpgrade ? 200 : 20)) * multiplier;
        break;
      case 'gardenBonus':
        value = Math.floor((isLegendaryUpgrade ? 200 : 15) + Math.random() * (isLegendaryUpgrade ? 300 : 25)) * multiplier;
        break;
      case 'autoClickPower':
        value = Math.floor((isLegendaryUpgrade ? 50 : 2) + Math.random() * (isLegendaryUpgrade ? 150 : 8)) * multiplier;
        break;
      case 'upgradeDiscount':
        value = Math.floor((isLegendaryUpgrade ? 20 : 3) + Math.random() * (isLegendaryUpgrade ? 30 : 7)) * multiplier;
        break;
      case 'prestigeBonus':
        value = Math.floor((isLegendaryUpgrade ? 100 : 8) + Math.random() * (isLegendaryUpgrade ? 200 : 17)) * multiplier;
        break;
      default:
        value = Math.floor((isLegendaryUpgrade ? 100 : 10) * multiplier);
    }

    const descriptions: Record<typeof abilityType, (v: number) => string> = {
      clickMultiplier: (v: number) => `+${v}% Münzen pro Klick`,
      coinPerSecond: (v: number) => `+${v} Münzen pro Sekunde`,
      gemChance: (v: number) => `+${v}% Edelstein-Chance`,
      xpBonus: (v: number) => `+${v}% Erfahrung`,
      gardenBonus: (v: number) => `+${v}% Garten-Boni`,
      autoClickPower: (v: number) => `+${v} Auto-Klick-Power`,
      upgradeDiscount: (v: number) => `${v}% Upgrade-Rabatt`,
      prestigeBonus: (v: number) => `+${v}% Prestige-Punkte`
    };

    abilities.push({
      type: abilityType as any,
      value,
      description: descriptions[abilityType](value)
    });
  }

  const typeDescriptions = {
    common: 'Ein verbesserter Begleiter durch Fusion',
    rare: 'Ein seltener Begleiter mit erhöhter Kraft',
    epic: 'Ein epischer Begleiter mit mächtigen Fähigkeiten',
    legendary: isLegendaryUpgrade 
      ? 'Ein göttlicher Begleiter mit unvorstellbarer Macht!'
      : 'Ein legendärer Begleiter mit unglaublicher Kraft!'
  };

  return {
    id: `upgrade_${type}_${name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: isLegendaryUpgrade ? `★ ${name} ★` : name,
    type,
    icon,
    description: typeDescriptions[type],
    abilities,
    discoveredAt: new Date().toISOString()
  };
};

const generateRandomCompanion = (): Companion => {
  const name = companionNames[Math.floor(Math.random() * companionNames.length)];
  const icon = companionIcons[Math.floor(Math.random() * companionIcons.length)];
  
  // Bestimme Seltenheit (Common 40%, Rare 35%, Epic 20%, Legendary 5%)
  const rarityRoll = Math.random();
  let type: Companion['type'];
  let numAbilities: number;
  let powerMultiplier: number;
  
  if (rarityRoll < 0.05) {
    type = 'legendary';
    numAbilities = 3;
    powerMultiplier = 3;
  } else if (rarityRoll < 0.25) {
    type = 'epic';
    numAbilities = 2;
    powerMultiplier = 2;
  } else if (rarityRoll < 0.60) {
    type = 'rare';
    numAbilities = 2;
    powerMultiplier = 1.5;
  } else {
    type = 'common';
    numAbilities = 1;
    powerMultiplier = 1;
  }
  
  // Generiere Fähigkeiten
  const abilityTypes: CompanionAbility['type'][] = [
    'clickMultiplier', 'coinPerSecond', 'gemChance', 'xpBonus', 
    'gardenBonus', 'autoClickPower', 'upgradeDiscount', 'prestigeBonus'
  ];
  
  const selectedAbilities = [];
  const usedTypes = new Set();
  
  for (let i = 0; i < numAbilities; i++) {
    let abilityType;
    do {
      abilityType = abilityTypes[Math.floor(Math.random() * abilityTypes.length)];
    } while (usedTypes.has(abilityType));
    
    usedTypes.add(abilityType);
    
    // Bestimme Wert basierend auf Fähigkeitstyp und Seltenheit
    let baseValue;
    switch (abilityType) {
      case 'clickMultiplier':
        baseValue = 10 + Math.floor(Math.random() * 20); // 10-30%
        break;
      case 'coinPerSecond':
        baseValue = 5 + Math.floor(Math.random() * 15); // 5-20
        break;
      case 'gemChance':
        baseValue = 2 + Math.floor(Math.random() * 8); // 2-10%
        break;
      case 'xpBonus':
        baseValue = 15 + Math.floor(Math.random() * 25); // 15-40%
        break;
      case 'gardenBonus':
        baseValue = 20 + Math.floor(Math.random() * 30); // 20-50%
        break;
      case 'autoClickPower':
        baseValue = 3 + Math.floor(Math.random() * 12); // 3-15
        break;
      case 'upgradeDiscount':
        baseValue = 5 + Math.floor(Math.random() * 15); // 5-20%
        break;
      case 'prestigeBonus':
        baseValue = 10 + Math.floor(Math.random() * 20); // 10-30%
        break;
      default:
        baseValue = 10;
    }
    
    const finalValue = Math.floor(baseValue * powerMultiplier);
    
    selectedAbilities.push({
      type: abilityType,
      value: finalValue,
      description: abilityDescriptions[abilityType](finalValue)
    });
  }
  
  return {
    id: `${name}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    type,
    icon,
    description: `Ein ${type === 'legendary' ? 'legendärer' : type === 'epic' ? 'epischer' : type === 'rare' ? 'seltener' : 'gewöhnlicher'} Begleiter namens ${name}`,
    abilities: selectedAbilities,
    discoveredAt: new Date().toISOString()
  };
};

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // Initial State
      coins: 10, // Startet mit ein paar Münzen
      gems: 0,
      experience: 0,
      
      // Offline-Einkommen System
              lastSaveTime: Date.now(),
        offlineEndTime: Date.now(), // Endzeitpunkt für präzise Offline-Berechnung
        lastActivityTime: Date.now(), // Letzte Aktivität des Spielers
        offlineIncome: 0,
        offlineGems: 0,
        offlineCompanion: null,
        
      // Video-Werbung System
      videoRewards: {
        isActive: false,
        accumulatedTime: 0,
        lastVideoTime: null,
        videoCooldown: 5, // 5 Minuten Cooldown zwischen Videos
      },
      
      // Börse-System
      stockMarket: {
        portfolio: [],
        tradeHistory: [],
        stockPrices: {}, // Aktuelle Aktienpreise (persistent gespeichert)
        // Globales Aktienmarkt-System für alle Spieler
        globalMarket: {
          totalSharesOwned: {}, // Gesamte Anteile aller Spieler pro Aktie
          virtualPlayers: [],
          marketActivity: [],
        },
      },
      
      // Prestige System
      prestige: {
        level: 0,
        points: 0,
        totalPoints: 0,
        permanentBonuses: {
          clickMultiplier: 0,
          incomeMultiplier: 0,
          xpMultiplier: 0,
          startingCoins: 0,
          gemChance: 0,
          autoClickerEfficiency: 0,
          upgradeDiscount: 0,
          gardenEfficiency: 0,
        },
        lastPrestigeDate: '',
        totalPrestiges: 0,
        megaPrestige: {
          level: 0,
          totalPointsSpent: 0,
          globalMultiplier: 1,
          lastMegaPrestigeDate: '',
        },
      },
      
      clickPower: 1,
      autoClickPower: 0,
      autoClickInterval: 1000,
      playerLevel: 1,
      
      premiumAutoClicker: {
        isActive: false,
        costPerSecond: 1000, // 1000 Diamanten pro Sekunde
        lastTickTime: Date.now(),
        clicksPerSecond: 1, // Standard: 1 Klick pro Sekunde
      },
      
      companions: {
        discovered: [],
        active: [], // Array für bis zu 3 aktive Begleiter
        pendingCards: null,
        freeDrawLastUsed: null,
        freeDrawAvailable: true,
        freePremiumSpinLastUsed: null,
        freePremiumSpinAvailable: true,
            freePremiumSpinActive: false,
    training: [],
    research: {
      // Forschungs-Projekte
      projects: {
        // === TIER 1 PROJEKTE ===
        'attack_boost_1': {
          id: 'attack_boost_1',
          name: 'Grundangriff',
          description: 'Erhöht den Angriff aller Begleiter',
          cost: 1000,
          duration: 5,
          isActive: false,
          bonusType: 'attack',
          bonusValue: 5,
          completed: false,
        },
        'defense_boost_1': {
          id: 'defense_boost_1',
          name: 'Grundverteidigung',
          description: 'Erhöht die Verteidigung aller Begleiter',
          cost: 1000,
          duration: 5,
          isActive: false,
          bonusType: 'defense',
          bonusValue: 5,
          completed: false,
        },
        'hp_boost_1': {
          id: 'hp_boost_1',
          name: 'Vitalität',
          description: 'Erhöht die Gesundheit aller Begleiter',
          cost: 1000,
          duration: 5,
          isActive: false,
          bonusType: 'hp',
          bonusValue: 10,
          completed: false,
        },
        // === TIER 2 PROJEKTE ===
        'attack_boost_2': {
          id: 'attack_boost_2',
          name: 'Verstärkter Angriff',
          description: 'Weiterer Angriffsbonus',
          cost: 2500,
          duration: 10,
          isActive: false,
          bonusType: 'attack',
          bonusValue: 10,
          completed: false,
        },
        'defense_boost_2': {
          id: 'defense_boost_2',
          name: 'Verstärkte Verteidigung',
          description: 'Weiterer Verteidigungsbonus',
          cost: 2500,
          duration: 10,
          isActive: false,
          bonusType: 'defense',
          bonusValue: 10,
          completed: false,
        },
        'hp_boost_2': {
          id: 'hp_boost_2',
          name: 'Erweiterte Vitalität',
          description: 'Weiterer Gesundheitsbonus',
          cost: 2500,
          duration: 10,
          isActive: false,
          bonusType: 'hp',
          bonusValue: 20,
          completed: false,
        },
        // === TIER 3 PROJEKTE ===
        'experience_boost_1': {
          id: 'experience_boost_1',
          name: 'Lerntempo',
          description: 'Begleiter erhalten mehr Erfahrung',
          cost: 5000,
          duration: 15,
          isActive: false,
          bonusType: 'experience',
          bonusValue: 25,
          completed: false,
        },
        'ability_chance_1': {
          id: 'ability_chance_1',
          name: 'Fähigkeitsmeisterung',
          description: 'Erhöht die Chance auf Spezialfähigkeiten',
          cost: 5000,
          duration: 15,
          isActive: false,
          bonusType: 'abilityChance',
          bonusValue: 15,
          completed: false,
        },
        // === TIER 4 PROJEKTE ===
        'combat_mastery': {
          id: 'combat_mastery',
          name: 'Kampfmeisterschaft',
          description: 'Ultimative Kampffähigkeiten',
          cost: 10000,
          duration: 20,
          isActive: false,
          bonusType: 'attack',
          bonusValue: 25,
          completed: false,
        },
        'meditation_mastery': {
          id: 'meditation_mastery',
          name: 'Meditations-Meisterschaft',
          description: 'Spirituelle Erleuchtung',
          cost: 8000,
          duration: 18,
          isActive: false,
          bonusType: 'experience',
          bonusValue: 50,
          completed: false,
        },
        'arena_mastery': {
          id: 'arena_mastery',
          name: 'Arena-Meisterschaft',
          description: 'Perfekte Kampftechniken',
          cost: 12000,
          duration: 25,
          isActive: false,
          bonusType: 'defense',
          bonusValue: 30,
          completed: false,
        },
        // === TIER 5 PROJEKTE ===
        'divine_bond': {
          id: 'divine_bond',
          name: 'Göttliche Verbindung',
          description: 'Transzendentale Macht',
          cost: 20000,
          duration: 30,
          isActive: false,
          bonusType: 'abilityChance',
          bonusValue: 35,
          completed: false,
        },
        'ancient_wisdom': {
          id: 'ancient_wisdom',
          name: 'Uralte Weisheit',
          description: 'Wissen der Ahnen',
          cost: 25000,
          duration: 35,
          isActive: false,
          bonusType: 'experience',
          bonusValue: 100,
          completed: false,
        },
        // === TIER 6 PROJEKTE ===
        'transcendence': {
          id: 'transcendence',
          name: 'Transzendenz',
          description: 'Überwindung aller Grenzen',
          cost: 50000,
          duration: 60,
          isActive: false,
          bonusType: 'attack',
          bonusValue: 100,
          completed: false,
        },
        // === TIER 7 PROJEKTE ===
        'cosmic_awakening': {
          id: 'cosmic_awakening',
          name: 'Kosmische Erweckung',
          description: 'Erwachen kosmischer Kräfte',
          cost: 75000,
          duration: 90,
          isActive: false,
          bonusType: 'defense',
          bonusValue: 150,
          completed: false,
        },
        'eternal_wisdom': {
          id: 'eternal_wisdom',
          name: 'Ewige Weisheit',
          description: 'Wissen der Unsterblichen',
          cost: 80000,
          duration: 100,
          isActive: false,
          bonusType: 'experience',
          bonusValue: 200,
          completed: false,
        },
        'divine_perfection': {
          id: 'divine_perfection',
          name: 'Göttliche Vollkommenheit',
          description: 'Perfektion der Götter',
          cost: 100000,
          duration: 120,
          isActive: false,
          bonusType: 'abilityChance',
          bonusValue: 50,
          completed: false,
        },
        // === TIER 8 PROJEKTE ===
        'reality_bending': {
          id: 'reality_bending',
          name: 'Realitätsverzerrung',
          description: 'Verzerrung der Realität selbst',
          cost: 150000,
          duration: 150,
          isActive: false,
          bonusType: 'attack',
          bonusValue: 250,
          completed: false,
        },
        'time_mastery': {
          id: 'time_mastery',
          name: 'Zeit-Meisterschaft',
          description: 'Kontrolle über die Zeit',
          cost: 180000,
          duration: 180,
          isActive: false,
          bonusType: 'hp',
          bonusValue: 500,
          completed: false,
        },
        'space_manipulation': {
          id: 'space_manipulation',
          name: 'Raum-Manipulation',
          description: 'Kontrolle über den Raum',
          cost: 200000,
          duration: 200,
          isActive: false,
          bonusType: 'defense',
          bonusValue: 300,
          completed: false,
        },
        // === TIER 9 PROJEKTE ===
        'creation_magic': {
          id: 'creation_magic',
          name: 'Schöpfungsmagie',
          description: 'Magie der Schöpfung',
          cost: 300000,
          duration: 250,
          isActive: false,
          bonusType: 'attack',
          bonusValue: 500,
          completed: false,
        },
        'destruction_magic': {
          id: 'destruction_magic',
          name: 'Zerstörungsmagie',
          description: 'Magie der Zerstörung',
          cost: 350000,
          duration: 300,
          isActive: false,
          bonusType: 'defense',
          bonusValue: 600,
          completed: false,
        },
        'life_magic': {
          id: 'life_magic',
          name: 'Lebensmagie',
          description: 'Magie des Lebens',
          cost: 400000,
          duration: 350,
          isActive: false,
          bonusType: 'hp',
          bonusValue: 1000,
          completed: false,
        },
        // === TIER 10 PROJEKTE ===
        'omnipotence': {
          id: 'omnipotence',
          name: 'Allmacht',
          description: 'Absolute Macht über alles',
          cost: 500000,
          duration: 400,
          isActive: false,
          bonusType: 'attack',
          bonusValue: 1000,
          completed: false,
        },
        'omniscience': {
          id: 'omniscience',
          name: 'Allwissenheit',
          description: 'Wissen über alles',
          cost: 600000,
          duration: 450,
          isActive: false,
          bonusType: 'experience',
          bonusValue: 500,
          completed: false,
        },
        'omnipresence': {
          id: 'omnipresence',
          name: 'Allgegenwart',
          description: 'Gegenwart überall',
          cost: 700000,
          duration: 500,
          isActive: false,
          bonusType: 'abilityChance',
          bonusValue: 100,
          completed: false,
        },

      },
      // Globale Boni (additiv zu allen Begleitern)
      globalBonuses: {
        attack: 0,
        defense: 0,
        hp: 0,
        experience: 0,
        abilityChance: 0,
      },
      // Skill-Tree-Reset-System
      skillTreeReset: {
        resetCount: 0,
        permanentBonuses: {
          attack: 0,
          defense: 0,
          hp: 0,
          experience: 0,
          abilityChance: 0,
        },
        lastResetDate: null,
      },
    },
    forge: {
      // Erz-Ressourcen
      ore: {
        iron: 0,
        steel: 0,
        mithril: 0,
        adamantium: 0,
      },
      // Rüstungen
      armor: {},
      // Waffen
      weapons: {},
      // Erz-Abbau
      mining: {
        activeMiners: {},
      },
    },
    arena: {
      level: 1,
      difficultyMultiplier: 1.0,
      resetCount: 0,
    },
    conquest: {
      activeConquests: {},
      territoryConquestCounts: {},
    },
  },
      
      powerUps: {
        goldRush: { isActive: false, timeLeft: 0, endTime: undefined },
        speedBoost: { isActive: false, timeLeft: 0, endTime: undefined },
        luckyCharm: { isActive: false, timeLeft: 0, endTime: undefined },
        // Neue Power-Ups
        megaMultiplier: { isActive: false, timeLeft: 0, endTime: undefined },
        gemStorm: { isActive: false, timeLeft: 0, endTime: undefined },
        timeWarp: { isActive: false, timeLeft: 0, endTime: undefined },
        gardenBlessing: { isActive: false, timeLeft: 0, endTime: undefined },
        clickFrenzy: { isActive: false, timeLeft: 0, endTime: undefined },
        diamondMidas: { isActive: false, timeLeft: 0, endTime: undefined },
        infiniteEnergy: { isActive: false, timeLeft: 0, endTime: undefined },
        cosmicBoost: { isActive: false, timeLeft: 0, endTime: undefined },
        dragonPower: { isActive: false, timeLeft: 0, endTime: undefined },
        experienceRush: { isActive: false, timeLeft: 0, endTime: undefined },
      },
      
      upgrades: {
        shovelLevel: 1,
        autoDiggerLevel: 0,
        fertilizer: 0,
        wateringCan: 0,
        magicSeeds: 0,
        gardenGnome: 0,
        rainMaker: 0,
        composter: 0,
        goldDetector: 0,
        timeAccelerator: 0,
        luckCharm: 0,
        autoHarvester: 0,
        megaMultiplier: 0,
                                crystalGenerator: 0,
            quantumShovel: 0,
            gardenBot: 0,
            plantWhisperer: 0,
            seedVault: 0,
            soilAnalyzer: 0,
            weatherStation: 0,
            beeHive: 0,
            treeFarm: 0,
            flowerPower: 0,
            herbGarden: 0,
            butterflyGarden: 0,
            rockGarden: 0,
            waterFeature: 0,
            gardenLighting: 0,
      },
      
      proceduralTools: {},
      proceduralClickTools: {},
      
      gardenAreas: initialGardenAreas,
      
      matchThreeGame: {
        isUnlocked: true, // Für Tests sofort freigeschaltet
        currentLevel: 1,
        completedLevels: [],
        highScore: 0,
        consecutiveWins: 0,
        powerUpsUnlocked: [],
      },
      

      
      achievements: {
        totalClicks: 0,
        totalCoinsEarned: 0,
        areasRenovated: 0,
        matchThreeWins: 0,
        upgradesPurchased: 0,
        powerUpsUsed: 0,
        perfectMatchThreeGames: 0,
        dailyStreakRecord: 0,
        totalExperienceGained: 0,
      },
      
          settings: {
      musicEnabled: false, // Standardmäßig aus
      soundEnabled: true,
      autoSaveEnabled: true,
      notifications: true,
      performanceMonitorEnabled: false, // Performance-Monitor standardmäßig deaktiviert
    },

      globalMultipliers: {
      totalMultiplier: 1,
      sacrificeCount: 0,
    },
      
      // Actions
      addCoins: (amount) => {
        const finalAmount = Math.floor(amount);
        
        set((prevState) => ({
          coins: prevState.coins + finalAmount,
          achievements: {
            ...prevState.achievements,
            totalCoinsEarned: prevState.achievements.totalCoinsEarned + finalAmount,
          },
        }));
      },
      
      // Sicherheitsfunktion für Level-Kompatibilität nach Updates
      ensureLevelCompatibility: () => {
        const state = get();
        let correctLevel = 1;
        
        // Berechne das korrekte Level basierend auf der aktuellen Erfahrung
        while (true) {
          const xpForNextLevel = getExperienceForLevel(correctLevel + 1);
          if (state.experience < xpForNextLevel) {
            break;
          }
          correctLevel++;
        }
        
        // Wenn das berechnete Level vom gespeicherten Level abweicht, aktualisiere es
        if (correctLevel !== state.playerLevel) {
          console.log(`🔄 Level-Kompatibilität: Level ${state.playerLevel} → ${correctLevel} (basierend auf ${state.experience} EP)`);
          set({ playerLevel: correctLevel });
        }
      },
      
      addGems: (amount) => {
        set((state) => ({ gems: state.gems + amount }));
      },
      
      addExperience: (amount) => {
        const state = get();
        
        // Begleiter XP-Bonus
        let xpMultiplier = 1;
        const companionXpBonus = state.getCompanionBonus('xpBonus');
        if (companionXpBonus > 0) {
          xpMultiplier += companionXpBonus / 100;
        }
        
        // Experience Rush Power-Up Bonus (100x EP-Bonus)
        if (state.powerUps.experienceRush.isActive) {
          xpMultiplier *= 100;
        }
        
        const finalAmount = Math.floor(amount * xpMultiplier);
        
        set((state) => ({
          experience: state.experience + finalAmount,
          achievements: {
            ...state.achievements,
            totalExperienceGained: state.achievements.totalExperienceGained + finalAmount,
          },
        }));
        get().calculatePlayerLevel();
      },
      
      spendCoins: (amount) => {
        const state = get();
        if (state.coins >= amount) {
          set({ coins: state.coins - amount });
          return true;
        }
        return false;
      },
      
      spendGems: (amount) => {
        const state = get();
        if (state.gems >= amount) {
          set({ gems: state.gems - amount });
          return true;
        }
        return false;
      },
      
      upgradeClickPower: () => {
        const state = get();
        const cost = state.getUpgradeCost('shovel', state.upgrades.shovelLevel);
        if (state.spendCoins(cost)) {
          set((prevState) => ({
            clickPower: prevState.clickPower + prevState.playerLevel, // Level beeinflusst Stärke
            upgrades: {
              ...prevState.upgrades,
              shovelLevel: prevState.upgrades.shovelLevel + 1,
            },
            achievements: {
              ...prevState.achievements,
              upgradesPurchased: prevState.achievements.upgradesPurchased + 1,
            },
          }));
          state.addExperience(10);
        }
      },
      
      upgradeAutoClicker: () => {
        const state = get();
        const cost = state.getUpgradeCost('autoDigger', state.upgrades.autoDiggerLevel);
        if (state.spendCoins(cost)) {
          set((prevState) => ({
            autoClickPower: prevState.autoClickPower + 1,
            upgrades: {
              ...prevState.upgrades,
              autoDiggerLevel: prevState.upgrades.autoDiggerLevel + 1,
            },
            achievements: {
              ...prevState.achievements,
              upgradesPurchased: prevState.achievements.upgradesPurchased + 1,
            },
          }));
          state.addExperience(15);
        }
      },
      
      purchaseUpgrade: (upgradeType) => {
        const state = get();
        const currentLevel = state.upgrades[upgradeType];
        const cost = state.getUpgradeCost(String(upgradeType), currentLevel);
        
        if (state.spendCoins(cost)) {
          set((prevState) => ({
            upgrades: {
              ...prevState.upgrades,
              [upgradeType]: prevState.upgrades[upgradeType] + 1,
            },
            achievements: {
              ...prevState.achievements,
              upgradesPurchased: prevState.achievements.upgradesPurchased + 1,
            },
          }));
          state.addExperience(20);
          return true;
        }
        return false;
      },
      
      purchaseUpgradeBulk: (upgradeType, quantity) => {
        const state = get();
        const currentLevel = state.upgrades[upgradeType];
        
        if (quantity <= 0) {
          return { success: false, actualPurchases: 0, totalCost: 0 };
        }
        
        // Berechne Gesamtkosten für alle Level
        let totalCost = 0;
        let affordableQuantity = 0;
        
        for (let i = 0; i < quantity; i++) {
          const levelCost = state.getUpgradeCost(String(upgradeType), currentLevel + i);
          if (totalCost + levelCost <= state.coins) {
            totalCost += levelCost;
            affordableQuantity++;
          } else {
            break;
          }
        }
        
        if (affordableQuantity === 0) {
          return { success: false, actualPurchases: 0, totalCost: 0 };
        }
        
        // Führe den Bulk-Kauf durch
        if (state.spendCoins(totalCost)) {
          // Berechne spezifische Boni für Schaufel und Auto-Graber
          let clickPowerIncrease = 0;
          let autoClickPowerIncrease = 0;
          
          if (upgradeType === 'shovelLevel') {
            clickPowerIncrease = affordableQuantity * state.playerLevel;
          } else if (upgradeType === 'autoDiggerLevel') {
            autoClickPowerIncrease = affordableQuantity;
          }
          
          set((prevState) => ({
            clickPower: prevState.clickPower + clickPowerIncrease,
            autoClickPower: prevState.autoClickPower + autoClickPowerIncrease,
            upgrades: {
              ...prevState.upgrades,
              [upgradeType]: prevState.upgrades[upgradeType] + affordableQuantity,
            },
            achievements: {
              ...prevState.achievements,
              upgradesPurchased: prevState.achievements.upgradesPurchased + affordableQuantity,
            },
          }));
          
          // XP Bonus für alle gekauften Level
          const xpBonus = 20 * affordableQuantity;
          state.addExperience(xpBonus);
          
          return { success: true, actualPurchases: affordableQuantity, totalCost };
        }
        
        return { success: false, actualPurchases: 0, totalCost: 0 };
      },
      
      renovateArea: (area) => {
        const state = get();
        const areaData = state.gardenAreas[area];
        const cost = state.getUpgradeCost('renovation', areaData.level);
        
        if (areaData.level < areaData.maxLevel && state.spendCoins(cost)) {
          set((prevState) => ({
            gardenAreas: {
              ...prevState.gardenAreas,
              [area]: {
                ...prevState.gardenAreas[area],
                level: prevState.gardenAreas[area].level + 1,
                lastBonus: Date.now(),
              },
            },
            achievements: {
              ...prevState.achievements,
              areasRenovated: prevState.achievements.areasRenovated + 1,
            },
          }));
          
          // XP Bonus mit Magic Seeds
          const xpBonus = 25 + (state.upgrades.magicSeeds * 5);
          state.addExperience(xpBonus);
          
          // Schalte Match-3 und neue Bereiche frei
          if (state.achievements.areasRenovated === 1) {
            get().unlockMatchThree();
          }
          
          // Zufälliger Bonus durch Garden Gnome
          if (state.upgrades.gardenGnome > 0 && Math.random() < 0.3) {
            const bonus = Math.floor(cost * 0.2);
            state.addCoins(bonus);
          }
          
          return true;
        }
        return false;
      },
      
      renovateAreaBulk: (area, quantity) => {
        const state = get();
        const areaData = state.gardenAreas[area];
        
        // Berechne maximale mögliche Käufe
        const maxPossibleLevels = areaData.maxLevel - areaData.level;
        const actualQuantity = Math.min(quantity, maxPossibleLevels);
        
        if (actualQuantity <= 0) {
          return { success: false, actualPurchases: 0, totalCost: 0 };
        }
        
        // Berechne Gesamtkosten für alle Level
        let totalCost = 0;
        let affordableQuantity = 0;
        
        for (let i = 0; i < actualQuantity; i++) {
          const levelCost = state.getUpgradeCost('renovation', areaData.level + i);
          if (totalCost + levelCost <= state.coins) {
            totalCost += levelCost;
            affordableQuantity++;
          } else {
            break;
          }
        }
        
        if (affordableQuantity === 0) {
          return { success: false, actualPurchases: 0, totalCost: 0 };
        }
        
        // Führe den Bulk-Kauf durch
        if (state.spendCoins(totalCost)) {
          const oldAreasRenovated = state.achievements.areasRenovated;
          
          set((prevState) => ({
            gardenAreas: {
              ...prevState.gardenAreas,
              [area]: {
                ...prevState.gardenAreas[area],
                level: prevState.gardenAreas[area].level + affordableQuantity,
                lastBonus: Date.now(),
              },
            },
            achievements: {
              ...prevState.achievements,
              areasRenovated: prevState.achievements.areasRenovated + affordableQuantity,
            },
          }));
          
          // XP Bonus für alle gekauften Level
          const xpBonus = (25 + (state.upgrades.magicSeeds * 5)) * affordableQuantity;
          state.addExperience(xpBonus);
          
          // Schalte Match-3 frei wenn erste Renovierung
          if (oldAreasRenovated === 0 && affordableQuantity > 0) {
            get().unlockMatchThree();
          }
          
          // Garden Gnome Bonus für jeden Kauf
          if (state.upgrades.gardenGnome > 0) {
            for (let i = 0; i < affordableQuantity; i++) {
              if (Math.random() < 0.3) {
                const levelCost = state.getUpgradeCost('renovation', areaData.level + i);
                const bonus = Math.floor(levelCost * 0.2);
                state.addCoins(bonus);
              }
            }
          }
          
          return { success: true, actualPurchases: affordableQuantity, totalCost };
        }
        
        return { success: false, actualPurchases: 0, totalCost: 0 };
      },
      
      unlockMatchThree: () => {
        set((state) => ({
          matchThreeGame: {
            ...state.matchThreeGame,
            isUnlocked: true,
          },
        }));
      },
      
      completeMatchThreeLevel: (level, score, isPerfect = false) => {
        set((state) => ({
          matchThreeGame: {
            ...state.matchThreeGame,
            completedLevels: [...state.matchThreeGame.completedLevels, level],
            highScore: Math.max(state.matchThreeGame.highScore, score),
            currentLevel: Math.max(state.matchThreeGame.currentLevel, level + 1),
            consecutiveWins: state.matchThreeGame.consecutiveWins + 1,
          },
          achievements: {
            ...state.achievements,
            matchThreeWins: state.achievements.matchThreeWins + 1,
            perfectMatchThreeGames: isPerfect ? state.achievements.perfectMatchThreeGames + 1 : state.achievements.perfectMatchThreeGames,
          },
        }));
        
        const multiplier = 1 + (get().matchThreeGame.consecutiveWins * 0.1);
        get().addGems(Math.floor(level * 2 * multiplier));
        get().addCoins(Math.floor(score * multiplier));
        get().addExperience(50 + level * 10);
      },
      
      incrementClick: () => {
        const state = get();
        set((prevState) => ({
          achievements: {
            ...prevState.achievements,
            totalClicks: prevState.achievements.totalClicks + 1,
          },
        }));
        
        // Berechne den Grundbetrag mit allen Multipliers
        const baseAmount = state.clickPower * state.getClickMultiplier();
        const goldRushMultiplier = state.powerUps.goldRush.isActive ? 2 : 1;
        let finalAmount = Math.floor(baseAmount * goldRushMultiplier);
        
        // Quantum Shovel - Mehrfachklicks
        const quantumMultiplier = state.upgrades.quantumShovel > 0 ? (1 + state.upgrades.quantumShovel) : 1;
        finalAmount *= quantumMultiplier;
        
        // Prozedurale Klick-Werkzeuge Boni
        let clickBonus = 0;
        let xpBonus = 0;
        let gemChance = 0;
        
        if (state.proceduralClickTools) {
          Object.entries(state.proceduralClickTools).forEach(([key, tool]) => {
            const level = state.upgrades[key] || 0;
            if (level > 0) {
              switch (tool.effectType) {
                case 'clickBonus':
                  clickBonus += tool.baseEffect * level;
                  break;
                case 'xpBonus':
                  xpBonus += tool.baseEffect * level;
                  break;
                case 'gemChance':
                  gemChance += tool.baseEffect * level;
                  break;
                case 'doubleClickChance':
                  if (Math.random() < tool.baseEffect * level) {
                    finalAmount *= 2; // Doppelte Klicks
                  }
                  break;
              }
            }
          });
        }
        
        finalAmount += clickBonus;
        
        // Münzen hinzufügen
        set((prevState) => ({
          coins: prevState.coins + finalAmount,
          achievements: {
            ...prevState.achievements,
            totalCoinsEarned: prevState.achievements.totalCoinsEarned + finalAmount,
          },
        }));
        
        // Erfahrung hinzufügen (mehr mit Mega Multiplier und prozeduralen Werkzeugen)
        const xpGain = 1 + (state.upgrades.megaMultiplier * 0.5) + xpBonus;
        state.addExperience(Math.floor(xpGain));
        
        // Chance auf Edelsteine mit Lucky Charm oder Luck Charm Upgrade
        if (state.powerUps.luckyCharm.isActive) gemChance += 0.1;
        if (state.upgrades.luckCharm > 0) gemChance += state.upgrades.luckCharm * 0.02;
        
        // Garten-Werkzeuge erhöhen Edelstein-Chance
        if (state.upgrades.flowerPower > 0) gemChance += state.upgrades.flowerPower * 0.01;
        if (state.upgrades.butterflyGarden > 0) gemChance += state.upgrades.butterflyGarden * 0.015;
        
        // Begleiter Edelstein-Bonus
        const companionGemBonus = state.getCompanionBonus('gemChance');
        if (companionGemBonus > 0) gemChance += companionGemBonus / 100;
        
        // Neue Power-Up-Effekte für Edelsteine
        if (state.powerUps.gemStorm.isActive) {
          // Garantierte Edelsteine bei Gem Storm
          state.addGems(Math.max(1, Math.floor(finalAmount / 100)));
        } else if (gemChance > 0 && Math.random() < gemChance) {
          state.addGems(1);
        }
        
        // Diamond Midas - 0.1% der Münzen werden zu Edelsteinen
        if (state.powerUps.diamondMidas.isActive) {
          const gemsFromCoins = Math.floor(finalAmount * 0.001);
          if (gemsFromCoins > 0) {
            state.addGems(Math.max(1, Math.floor(gemsFromCoins / 10))); // 10 Münzen = 1 Edelstein
          }
        }
        
        // Gold Detector - Chance auf Bonus-Münzen
        if (state.upgrades.goldDetector > 0 && Math.random() < state.upgrades.goldDetector * 0.05) {
          const bonusCoins = Math.floor(finalAmount * 0.5);
          state.addCoins(bonusCoins);
        }
        
        // Composter - Chance Edelsteine zu generieren (basierend auf Münzen)
        if (state.upgrades.composter > 0 && finalAmount >= 10) {
          const composterChance = state.upgrades.composter * 0.01; // 1% pro Level wenn >= 10 Münzen
          if (Math.random() < composterChance) {
            state.addGems(1);
          }
        }
      },
      
      activatePowerUp: (powerUpType, duration) => {
        const endTime = Date.now() + (duration * 1000); // Endzeit in Millisekunden
        set((state) => ({
          powerUps: {
            ...state.powerUps,
            [powerUpType]: {
              isActive: true,
              timeLeft: duration,
              endTime: endTime,
            },
          },
          achievements: {
            ...state.achievements,
            powerUpsUsed: state.achievements.powerUpsUsed + 1,
          },
        }));
      },
      
      updatePowerUps: () => {
        const now = Date.now();
        set((state) => {
          const newPowerUps = { ...state.powerUps };
          let hasChanges = false;
          
          Object.keys(newPowerUps).forEach((key) => {
            const powerUp = newPowerUps[key as keyof typeof newPowerUps];
            if (powerUp.isActive && powerUp.endTime) {
              const timeRemaining = Math.max(0, Math.ceil((powerUp.endTime - now) / 1000));
              const oldTimeLeft = powerUp.timeLeft;
              powerUp.timeLeft = timeRemaining;
              
              if (timeRemaining !== oldTimeLeft) {
                hasChanges = true;
              }
              
              if (timeRemaining <= 0) {
                powerUp.isActive = false;
                powerUp.endTime = undefined;
              }
            }
          });
          
          return hasChanges ? { powerUps: newPowerUps } : state;
        });
      },
      

      
      calculatePlayerLevel: () => {
        const state = get();
        let newLevel = 1;
        
        // Berechne das Level basierend auf der aktuellen Erfahrung
        while (true) {
          const xpForNextLevel = getExperienceForLevel(newLevel + 1);
          if (state.experience < xpForNextLevel) {
            break;
          }
          newLevel++;
        }
        
        if (newLevel !== state.playerLevel) {
          const oldLevel = state.playerLevel;
          set({ playerLevel: newLevel });
          
          // Skalierte Level-up Belohnungen für unendliches System
          let coinReward = newLevel * 25;
          let gemReward = Math.floor(newLevel / 2);
          
          // Bonus-Belohnungen für hohe Level
          if (newLevel >= 1000000) {
            coinReward = newLevel * 1000000; // 1M Münzen pro Level
            gemReward = Math.floor(newLevel / 10); // Mehr Edelsteine
          } else if (newLevel >= 100000) {
            coinReward = newLevel * 100000; // 100K Münzen pro Level
            gemReward = Math.floor(newLevel / 20);
          } else if (newLevel >= 10000) {
            coinReward = newLevel * 10000; // 10K Münzen pro Level
            gemReward = Math.floor(newLevel / 50);
          } else if (newLevel >= 1000) {
            coinReward = newLevel * 1000; // 1K Münzen pro Level
            gemReward = Math.floor(newLevel / 100);
          } else if (newLevel >= 100) {
            coinReward = newLevel * 100; // 100 Münzen pro Level
            gemReward = Math.floor(newLevel / 10);
          }
          
          get().addCoins(coinReward);
          get().addGems(gemReward);
          
          // Spezielle Meilenstein-Belohnungen
          if (newLevel % 1000 === 0) {
            get().addGems(newLevel); // Bonus Edelsteine bei 1000er Meilensteinen
            console.log(`🎊 MEILENSTEIN! Level ${newLevel} erreicht! Bonus: ${newLevel} Edelsteine!`);
          }
          
          // Debug: Level-up Info für Verständnis
          console.log(`🎉 Level Up! ${oldLevel} → ${newLevel} | XP: ${state.experience}/${getExperienceForLevel(newLevel + 1)} | Belohnung: ${formatNumber(coinReward)} 🪙 + ${gemReward} 💎`);
          
          // Prüfe Steve-Freischaltung und -Upgrade nach Level-Up
          get().checkAndUnlockSteve();
          get().checkAndUpgradeSteve();
        }
      },
      
      getUpgradeCost: (upgradeType, currentLevel) => {
        const state = get();
        const baseCosts = {
          shovel: 10,
          autoDigger: 50,
          renovation: 100,
          fertilizer: 200,
          wateringCan: 150,
          magicSeeds: 300,
          gardenGnome: 500,
          rainMaker: 400,
          composter: 800,
          goldDetector: 1000,
          timeAccelerator: 1500,
          luckCharm: 600,
          autoHarvester: 1200,
          megaMultiplier: 2000,
          crystalGenerator: 2500,
          quantumShovel: 3000,
          gardenBot: 3500,
          plantWhisperer: 750,
          seedVault: 900,
          soilAnalyzer: 1100,
          weatherStation: 1400,
          beeHive: 650,
          treeFarm: 1800,
          flowerPower: 550,
          herbGarden: 700,
          butterflyGarden: 850,
          rockGarden: 950,
          waterFeature: 1300,
          gardenLighting: 1600,
        };
        
        // Bestimme baseCost (wie im UpgradeShop)
        let baseCost: number;
        if (String(upgradeType).startsWith('procedural_')) {
          // Für prozedurale Werkzeuge: Verwende baseCost aus proceduralTools
          const proceduralTool = state.proceduralTools[upgradeType];
          baseCost = proceduralTool?.baseCost || 1000;
        } else {
          // Für normale Werkzeuge: Verwende baseCosts Tabelle
          baseCost = baseCosts[upgradeType as keyof typeof baseCosts] || 10;
        }
        
        const safeCurrentLevel = Math.max(0, currentLevel || 0);
        
        let cost = baseCost;
        
        // Spezielle Skalierung für Gartenbereiche (renovation)
        if (upgradeType === 'renovation') {
          // Sanftere Skalierung für Level 1-100
          if (safeCurrentLevel <= 10) {
            cost = Math.floor(baseCost * Math.pow(1.4, safeCurrentLevel)); // Normale Skalierung für frühe Level
          } else if (safeCurrentLevel <= 50) {
            cost = Math.floor(baseCost * Math.pow(1.4, 10) * Math.pow(1.2, safeCurrentLevel - 10)); // Langsamere Skalierung
          } else {
            cost = Math.floor(baseCost * Math.pow(1.4, 10) * Math.pow(1.2, 40) * Math.pow(1.1, safeCurrentLevel - 50)); // Noch langsamere Skalierung
          }
        } else {
          // Normale Werkzeug-Skalierung (bleibt wie vorher)
          cost = Math.floor(baseCost * Math.pow(1.5, safeCurrentLevel));
        }
        
        // Prestige Upgrade Discount
        let discount = (state.prestige?.permanentBonuses?.upgradeDiscount || 0) * 0.05; // 5% pro Level
        
        // Begleiter Upgrade-Rabatt
        const companionDiscount = state.getCompanionBonus('upgradeDiscount');
        if (companionDiscount > 0) {
          discount += companionDiscount / 100; // Prozent zu Dezimal
        }
        
        cost = Math.floor(cost * (1 - Math.min(discount, 0.8))); // Maximal 80% Rabatt
        
        return Math.max(1, cost); // Mindestens 1 Münze
      },
      
      getAreaBonus: (area) => {
        const state = get();
        const areaData = state.gardenAreas[area];
        
        // Skalierte Basis-Berechnung für Level 1-100
        // Verwende logarithmische Skalierung für bessere Balance
        let bonus = areaData.level * 2; // Basis für niedrige Level
        
        // Bonus-Skalierung für höhere Level (weniger exponentiell)
        if (areaData.level > 10) {
          bonus += Math.floor(Math.log(areaData.level) * 5); // Logarithmischer Bonus
        }
        if (areaData.level > 50) {
          bonus += Math.floor((areaData.level - 50) * 0.5); // Reduzierter Bonus für sehr hohe Level
        }
        
        // Spezielle Boni für verschiedene Bereiche
        switch (area) {
          case 'roseGarden':
            bonus *= 1.5; // Rosen sind wertvoll
            break;
          case 'japaneseGarden':
            bonus *= 1.3; // Zen bringt Effizienz
            break;
          case 'wildflowerMeadow':
            bonus *= 1.2; // Biodiversität
            break;
          case 'sculptureGarden':
            bonus *= 1.4; // Kunst ist wertvoll
            break;
          case 'enchantedGrove':
            bonus *= 2.0; // Feenmagie! (reduziert für Balance)
            break;
          case 'crystalCave':
            bonus *= 1.8; // Kristall-Power (reduziert)
            break;
          case 'moonlightGarden':
            // Mondphasen-Bonus (variiert mit Zeit)
            const hour = new Date().getHours();
            const moonBonus = hour >= 20 || hour <= 6 ? 2.2 : 1.6; // Nachts stärker (reduziert)
            bonus *= moonBonus;
            break;
          case 'dragonLair':
            bonus *= 3.0; // DRACHEN-MACHT! (reduziert für Balance)
            break;
          // Klick-fokussierte Bereiche (diese werden in getClickMultiplier verwendet)
          case 'goldMine':
            bonus *= 1.2; // Goldmine bringt auch etwas passives Einkommen
            break;
          case 'treasureVault':
            bonus *= 1.3; // Schatzkammer sammelt Reichtümer
            break;
          case 'alchemyLab':
            bonus *= 1.4; // Alchemie verwandelt Materialien
            break;
          case 'magicForge':
            bonus *= 1.5; // Magische Schmiede verstärkt alles
            break;
        }
        
        // Plant Whisperer verstärkt alle Gartenbereiche
        if (state.upgrades.plantWhisperer > 0) {
          bonus *= (1 + state.upgrades.plantWhisperer * 0.2); // Reduziert von 0.3
        }
        
        // Soil Analyzer optimiert Bodenqualität
        if (state.upgrades.soilAnalyzer > 0) {
          bonus += state.upgrades.soilAnalyzer * Math.max(1, Math.floor(areaData.level / 10));
        }
        
        // Weather Station gibt Wetter-Boni
        if (state.upgrades.weatherStation > 0 && Math.random() < 0.2) {
          bonus *= (1 + state.upgrades.weatherStation * 0.08); // Reduziert von 0.1
        }
        
        // Prestige Garden Efficiency Bonus
        let gardenEfficiency = 1 + (state.prestige?.permanentBonuses?.gardenEfficiency || 0) * 0.15; // Reduziert von 0.2
        
        // Begleiter Garten-Bonus
        const companionGardenBonus = state.getCompanionBonus('gardenBonus');
        if (companionGardenBonus > 0) {
          gardenEfficiency += companionGardenBonus / 100;
        }
        
        bonus *= gardenEfficiency;
        
        return Math.floor(bonus);
      },
      
      getTotalCoinsPerSecond: () => {
        const state = get();
        
        // Optimierter Cache-Key für bessere Performance
        const activeCompanionIds = state.companions.active?.map(c => c?.id).filter(Boolean).join(',') || 'none';
        const powerUpFlags = [
          state.powerUps.goldRush.isActive ? 1 : 0,
          state.powerUps.speedBoost.isActive ? 1 : 0,
          state.powerUps.timeWarp.isActive ? 1 : 0,
          state.powerUps.gardenBlessing.isActive ? 1 : 0,
          state.powerUps.clickFrenzy.isActive ? 1 : 0,
          state.powerUps.cosmicBoost.isActive ? 1 : 0,
          state.powerUps.dragonPower.isActive ? 1 : 0
        ].join('');
        const cacheKey = `cps_${state.playerLevel}_${state.autoClickPower}_${powerUpFlags}_${activeCompanionIds}`;
        
        // Prüfe Cache (nur für hohe Levels)
        if (state.playerLevel > 5000 && state._coinsPerSecondCache && state._coinsPerSecondCache.key === cacheKey) {
          return state._coinsPerSecondCache.value;
        }
        
        let total = state.autoClickPower;
        
        // Prestige Auto-Clicker Efficiency Bonus
        const autoClickerEfficiency = 1 + (state.prestige?.permanentBonuses?.autoClickerEfficiency || 0) * 0.3;
        total *= autoClickerEfficiency;
        
        // Optimierte Gartenbereich-Berechnung (nur wenn nötig)
        if (state.playerLevel < 5000) {
          // Normale Berechnung für niedrige Level
          Object.keys(state.gardenAreas).forEach(areaKey => {
            const area = areaKey as keyof typeof state.gardenAreas;
            total += state.getAreaBonus(area) / 60; // Pro Sekunde statt pro Minute
          });
        } else {
          // Optimierte Berechnung für hohe Level - nur die wichtigsten Bereiche
          const keyAreas = ['goldMine', 'treasureVault', 'alchemyLab', 'magicForge'];
          keyAreas.forEach(areaKey => {
            const area = areaKey as keyof typeof state.gardenAreas;
            if (state.gardenAreas[area]) {
              total += state.getAreaBonus(area) / 60;
            }
          });
        }
        
        // Auto Harvester
        total += state.upgrades.autoHarvester * 2;
        
        // Garden Bot
        total += state.upgrades.gardenBot * 5;
        
        // Rain Maker
        total += state.upgrades.rainMaker * 2;
        
        // Garten-spezifische Werkzeuge (nur für niedrige Level oder wenn aktiv)
        if (state.playerLevel < 5000 || state.upgrades.treeFarm > 0 || state.upgrades.herbGarden > 0 || state.upgrades.beeHive > 0 || state.upgrades.waterFeature > 0) {
          total += state.upgrades.treeFarm * 3; // Bäume produzieren viel
          total += state.upgrades.herbGarden * 1.5; // Kräuter für Heilung
          total += state.upgrades.beeHive * 2.5; // Bienen bestäuben für Bonuserträge
          total += state.upgrades.waterFeature * 1; // Wasserspiele für Ruhe
        }
        
        // Speed Boost Power-Up
        if (state.powerUps.speedBoost.isActive) {
          total *= 2;
        }
        
        // Time Accelerator
        if (state.upgrades.timeAccelerator > 0) {
          total *= (1 + state.upgrades.timeAccelerator * 0.2);
        }
        
        // Neue Power-Up-Effekte
        if (state.powerUps.timeWarp.isActive) {
          total *= 10; // Alles läuft 10x schneller
        }
        
        if (state.powerUps.gardenBlessing.isActive) {
          total *= 3; // Verdreifacht Garten-Bereiche Boni
        }
        
        if (state.powerUps.clickFrenzy.isActive) {
          total *= 5; // Auto-Klicks werden zu Mega-Klicks
        }
        
        if (state.powerUps.cosmicBoost.isActive) {
          total *= 10; // Alle Boni x10
        }
        
        if (state.powerUps.dragonPower.isActive) {
          total *= 25; // Legendärer Drachen-Boost
        }
        
        // Prestige Income Multiplier
        const incomeMultiplier = 1 + (state.prestige?.permanentBonuses?.incomeMultiplier || 0) * 0.25;
        total *= incomeMultiplier;
        
        // Begleiter-Boni (nur wenn Begleiter vorhanden)
        if (state.companions.active && state.companions.active.length > 0) {
          const companionCoinPerSecond = state.getCompanionBonus('coinPerSecond');
          if (companionCoinPerSecond > 0) {
            total += companionCoinPerSecond;
          }
          
          const companionAutoClickBonus = state.getCompanionBonus('autoClickPower');
          if (companionAutoClickBonus > 0) {
            total += companionAutoClickBonus;
          }
        }
        
        // Auto Gräber Xdustries Aktien-Bonus
        const autoGraberBonus = state.getAutoGraberBonusFromStocks();
        if (autoGraberBonus > 0) {
          total += autoGraberBonus;
        }
        
        // Mega-Prestige Globaler Multiplikator
        const megaPrestigeMultiplier = state.getMegaPrestigeMultiplier();
        if (megaPrestigeMultiplier > 1) {
          total *= megaPrestigeMultiplier;
        }
        
        // Premium-Multiplikatoren
        if (state.globalMultipliers?.totalMultiplier && state.globalMultipliers.totalMultiplier > 1) {
          total *= state.globalMultipliers.totalMultiplier;
        }
        
        // Video-Werbung Multiplikator (2x wenn angesammelte Zeit vorhanden)
        if (state.videoRewards.accumulatedTime > 0) {
          total *= 2;
        }
        
        // Cache für hohe Level mit Memory-Optimierung
        if (state.playerLevel > 5000) {
          const now = Date.now();
          // Cache nur alle 5 Sekunden aktualisieren und nach 30 Sekunden löschen
          if (!state._coinsPerSecondCache || 
              state._coinsPerSecondCache.key !== cacheKey || 
              now - state._coinsPerSecondCache.timestamp > 5000) {
            set((state) => ({
              _coinsPerSecondCache: {
                key: cacheKey,
                value: total,
                timestamp: now
              }
            }));
          }
          
          // Cache-Bereinigung nach 30 Sekunden
          if (state._coinsPerSecondCache && now - state._coinsPerSecondCache.timestamp > 30000) {
            set((state) => ({
              _coinsPerSecondCache: undefined
            }));
          }
        }
        
        return total;
      },

      // Berechnet den Auto-Gräber-Bonus aus Auto Gräber Xdustries Aktien
      getAutoGraberBonusFromStocks: () => {
        const state = get();
        const portfolio = state.stockMarket.portfolio;
        
        // Suche nach Auto Gräber Xdustries Aktien im Portfolio
        const autoGraberPosition = portfolio.find(position => position.stockId === 'auto_graber_xdustries');
        
        if (autoGraberPosition) {
          // Jeder Anteil gibt 1 Münze pro Sekunde
          return autoGraberPosition.shares * 1;
        }
        
        return 0;
      },
      
      getClickMultiplier: () => {
        const state = get();
        
        // Cache-Key für die Berechnung - jetzt mit aktiven Begleitern
        const activeCompanionIds = state.companions.active?.map(c => c?.id).filter(Boolean).join(',') || 'none';
        const cacheKey = `clickMultiplier_${state.playerLevel}_${state.powerUps.megaMultiplier.isActive ? 1 : 0}_${state.powerUps.infiniteEnergy.isActive ? 1 : 0}_${state.powerUps.cosmicBoost.isActive ? 1 : 0}_${state.powerUps.dragonPower.isActive ? 1 : 0}_${activeCompanionIds}`;
        
        // Prüfe Cache (nur für hohe Levels)
        if (state.playerLevel > 5000 && state._clickMultiplierCache && state._clickMultiplierCache.key === cacheKey) {
          return state._clickMultiplierCache.value;
        }
        
        let multiplier = 1;
        
        // Player Level Bonus - skaliert mit höheren Leveln
        let levelBonus = 0;
        if (state.playerLevel >= 1000000) {
          levelBonus = (state.playerLevel - 1) * 10; // Massive Boni für Ultra-Level
        } else if (state.playerLevel >= 100000) {
          levelBonus = (state.playerLevel - 1) * 5; // Große Boni für Mega-Level
        } else if (state.playerLevel >= 10000) {
          levelBonus = (state.playerLevel - 1) * 2; // Mittlere Boni für Super-Level
        } else if (state.playerLevel >= 1000) {
          levelBonus = (state.playerLevel - 1) * 1; // Erhöhte Boni für hohe Level
        } else if (state.playerLevel >= 100) {
          levelBonus = (state.playerLevel - 1) * 0.5; // Mittlere Boni
        } else {
          levelBonus = (state.playerLevel - 1) * 0.1; // Normale Boni für niedrige Level
        }
        multiplier += levelBonus;
        
        // Prozedurale Klick-Werkzeuge Boni
        if (state.proceduralClickTools) {
          Object.entries(state.proceduralClickTools).forEach(([key, tool]) => {
            const level = state.upgrades[key] || 0;
            if (level > 0) {
              switch (tool.effectType) {
                case 'clickMultiplier':
                  multiplier += tool.baseEffect * level;
                  break;
                case 'totalMultiplier':
                  multiplier *= (1 + tool.baseEffect * level);
                  break;
              }
            }
          });
        }
        
        // Prestige Click Multiplier Bonus
        multiplier += (state.prestige?.permanentBonuses?.clickMultiplier || 0) * 0.5;
        
        // Basis Upgrade Boni
        multiplier += (state.upgrades.fertilizer || 0) * 0.2;
        multiplier += (state.upgrades.wateringCan || 0) * 0.15;
        multiplier += (state.upgrades.luckCharm || 0) * 0.1;
        multiplier += (state.upgrades.megaMultiplier || 0) * 0.5;
        
        // Quantum Shovel - Klicks wirken mehrfach (großer Bonus)
        if (state.upgrades.quantumShovel > 0) {
          multiplier *= (1 + state.upgrades.quantumShovel); // Verdoppelt/Verdreifacht etc.
        }
        
        // Weitere fehlende Upgrades
        multiplier += (state.upgrades.gardenGnome || 0) * 0.3; // Magische Boni
        multiplier += (state.upgrades.rainMaker || 0) * 0.12; // Automatische Bewässerung
        multiplier += (state.upgrades.composter || 0) * 0.08; // Recycling-Boni
        
        // Erweiterte Werkzeug-Boni
        multiplier += (state.upgrades.goldDetector || 0) * 0.25;
        multiplier += (state.upgrades.timeAccelerator || 0) * 0.3;
        multiplier += (state.upgrades.autoHarvester || 0) * 0.18;
        multiplier += (state.upgrades.crystalGenerator || 0) * 0.12;
        multiplier += (state.upgrades.gardenBot || 0) * 0.35;
        
        // Garten-spezifische Werkzeug Boni
        multiplier += (state.upgrades.flowerPower || 0) * 0.15;
        multiplier += (state.upgrades.butterflyGarden || 0) * 0.12;
        multiplier += (state.upgrades.rockGarden || 0) * 0.08;
        multiplier += (state.upgrades.plantWhisperer || 0) * 0.22;
        multiplier += (state.upgrades.seedVault || 0) * 0.14;
        multiplier += (state.upgrades.soilAnalyzer || 0) * 0.16;
        multiplier += (state.upgrades.weatherStation || 0) * 0.18;
        multiplier += (state.upgrades.beeHive || 0) * 0.13;
        multiplier += (state.upgrades.treeFarm || 0) * 0.19;
        multiplier += (state.upgrades.herbGarden || 0) * 0.11;
        multiplier += (state.upgrades.waterFeature || 0) * 0.17;
        multiplier += (state.upgrades.gardenLighting || 0) * 0.1;
        multiplier += (state.upgrades.magicSeeds || 0) * 0.05;
        
        // Klick-fokussierte Gartenbereiche - GROSSE Klick-Boni!
        if (state.gardenAreas.goldMine.level > 0) {
          multiplier += state.gardenAreas.goldMine.level * 0.05; // 5% pro Level
        }
        if (state.gardenAreas.treasureVault.level > 0) {
          multiplier += state.gardenAreas.treasureVault.level * 0.08; // 8% pro Level
        }
        if (state.gardenAreas.alchemyLab.level > 0) {
          multiplier += state.gardenAreas.alchemyLab.level * 0.12; // 12% pro Level
        }
        if (state.gardenAreas.magicForge.level > 0) {
          multiplier += state.gardenAreas.magicForge.level * 0.15; // 15% pro Level
        }
        
        // Power-Up Boni
        if (state.powerUps.megaMultiplier.isActive) {
          multiplier *= 5; // Verfünffacht den Multiplier
        }
        
        if (state.powerUps.infiniteEnergy.isActive) {
          multiplier *= 2; // Verdoppelt zusätzlich
        }
        
        if (state.powerUps.cosmicBoost.isActive) {
          multiplier *= 10; // Verzehnfacht alle Boni
        }
        
        if (state.powerUps.dragonPower.isActive) {
          multiplier *= 25; // Legendärer Boost
        }
        
        // Begleiter-Boni
        const companionClickBonus = state.getCompanionBonus('clickMultiplier');
        if (companionClickBonus > 0) {
          multiplier += companionClickBonus / 100; // Prozent zu Dezimal
        }
        
        // Mega-Prestige Globaler Multiplikator
        const megaPrestigeMultiplier = state.getMegaPrestigeMultiplier();
        if (megaPrestigeMultiplier > 1) {
          multiplier *= megaPrestigeMultiplier;
        }
        
        // Premium-Multiplikatoren
        if (state.globalMultipliers?.totalMultiplier && state.globalMultipliers.totalMultiplier > 1) {
          multiplier *= state.globalMultipliers.totalMultiplier;
        }
        
        // Video-Werbung Multiplikator (2x wenn angesammelte Zeit vorhanden)
        if (state.videoRewards.accumulatedTime > 0) {
          multiplier *= 2;
        }
        
        const finalMultiplier = Math.max(1, multiplier);
        
        // Cache für hohe Level mit Memory-Optimierung
        if (state.playerLevel > 5000) {
          const now = Date.now();
          // Cache nur alle 5 Sekunden aktualisieren und nach 30 Sekunden löschen
          if (!state._clickMultiplierCache || 
              state._clickMultiplierCache.key !== cacheKey || 
              now - state._clickMultiplierCache.timestamp > 5000) {
            set((state) => ({
              _clickMultiplierCache: {
                key: cacheKey,
                value: finalMultiplier,
                timestamp: now
              }
            }));
          }
          
          // Cache-Bereinigung nach 30 Sekunden
          if (state._clickMultiplierCache && now - state._clickMultiplierCache.timestamp > 30000) {
            set((state) => ({
              _clickMultiplierCache: undefined
            }));
          }
        }
        
        return finalMultiplier;
      },
      
      getLevelProgress: () => {
        const state = get();
        const currentLevelXP = state.playerLevel > 1 ? getExperienceForLevel(state.playerLevel) : 0;
        const nextLevelXP = getExperienceForLevel(state.playerLevel + 1);
        const currentXPInLevel = state.experience - currentLevelXP;
        const xpNeededForNextLevel = nextLevelXP - currentLevelXP;
        const progress = xpNeededForNextLevel > 0 ? (currentXPInLevel / xpNeededForNextLevel) : 0;
        
        return {
          currentXP: Math.max(0, currentXPInLevel),
          nextLevelXP: xpNeededForNextLevel,
          progress: Math.min(1, Math.max(0, progress)),
        };
      },
      
      repairGardenAreas: () => {
        const state = get();
        const { repairedAreas, hasChanges } = repairGardenAreas(state.gardenAreas);
        
        if (hasChanges) {
          set({ gardenAreas: repairedAreas });
          console.log('Gartenbereiche wurden repariert:', repairedAreas);
        }
      },
      
      // Prestige-Funktionen
      canPrestige: () => {
        const state = get();
        const prestigeLevel = state.prestige.level || 0;
        
        // Dynamische Anforderungen basierend auf Prestige-Level
        const baseLevel = 100;
        const levelIncrement = 15 + Math.floor(prestigeLevel / 2);
        const requiredLevel = baseLevel + (prestigeLevel * levelIncrement);
        
        // Münzen-Anforderung steigt exponentiell
        const baseCoins = 100000;
        const coinMultiplier = 1.5 + (prestigeLevel * 0.3);
        const requiredCoins = Math.floor(baseCoins * Math.pow(coinMultiplier, prestigeLevel));
        
        // Gartenbereich-Anforderung steigt um 30 jedes Prestige
        const baseGardenLevel = 6;
        const gardenLevelIncrement = prestigeLevel * 30;
        const requiredGardenLevel = baseGardenLevel + gardenLevelIncrement;
        
        // Basis-Anforderungen
        const basicRequirements = 
          state.playerLevel >= requiredLevel && 
          state.achievements.totalCoinsEarned >= requiredCoins &&
          Object.values(state.gardenAreas).some(area => area.level >= requiredGardenLevel);
        
        if (!basicRequirements) return false;
        
        // Zusätzliche Anforderungen für höhere Prestige-Levels
        if (prestigeLevel >= 5) {
          const requiredUpgrades = Math.floor(prestigeLevel / 2) + 3;
          const currentUpgrades = Object.values(state.upgrades || {}).filter(level => level > 0).length;
          if (currentUpgrades < requiredUpgrades) return false;
        }
        
        if (prestigeLevel >= 10) {
          const requiredCompanions = Math.floor(prestigeLevel / 3) + 2;
          const currentCompanions = state.companions?.discovered?.length || 0;
          if (currentCompanions < requiredCompanions) return false;
        }
        
        if (prestigeLevel >= 15) {
          const requiredMatchThreeWins = Math.floor(prestigeLevel / 2) + 5;
          const currentWins = state.achievements?.matchThreeWins || 0;
          if (currentWins < requiredMatchThreeWins) return false;
        }
        
        if (prestigeLevel >= 20) {
          const requiredDailyStreak = Math.floor(prestigeLevel / 4) + 7;
          const currentStreak = state.achievements?.dailyStreakRecord || 0;
          if (currentStreak < requiredDailyStreak) return false;
        }
        
        if (prestigeLevel >= 25) {
          const requiredTerritories = Math.floor(prestigeLevel / 5) + 2;
          const conqueredTerritories = Object.keys(state.companions?.conquest?.territoryConquestCounts || {}).length;
          if (conqueredTerritories < requiredTerritories) return false;
        }
        
        return true;
      },
      
      calculatePrestigePoints: () => {
        const state = get();
        let points = 0;
        
        try {
          // Punkte basierend auf Level (mit Null-Check)
          const playerLevel = state.playerLevel || 1;
          points += Math.floor(playerLevel / 5);
          
          // Punkte basierend auf verdiente Münzen (mit Null-Check)
          const totalCoinsEarned = state.achievements?.totalCoinsEarned || 0;
          points += Math.floor(totalCoinsEarned / 50000);
          
          // Punkte basierend auf Gartenbereiche (mit Null-Check)
          if (state.gardenAreas && typeof state.gardenAreas === 'object') {
            Object.values(state.gardenAreas).forEach(area => {
              if (area && typeof area.level === 'number' && !isNaN(area.level)) {
                points += Math.floor(area.level / 2);
              }
            });
          }
          
          // Punkte basierend auf Upgrades (mit Null-Check)
          if (state.upgrades && typeof state.upgrades === 'object') {
            Object.values(state.upgrades).forEach(level => {
              if (typeof level === 'number' && !isNaN(level)) {
                points += Math.floor(level / 3);
              }
            });
          }
          
          // Bonus für Achievements (mit Null-Check)
          const achievements = state.achievements || {};
          points += achievements.matchThreeWins || 0;
          points += (achievements.perfectMatchThreeGames || 0) * 2;
          points += Math.floor((achievements.dailyStreakRecord || 0) / 2);
          
          // Begleiter Prestige-Bonus (mit Null-Check)
          try {
            const companionPrestigeBonus = state.getCompanionBonus('prestigeBonus');
            if (typeof companionPrestigeBonus === 'number' && companionPrestigeBonus > 0 && !isNaN(companionPrestigeBonus)) {
              points = Math.floor(points * (1 + companionPrestigeBonus / 100));
            }
          } catch (error) {
            console.warn('Fehler beim Berechnen des Begleiter-Prestige-Bonus:', error);
            // Fallback: Ignoriere Begleiter-Bonus bei Fehler
          }
          
          // Finale Validierung
          if (isNaN(points) || !isFinite(points)) {
            console.warn('NaN oder unendlicher Wert in calculatePrestigePoints, verwende Fallback');
            points = 1;
          }
          
          return Math.max(1, points);
        } catch (error) {
          console.error('Fehler in calculatePrestigePoints:', error);
          return 1; // Fallback-Wert
        }
      },
      
      performPrestige: () => {
        const state = get();
        if (!state.canPrestige()) return;
        
        const prestigePoints = state.calculatePrestigePoints();
        const today = new Date().toDateString();
        
        // Behalte permanente Boni und füge neue Prestige-Punkte hinzu
        const newPrestigeLevel = state.prestige.level + 1;
        

        const newTotalPoints = state.prestige.totalPoints + prestigePoints;
        const newPoints = state.prestige.points + prestigePoints;
        
        // Berechne Startmünzen basierend auf permanenten Boni
        const startingCoins = 10 + state.prestige.permanentBonuses.startingCoins * 50;
        
        // Berechne die Max-Level der Gartenbereiche basierend auf Eroberungen
        const territoryToGardenMapping: { [key: string]: { area: keyof typeof state.gardenAreas, increase: number } } = {
          'Grüne Wiesen': { area: 'lawn', increase: 25 },
          'Blumenwiese': { area: 'flowerBed', increase: 30 },
          'Kristallsee': { area: 'pond', increase: 35 },
          'Steinterrasse': { area: 'terrace', increase: 40 },
          'Kristallgewächshaus': { area: 'greenhouse', increase: 45 },
          'Uralter Brunnen': { area: 'fountain', increase: 50 },
          'Dunkler Wald': { area: 'orchard', increase: 55 },
          'Fruchtbare Felder': { area: 'veggiePatch', increase: 60 },
          'Rosental': { area: 'roseGarden', increase: 65 },
          'Zen-Tempel': { area: 'japaneseGarden', increase: 70 },
          'Mystischer Sumpf': { area: 'wildflowerMeadow', increase: 75 },
          'Steinige Berge': { area: 'sculptureGarden', increase: 80 },
          'Verzauberter Hain': { area: 'enchantedGrove', increase: 85 },
          'Kristallhöhle': { area: 'crystalCave', increase: 90 },
          'Mondschein-Garten': { area: 'moonlightGarden', increase: 95 },
          'Drachenhort': { area: 'dragonLair', increase: 100 },
          'Goldmine': { area: 'goldMine', increase: 120 },
          'Sandwüste': { area: 'treasureVault', increase: 140 },
          'Alchemie-Turm': { area: 'alchemyLab', increase: 160 },
          'Feuervulkan': { area: 'magicForge', increase: 180 },
        };
        
        // Berechne die neuen Max-Level basierend auf Eroberungen
        const newGardenAreas = { ...initialGardenAreas };
        Object.keys(state.companions.conquest.territoryConquestCounts).forEach(territoryName => {
          const conquestCount = state.companions.conquest.territoryConquestCounts[territoryName];
          const mapping = territoryToGardenMapping[territoryName];
          
          if (mapping && conquestCount > 0) {
            const totalIncrease = mapping.increase * conquestCount;
            // Verwende immer die Basis-Max-Level (1000) + nur die durch Eroberungen verdienten Level
            newGardenAreas[mapping.area].maxLevel = 1000 + totalIncrease;
          }
        });
        
        set({
          // Reset Ressourcen
          coins: startingCoins,
          gems: Math.floor(state.gems * 0.1), // Behalte 10% der Edelsteine
          experience: 0,
          playerLevel: 1,
          
          // Reset Upgrades (aber behalte prozedurale Werkzeuge)
          upgrades: {
            shovelLevel: 1,
            autoDiggerLevel: 0,
            fertilizer: 0,
            wateringCan: 0,
            magicSeeds: 0,
            gardenGnome: 0,
            rainMaker: 0,
            composter: 0,
            goldDetector: 0,
            timeAccelerator: 0,
            luckCharm: 0,
            autoHarvester: 0,
            megaMultiplier: 0,
            crystalGenerator: 0,
            quantumShovel: 0,
            gardenBot: 0,
            plantWhisperer: 0,
            seedVault: 0,
            soilAnalyzer: 0,
            weatherStation: 0,
            beeHive: 0,
            treeFarm: 0,
            flowerPower: 0,
            herbGarden: 0,
            butterflyGarden: 0,
            rockGarden: 0,
            waterFeature: 0,
            gardenLighting: 0,
            // Behalte prozedurale Werkzeuge bei Level 0
            ...Object.keys(state.proceduralTools).reduce((acc, key) => {
              acc[key] = 0;
              return acc;
            }, {} as { [key: string]: number }),
          },
          
          // Reset Gartenbereiche aber behalte Max-Level basierend auf Eroberungen
          gardenAreas: newGardenAreas,
          
          // Reset Match-3
          matchThreeGame: {
            isUnlocked: false,
            currentLevel: 1,
            completedLevels: [],
            highScore: 0,
            consecutiveWins: 0,
            powerUpsUnlocked: [],
          },
          
          // Reset Power-Ups
          powerUps: {
            goldRush: { isActive: false, timeLeft: 0, endTime: undefined },
            speedBoost: { isActive: false, timeLeft: 0, endTime: undefined },
            luckyCharm: { isActive: false, timeLeft: 0, endTime: undefined },
            megaMultiplier: { isActive: false, timeLeft: 0, endTime: undefined },
            gemStorm: { isActive: false, timeLeft: 0, endTime: undefined },
            timeWarp: { isActive: false, timeLeft: 0, endTime: undefined },
            gardenBlessing: { isActive: false, timeLeft: 0, endTime: undefined },
            clickFrenzy: { isActive: false, timeLeft: 0, endTime: undefined },
            diamondMidas: { isActive: false, timeLeft: 0, endTime: undefined },
            infiniteEnergy: { isActive: false, timeLeft: 0, endTime: undefined },
            cosmicBoost: { isActive: false, timeLeft: 0, endTime: undefined },
            dragonPower: { isActive: false, timeLeft: 0, endTime: undefined },
            experienceRush: { isActive: false, timeLeft: 0, endTime: undefined },
          },
          
          // Reset Achievements (aber behalte Prestige-relevante)
          achievements: {
            totalClicks: 0,
            totalCoinsEarned: 0,
            areasRenovated: 0,
            matchThreeWins: 0,
            upgradesPurchased: 0,
            powerUpsUsed: 0,
            perfectMatchThreeGames: 0,
            dailyStreakRecord: state.achievements.dailyStreakRecord, // Behalten
            totalExperienceGained: 0,
          },
          
          // Update Prestige
          prestige: {
            ...state.prestige,
            level: newPrestigeLevel,
            points: newPoints,
            totalPoints: newTotalPoints,
            lastPrestigeDate: today,
            totalPrestiges: state.prestige.totalPrestiges + 1,
          },
          
          // Reset Click Power
          clickPower: 1,
          autoClickPower: 0,
          
          // Behalte prozedurale Werkzeuge (wie im UpgradeShop)
          proceduralTools: state.proceduralTools,
          
          // Behalte Börse-Daten
          stockMarket: state.stockMarket,
        });
        
        // Begleiter-Management bei Prestige: Nur die besten 3 aktiven Begleiter behalten
        const currentState = get();
        const currentActiveCompanions = currentState.companions.active;
        
        // Sortiere aktive Begleiter nach Seltenheit (legendary > epic > rare > common)
        const rarityOrder = { legendary: 4, epic: 3, rare: 2, common: 1 };
        const sortedActiveCompanions = [...currentActiveCompanions]
          .filter(companion => companion !== null)
          .sort((a, b) => {
            const rarityA = rarityOrder[a.type] || 0;
            const rarityB = rarityOrder[b.type] || 0;
            if (rarityA !== rarityB) {
              return rarityB - rarityA; // Höhere Seltenheit zuerst
            }
            // Bei gleicher Seltenheit: Sortiere nach Gesamtwert der Fähigkeiten
            const totalValueA = a.abilities.reduce((sum, ability) => sum + ability.value, 0);
            const totalValueB = b.abilities.reduce((sum, ability) => sum + ability.value, 0);
            return totalValueB - totalValueA;
          });
        
        // Behalte nur die besten 3 Begleiter
        const bestThreeCompanions = sortedActiveCompanions.slice(0, 3);
        
        // Update Begleiter-Status
        set((state) => ({
          companions: {
            ...state.companions,
            active: bestThreeCompanions,
            conquest: {
              ...state.companions.conquest,
              activeConquests: {}, // Setze laufende Eroberungen zurück
            },
          }
        }));
        
        
        // Generiere Begleiter-Karten nach Prestige
        get().generateCompanionCards();
        
        // Generiere prozedurales Werkzeug bei jedem zweiten Prestige
        if (newPrestigeLevel % 2 === 0) {
          const newTool = get().generateProceduralTool();
          get().addProceduralTool(newTool);
          // NEU: Auch prozedurales Klick-Werkzeug generieren
          const newClickTool = get().generateProceduralClickTool();
          get().addProceduralClickTool(newClickTool);
        }
        
        // Freischalten von Dolfjinmann bei Prestige Level 5 (entfernt - wird jetzt bei Mega Prestige Level 5 freigeschaltet)
      },
      
      purchasePrestigeUpgrade: (upgradeType) => {
        const state = get();
        const currentLevel = state.prestige.permanentBonuses[upgradeType];
        const cost = state.getPrestigeUpgradeCost(upgradeType, currentLevel);
        
        if (state.prestige.points >= cost) {
          set((prevState) => ({
            prestige: {
              ...prevState.prestige,
              points: prevState.prestige.points - cost,
              permanentBonuses: {
                ...prevState.prestige.permanentBonuses,
                [upgradeType]: prevState.prestige.permanentBonuses[upgradeType] + 1,
              },
            },
          }));
          return true;
        }
        return false;
      },

      purchasePrestigeUpgradeBulk: (upgradeType, quantity) => {
        const state = get();
        let currentLevel = state.prestige.permanentBonuses[upgradeType];
        let totalCost = 0;
        let actualPurchases = 0;
        let availablePoints = state.prestige.points;

        // Berechne wie viele Upgrades tatsächlich gekauft werden können
        for (let i = 0; i < quantity; i++) {
          const cost = state.getPrestigeUpgradeCost(upgradeType, currentLevel + i);
          if (availablePoints >= cost + totalCost) {
            totalCost += cost;
            actualPurchases++;
          } else {
            break;
          }
        }

        if (actualPurchases > 0) {
          set((prevState) => ({
            prestige: {
              ...prevState.prestige,
              points: prevState.prestige.points - totalCost,
              permanentBonuses: {
                ...prevState.prestige.permanentBonuses,
                [upgradeType]: prevState.prestige.permanentBonuses[upgradeType] + actualPurchases,
              },
            },
          }));
          return { success: true, actualPurchases, totalCost };
        }

        return { success: false, actualPurchases: 0, totalCost: 0 };
      },
      
      getPrestigeUpgradeCost: (upgradeType, currentLevel) => {
        const baseCosts = {
          clickMultiplier: 5,
          incomeMultiplier: 8,
          xpMultiplier: 6,
          startingCoins: 3,
          gemChance: 10,
          autoClickerEfficiency: 7,
          upgradeDiscount: 12,
          gardenEfficiency: 9,
        };
        
        const baseCost = baseCosts[upgradeType];
        return Math.floor(baseCost * Math.pow(1.8, currentLevel));
      },

      // Mega-Prestige-Funktionen
      canMegaPrestige: () => {
        const state = get();
        // Mindestens Prestige Level 10 erforderlich
        return state.prestige.level >= 10;
      },

      performMegaPrestige: () => {
        const state = get();
        if (!state.canMegaPrestige()) return;

        const today = new Date().toDateString();
        const totalPointsSpent = state.prestige.totalPoints;
        
        // Berechne neuen globalen Multiplikator: 1% pro Million Punkte
        const newGlobalMultiplier = 1 + (totalPointsSpent / 1000000) * 0.01;
        
        // Prüfe ob Dolfjinmann freigeschaltet werden soll (Mega Prestige Level 5)
        const newMegaPrestigeLevel = state.prestige.megaPrestige.level + 1;
        const shouldUnlockDolfjinmann = newMegaPrestigeLevel === 5 && 
          !state.companions.discovered.some(c => c.name.includes('Dolfjinmann'));

        // Reset ALLES außer Begleitern
        set({
          // Reset Ressourcen
          coins: 10,
          gems: 0,
          experience: 0,
          playerLevel: 1,
          
          // Reset Upgrades komplett, aber behalte prozedurale Werkzeuge
          upgrades: {
            shovelLevel: 1,
            autoDiggerLevel: 0,
            fertilizer: 0,
            wateringCan: 0,
            magicSeeds: 0,
            gardenGnome: 0,
            rainMaker: 0,
            composter: 0,
            goldDetector: 0,
            timeAccelerator: 0,
            luckCharm: 0,
            autoHarvester: 0,
            megaMultiplier: 0,
            crystalGenerator: 0,
            quantumShovel: 0,
            gardenBot: 0,
            plantWhisperer: 0,
            seedVault: 0,
            soilAnalyzer: 0,
            weatherStation: 0,
            beeHive: 0,
            treeFarm: 0,
            flowerPower: 0,
            herbGarden: 0,
            butterflyGarden: 0,
            rockGarden: 0,
            waterFeature: 0,
            gardenLighting: 0,
            // Prozedurale Werkzeuge Level auf 0 zurücksetzen
            ...Object.fromEntries(
              Object.keys(state.proceduralTools).map(key => [key, 0])
            ),
            ...Object.fromEntries(
              Object.keys(state.proceduralClickTools).map(key => [key, 0])
            ),
          },
          
          // Reset Gartenbereiche
          gardenAreas: initialGardenAreas,
          
          // Reset Match-3
          matchThreeGame: {
            isUnlocked: false,
            currentLevel: 1,
            completedLevels: [],
            highScore: 0,
            consecutiveWins: 0,
            powerUpsUnlocked: [],
          },
          
          // Reset Power-Ups
          powerUps: {
            goldRush: { isActive: false, timeLeft: 0 },
            speedBoost: { isActive: false, timeLeft: 0 },
            luckyCharm: { isActive: false, timeLeft: 0 },
            megaMultiplier: { isActive: false, timeLeft: 0 },
            gemStorm: { isActive: false, timeLeft: 0 },
            timeWarp: { isActive: false, timeLeft: 0 },
            gardenBlessing: { isActive: false, timeLeft: 0 },
            clickFrenzy: { isActive: false, timeLeft: 0 },
            diamondMidas: { isActive: false, timeLeft: 0 },
            infiniteEnergy: { isActive: false, timeLeft: 0 },
            cosmicBoost: { isActive: false, timeLeft: 0 },
            dragonPower: { isActive: false, timeLeft: 0 },
            experienceRush: { isActive: false, timeLeft: 0 },
          },
          
          // Reset Achievements komplett
          achievements: {
            totalClicks: 0,
            totalCoinsEarned: 0,
            areasRenovated: 0,
            matchThreeWins: 0,
            upgradesPurchased: 0,
            powerUpsUsed: 0,
            perfectMatchThreeGames: 0,
            dailyStreakRecord: 0,
            totalExperienceGained: 0,
          },
          
          // Reset Prestige komplett, aber behalte Mega-Prestige
          prestige: {
            level: 0,
            points: 0,
            totalPoints: 0,
            permanentBonuses: {
              clickMultiplier: 0,
              incomeMultiplier: 0,
              xpMultiplier: 0,
              startingCoins: 0,
              gemChance: 0,
              autoClickerEfficiency: 0,
              upgradeDiscount: 0,
              gardenEfficiency: 0,
            },
            lastPrestigeDate: '',
            totalPrestiges: 0,
            megaPrestige: {
              level: state.prestige.megaPrestige.level + 1,
              totalPointsSpent: state.prestige.megaPrestige.totalPointsSpent + totalPointsSpent,
              globalMultiplier: newGlobalMultiplier,
              lastMegaPrestigeDate: today,
            },
          },
          
          // Reset Click Power
          clickPower: 1,
          autoClickPower: 0,
          
          // BEGLEITER BLEIBEN ERHALTEN!
          // companions: state.companions (bleibt unverändert)
          
          // Behalte Börse-Daten
          stockMarket: state.stockMarket,
        });
        
        // Freischalten von Dolfjinmann bei Mega Prestige Level 5
        if (shouldUnlockDolfjinmann) {
          const dolfjinmann = generateDolfjinmann();
          get().addCompanionToCollection(dolfjinmann);
          console.log('🌟 Dolfjinmann wurde automatisch freigeschaltet! Mega Prestige Level 5 erreicht!');
        }
      },

      getMegaPrestigeMultiplier: () => {
        const state = get();
        return state.prestige.megaPrestige.globalMultiplier;
      },

      // Begleiter-Funktionen
      generateCompanionCards: () => {
        const cards = [];
        for (let i = 0; i < 3; i++) {
          cards.push(generateRandomCompanion());
        }
        set({ companions: { ...get().companions, pendingCards: cards } });
        return cards;
      },
      
      // Prüfe und freischalten von Dolfjinmann für bestehende Spieler
      checkAndUnlockDolfjinmann: () => {
        const state = get();
        const hasDolfjinmann = state.companions.discovered.some(c => c.name.includes('Dolfjinmann'));
        const hasMegaPrestigeLevel5 = state.prestige.megaPrestige.level >= 5;
        
        if (hasMegaPrestigeLevel5 && !hasDolfjinmann) {
          const dolfjinmann = generateDolfjinmann();
          state.addCompanionToCollection(dolfjinmann);
          console.log('🌟 Dolfjinmann wurde nachträglich freigeschaltet! Mega Prestige Level 5 bereits erreicht!');
        }
      },

      // Prüfe und freischalten von Steve bei Level 3
      checkAndUnlockSteve: () => {
        const state = get();
        const hasSteve = state.companions.discovered.some(c => c.name.includes('Steve') && !c.name.includes('Allmächtige'));
        const hasLevel3 = state.playerLevel >= 3;
        
        if (hasLevel3 && !hasSteve) {
          const steve = generateSteve(1);
          state.addCompanionToCollection(steve);
          console.log('👤 Steve wurde freigeschaltet! Level 3 erreicht!');
        }
      },

      // Prüfe und aktualisiere Steve zu "Steve der Allmächtige" bei Level 1000
      checkAndUpgradeSteve: () => {
        const state = get();
        const steve = state.companions.discovered.find(c => c.name.includes('Steve') && !c.name.includes('Allmächtige'));
        
        if (steve && steve.level && steve.level >= 1000) {
          // Erstelle neuen Steve der Allmächtige
          const allmightySteve = generateSteve(1000);
          
          // Ersetze den alten Steve
          set((state) => ({
            companions: {
              ...state.companions,
              discovered: state.companions.discovered.map(c => 
                c.id === steve.id ? allmightySteve : c
              ),
              active: state.companions.active.map(c => 
                c && c.id === steve.id ? allmightySteve : c
              )
            }
          }));
          
          console.log('⚡ Steve wurde zu Steve der Allmächtige! Level 1000 erreicht!');
        }
      },

      startCompanionCardDraw: (cost) => {
        const state = get();
        
        // Prüfe ob genug Diamanten vorhanden sind
        if (state.gems < cost) {
          return false;
        }
        
        // Prüfe ob bereits ein Kartenspiel läuft
        if (state.companions.pendingCards && state.companions.pendingCards.length > 0) {
          return false;
        }
        
        // Zahle die Diamanten
        if (!state.spendGems(cost)) {
          return false;
        }
        
        // Generiere 3 Begleiter-Karten für die Auswahl
        state.generateCompanionCards();
        
        return true;
      },

      selectCompanionCard: (companion) => {
        const state = get();
        
        // Prüfe ob es ein kostenloser Zug ist (pendingCards vom kostenlosen Zug)
        const isFreeDrawActive = state.companions.freeDrawLastUsed && 
          !state.companions.freeDrawAvailable && 
          state.companions.pendingCards;
        
        // Kostenloser Zug ist immer erfolgreich, normale Züge haben 50% Chance
        const success = isFreeDrawActive ? true : Math.random() < 0.5;
        
        if (success) {
          // Begleiter erfolgreich erhalten
          const newDiscovered = [...state.companions.discovered, companion];
          const newActive = [...state.companions.active];
          
          // Füge Begleiter zu einem freien Slot hinzu (bis zu 3)
          if (newActive.length < 3) {
            newActive.push(companion);
          } else {
            // Überschreibe den ersten Slot wenn alle belegt sind
            newActive[0] = companion;
          }
          
          set((currentState) => ({
            companions: {
              ...currentState.companions,
              discovered: newDiscovered,
              active: newActive,
              pendingCards: null,
            }
          }));
        } else {
          // Kein Begleiter erhalten
          set((currentState) => ({
            companions: {
              ...currentState.companions,
              pendingCards: null,
            }
          }));
        }
        
        return success;
      },

      setActiveCompanion: (companion, slot = 0) => {
        const state = get();
        const maxSlots = get().getMaxCompanionSlots();
        const newActive = [...state.companions.active];
        
        // Begrenzung: Maximal 100 aktive Begleiter
        if (newActive.filter(c => c).length >= 100) {
          return; // Keine weiteren Begleiter hinzufügen
        }
        
        // Stelle sicher, dass der Slot gültig ist (0 bis maxSlots-1)
        const targetSlot = Math.max(0, Math.min(maxSlots - 1, slot));
        
        // Finde einen freien Slot falls der gewünschte belegt ist
        let finalSlot = targetSlot;
        if (newActive[finalSlot]) {
          finalSlot = newActive.findIndex(c => !c);
          if (finalSlot === -1 && newActive.length < maxSlots) {
            finalSlot = newActive.length;
          } else if (finalSlot === -1) {
            finalSlot = targetSlot; // Überschreibe den gewünschten Slot
          }
        }
        
        newActive[finalSlot] = companion;
        
        set((state) => ({
          companions: {
            ...state.companions,
            active: newActive,
          },
          // Cache invalidieren für hohe Level
          _coinsPerSecondCache: state.playerLevel > 5000 ? undefined : state._coinsPerSecondCache,
          _clickMultiplierCache: state.playerLevel > 5000 ? undefined : state._clickMultiplierCache,
        }));
      },

      removeActiveCompanion: (slot) => {
        const state = get();
        const newActive = [...state.companions.active];
        
        if (slot >= 0 && slot < newActive.length) {
          newActive.splice(slot, 1); // Entferne den Begleiter aus dem Array
        }
        
        set((state) => ({
          companions: {
            ...state.companions,
            active: newActive,
          },
          // Cache invalidieren für hohe Level
          _coinsPerSecondCache: state.playerLevel > 5000 ? undefined : state._coinsPerSecondCache,
          _clickMultiplierCache: state.playerLevel > 5000 ? undefined : state._clickMultiplierCache,
        }));
      },

      removeAllActiveCompanions: () => {
        set((state) => ({
          companions: {
            ...state.companions,
            active: [], // Entferne alle aktiven Begleiter auf einmal
          },
          // Cache invalidieren für hohe Level
          _coinsPerSecondCache: state.playerLevel > 5000 ? undefined : state._coinsPerSecondCache,
          _clickMultiplierCache: state.playerLevel > 5000 ? undefined : state._clickMultiplierCache,
        }));
      },

      getCompanionBonus: (abilityType) => {
        const state = get();
        
        try {
          const activeCompanions = state.companions?.active;
          
          if (!activeCompanions || !Array.isArray(activeCompanions) || activeCompanions.length === 0) {
            return 0;
          }
          
          // Optimierte Begleiter-Bonus-Berechnung mit Memory-Optimierung
          let totalBonus = 0;
          const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(abilityType);
          
          for (let i = 0; i < activeCompanions.length; i++) {
            const companion = activeCompanions[i];
            if (!companion || !companion.abilities || !Array.isArray(companion.abilities)) continue;
            
            // Suche nach der Fähigkeit (optimiert)
            let abilityValue = 0;
            for (let j = 0; j < companion.abilities.length; j++) {
              const ability = companion.abilities[j];
              if (ability && ability.type === abilityType && typeof ability.value === 'number' && !isNaN(ability.value)) {
                abilityValue = ability.value;
                break; // Erste Übereinstimmung gefunden
              }
            }
            
            if (abilityValue > 0) {
              // Kampf-Level verstärkt Standard-Fähigkeiten
              if (isStandardAbility && companion.battleLevel && typeof companion.battleLevel === 'number' && companion.battleLevel > 1) {
                const battleLevelMultiplier = 1 + ((companion.battleLevel - 1) * 0.1);
                abilityValue = Math.floor(abilityValue * battleLevelMultiplier);
              }
              
              // Validierung vor Addition
              if (!isNaN(abilityValue) && isFinite(abilityValue)) {
                totalBonus += abilityValue;
              }
            }
          }
          
          // Globale Forschungs-Boni hinzufügen
          const globalBonuses = state.companions.research.globalBonuses;
          if (globalBonuses) {
            // Füge relevante globale Boni basierend auf abilityType hinzu
            switch (abilityType) {
              case 'clickMultiplier':
                totalBonus += globalBonuses.attack;
                break;
              case 'coinPerSecond':
                totalBonus += globalBonuses.defense;
                break;
              case 'xpBonus':
                totalBonus += globalBonuses.experience;
                break;
              case 'gemChance':
                totalBonus += globalBonuses.abilityChance;
                break;
              default:
                // Für andere Fähigkeiten füge einen allgemeinen Bonus hinzu
                totalBonus += globalBonuses.hp;
                break;
            }
          }
          
          // Finale Validierung
          if (isNaN(totalBonus) || !isFinite(totalBonus)) {
            console.warn('NaN oder unendlicher Wert in getCompanionBonus, verwende Fallback');
            return 0;
          }
          
          return totalBonus;
        } catch (error) {
          console.error('Fehler in getCompanionBonus:', error);
          return 0; // Fallback-Wert
        }
      },

      addCompanionToCollection: (companion) => {
        set((state) => ({
          companions: {
            ...state.companions,
            discovered: [...state.companions.discovered, companion],
          }
        }));
      },

      formatNumber: (num) => formatNumber(num),
      
      // Kostenloser Begleiter-Zug Funktionen
      canUseFreeCompanionDraw: () => {
        const state = get();
        if (!state.companions.freeDrawAvailable) {
          return false;
        }
        
        if (!state.companions.freeDrawLastUsed) {
          return true; // Noch nie verwendet
        }
        
        const lastUsed = new Date(state.companions.freeDrawLastUsed);
        const now = new Date();
        const timeDiff = now.getTime() - lastUsed.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60); // 60 Minuten = 1 Stunde
        
        return hoursDiff >= 1; // 60 Minuten vergangen
      },

      useFreeCompanionDraw: () => {
        const state = get();
        if (!state.canUseFreeCompanionDraw()) {
          return;
        }
        
        // Generiere 3 Begleiter-Karten für die Auswahl
        const cards = state.generateCompanionCards();
        
        set((state) => ({
          companions: {
            ...state.companions,
            pendingCards: cards,
            freeDrawLastUsed: new Date().toISOString(),
            freeDrawAvailable: false,
          }
        }));
        
        // Cooldown wird durch updateCooldowns() überprüft
       },

       // Kostenloser Premium-Spin Funktionen
       canUseFreePremiumSpin: () => {
         const state = get();
         if (!state.companions.freePremiumSpinAvailable) {
           return false;
         }
         
         if (!state.companions.freePremiumSpinLastUsed) {
           return true; // Noch nie verwendet
         }
         
         const lastUsed = new Date(state.companions.freePremiumSpinLastUsed);
         const now = new Date();
         const timeDiff = now.getTime() - lastUsed.getTime();
         const hoursDiff = timeDiff / (1000 * 60 * 60); // 12 Stunden
         
         return hoursDiff >= 12; // 12 Stunden vergangen
       },

       useFreePremiumSpin: () => {
         const state = get();
         if (!state.canUseFreePremiumSpin()) {
           return;
         }
         
         // Führe einen Premium-Spin durch
         const result = Math.random();
         let companion: Companion | null = null;
         let gems = 0;
         
         if (result < 0.11) { // 11% Chance auf legendären Begleiter
           companion = generateUpgradedCompanion('legendary');
         } else if (result < 0.44) { // 33% Chance auf epischen Begleiter (11% + 33% = 44%)
           companion = generateUpgradedCompanion('epic');
         } else if (result < 0.64) { // 20% Chance auf seltenen Begleiter
           companion = generateUpgradedCompanion('rare');
         } else if (result < 0.84) { // 20% Chance auf gewöhnlichen Begleiter
           companion = generateUpgradedCompanion('common');
         } else { // 16% Chance auf 50% Rückerstattung (als Edelsteine)
           gems = Math.floor(state.gems * 0.05); // 5% der aktuellen Edelsteine als Bonus
         }
         
         if (companion) {
           state.addCompanionToCollection(companion);
         }
         
         if (gems > 0) {
           state.addGems(gems);
         }
         
         set((state) => ({
           companions: {
             ...state.companions,
             freePremiumSpinLastUsed: new Date().toISOString(),
             freePremiumSpinAvailable: false,
           }
         }));
         
         // Cooldown wird durch updateCooldowns() überprüft
       },

       setFreePremiumSpinActive: (active) => {
         set((state) => ({
           companions: {
             ...state.companions,
             freePremiumSpinActive: active,
           }
         }));
       },
       
       updateCooldowns: () => {
         const state = get();
         let needsUpdate = false;
         let newCompanions = { ...state.companions };
         
         // Überprüfe kostenlosen Begleiter-Zug
         if (!state.companions.freeDrawAvailable && state.companions.freeDrawLastUsed) {
           const lastUsed = new Date(state.companions.freeDrawLastUsed);
           const now = new Date();
           const timeDiff = now.getTime() - lastUsed.getTime();
           const hoursDiff = timeDiff / (1000 * 60 * 60);
           
           if (hoursDiff >= 1) { // 1 Stunde vergangen
             newCompanions.freeDrawAvailable = true;
             needsUpdate = true;
           }
         }
         
         // Überprüfe kostenlosen Premium-Spin
         if (!state.companions.freePremiumSpinAvailable && state.companions.freePremiumSpinLastUsed) {
           const lastUsed = new Date(state.companions.freePremiumSpinLastUsed);
           const now = new Date();
           const timeDiff = now.getTime() - lastUsed.getTime();
           const hoursDiff = timeDiff / (1000 * 60 * 60);
           
           if (hoursDiff >= 12) { // 12 Stunden vergangen
             newCompanions.freePremiumSpinAvailable = true;
             needsUpdate = true;
           }
         }
         
         // Update nur wenn nötig
         if (needsUpdate) {
           set((state) => ({
             companions: newCompanions
           }));
         }
       },
      
      getMaxCompanionSlots: () => {
        const state = get();
        const level = state.playerLevel || 1;
        
        // Basis: 3 Slots
        // Ab Level 100: +1 Slot alle 50 Level
        if (level < 100) {
          return 3;
        }
        
        const additionalSlots = Math.floor((level - 100) / 50) + 1;
        return 3 + additionalSlots;
      },

      getCompanionsByType: (type) => {
        const state = get();
        // Filtere göttliche Begleiter aus für Upgrade-Zwecke
        return state.companions.discovered.filter(companion => 
          companion.type === type && !(companion.name.startsWith('★') && companion.name.endsWith('★'))
        );
      },

      upgradeCompanions: (type, specificIds) => {
        const state = get();
        
        // Wenn spezifische IDs angegeben sind, verwende diese
        let companionsToUpgrade: Companion[];
        if (specificIds && specificIds.length > 0) {
          companionsToUpgrade = state.companions.discovered.filter(companion => 
            specificIds.includes(companion.id) && 
            companion.type === type && 
            !(companion.name.startsWith('★') && companion.name.endsWith('★'))
          );
          
          // Prüfe ob genau 5 spezifische Begleiter ausgewählt wurden
          if (companionsToUpgrade.length !== 5) {
            return false;
          }
        } else {
          // Fallback: Filtere göttliche Begleiter aus (erkennbar an ★ Sternen)
          const companionsOfType = state.companions.discovered.filter(companion => 
            companion.type === type && !(companion.name.startsWith('★') && companion.name.endsWith('★'))
          );
          
          // Brauche mindestens 5 Begleiter des Typs (ohne göttliche)
          if (companionsOfType.length < 5) {
            return false;
          }
          
          // Nehme die ersten 5 Begleiter
          companionsToUpgrade = companionsOfType.slice(0, 5);
        }
        
        // Bestimme den Ziel-Typ
        let targetType: Companion['type'];
        let isLegendaryUpgrade = false;
        
        switch (type) {
          case 'common':
            targetType = 'rare';
            break;
          case 'rare':
            targetType = 'epic';
            break;
          case 'epic':
            targetType = 'legendary';
            break;
          case 'legendary':
            targetType = 'legendary'; // Legendäre werden zu stärkeren Legendären
            isLegendaryUpgrade = true;
            break;
          default:
            return false;
        }
        
        // Entferne die ausgewählten Begleiter
        const newDiscovered = state.companions.discovered.filter(companion => 
          !companionsToUpgrade.some(remove => remove.id === companion.id)
        );
        
        // Entferne auch aus aktiven Begleitern falls nötig
        const newActive = state.companions.active.map(activeCompanion => {
          if (activeCompanion && companionsToUpgrade.some(remove => remove.id === activeCompanion.id)) {
            return null;
          }
          return activeCompanion;
        }).filter(companion => companion !== null) as Companion[];
        
        // Generiere neuen Begleiter
        const newCompanion = generateUpgradedCompanion(targetType, isLegendaryUpgrade);
        
        set((state) => ({
          companions: {
            ...state.companions,
            discovered: [...newDiscovered, newCompanion],
            active: newActive,
          }
        }));
        
        return true;
      },

      // Neue Funktion für mystisch göttlich legendäre Begleiter
      upgradeToMysticDivine: (specificIds?: string[]) => {
        const state = get();
        
        // Filtere göttlich legendäre Begleiter (mit ★ Sternen)
        let divineCompanions: Companion[];
        if (specificIds && specificIds.length > 0) {
          divineCompanions = state.companions.discovered.filter(companion => 
            specificIds.includes(companion.id) && 
            companion.type === 'legendary' && 
            companion.name.startsWith('★') && companion.name.endsWith('★')
          );
        } else {
          divineCompanions = state.companions.discovered.filter(companion => 
            companion.type === 'legendary' && 
            companion.name.startsWith('★') && companion.name.endsWith('★')
          );
        }
        
        // Brauche genau 50 göttlich legendäre Begleiter
        if (divineCompanions.length < 50) {
          return false;
        }
        
        // Nehme die ersten 50 Begleiter
        const companionsToUpgrade = divineCompanions.slice(0, 50);
        
        // Entferne die ausgewählten Begleiter
        const newDiscovered = state.companions.discovered.filter(companion => 
          !companionsToUpgrade.some(remove => remove.id === companion.id)
        );
        
        // Entferne auch aus aktiven Begleitern falls nötig
        const newActive = state.companions.active.map(activeCompanion => {
          if (activeCompanion && companionsToUpgrade.some(remove => remove.id === activeCompanion.id)) {
            return null;
          }
          return activeCompanion;
        }).filter(companion => companion !== null) as Companion[];
        
        // Generiere mystisch göttlich legendären Begleiter
        const mysticCompanion = generateMysticDivineCompanion();
        
        set((state) => ({
          companions: {
            ...state.companions,
            discovered: [...newDiscovered, mysticCompanion],
            active: newActive,
          }
        }));
        
        return true;
      },

      // Begleiter Training & Reisen Funktionen
      getCompanionLevel: (companion) => {
        return companion.level || 1;
      },

      getCompanionExperience: (companion) => {
        return companion.experience || 0;
      },

      getCompanionExperienceForNextLevel: (level) => {
        return level * 100; // 100 XP pro Level
      },

      canCompanionLevelUp: (companion) => {
        const state = get();
        const currentLevel = state.getCompanionLevel(companion);
        const currentXP = state.getCompanionExperience(companion);
        const requiredXP = state.getCompanionExperienceForNextLevel(currentLevel);
        return currentXP >= requiredXP;
      },

      levelUpCompanion: (companionId) => {
        const state = get();
        const companion = state.companions.discovered.find(c => c.id === companionId);
        if (!companion || !state.canCompanionLevelUp(companion)) {
          return false;
        }

        const currentLevel = state.getCompanionLevel(companion);
        const requiredXP = state.getCompanionExperienceForNextLevel(currentLevel);

        // Verbessere nur Standard-Fähigkeiten beim manuellen Level-Up
        const updatedAbilities = companion.abilities.map(ability => {
          const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
          if (isStandardAbility) {
            return {
              ...ability,
              value: Math.floor(ability.value * 1.1) // 10% Verbesserung
            };
          }
          return ability;
        });

        set((state) => ({
          companions: {
            ...state.companions,
            discovered: state.companions.discovered.map(c => 
              c.id === companionId 
                ? { 
                    ...c, 
                    level: (c.level || 1) + 1,
                    experience: (c.experience || 0) - requiredXP,
                    abilities: updatedAbilities
                  }
                : c
            ),
            active: state.companions.active.map(c => 
              c && c.id === companionId 
                ? { 
                    ...c, 
                    level: (c.level || 1) + 1,
                    experience: (c.experience || 0) - requiredXP,
                    abilities: updatedAbilities
                  }
                : c
            )
          }
        }));

        return true;
      },

      startCompanionTraining: (companionId, durationMinutes) => {
        const state = get();
        const companion = state.companions.discovered.find(c => c.id === companionId);
        
        if (!companion || companion.isTraining || companion.isOnJourney) {
          return false;
        }

        // Prüfe ob Begleiter aktiv ist
        const isActive = state.companions.active.some(c => c && c.id === companionId);
        if (isActive) {
          return false; // Aktive Begleiter können nicht trainieren
        }

        const cost = durationMinutes * 10; // 10 Münzen pro Minute
        if (!state.spendCoins(cost)) {
          return false;
        }

        set((state) => ({
          companions: {
            ...state.companions,
            discovered: state.companions.discovered.map(c => 
              c.id === companionId 
                ? { 
                    ...c, 
                    isTraining: true,
                    trainingStartTime: new Date().toISOString(),
                    trainingDuration: durationMinutes
                  }
                : c
            )
          }
        }));

        return true;
      },

      completeCompanionTraining: (companionId) => {
        const state = get();
        const companion = state.companions.discovered.find(c => c.id === companionId);
        
        if (!companion || !companion.isTraining) {
          return;
        }

        // Forschungs-Boni für Erfahrung anwenden
        const globalBonuses = state.companions.research.globalBonuses;
        const experienceBonus = globalBonuses.experience || 0;
        const baseXpGained = (companion.trainingDuration || 0) * 2; // 2 XP pro Minute Training
        const xpGained = baseXpGained + (baseXpGained * experienceBonus / 100); // Erfahrungs-Bonus als Prozentsatz
        const currentNormalLevel = companion.level || 1;
        const currentNormalExperience = companion.experience || 0;
        const newNormalExperience = currentNormalExperience + xpGained;
        let newNormalLevel = currentNormalLevel;
        let finalNormalExperience = newNormalExperience;

        // Prüfe ob Level-Up möglich ist (kontinuierlich bis alle Level erreicht sind)
        let updatedAbilities = companion.abilities;
        while (finalNormalExperience >= (newNormalLevel * 100)) {
          finalNormalExperience = finalNormalExperience - (newNormalLevel * 100);
          newNormalLevel++;
          
          // Verbessere Standard-Fähigkeiten beim Level-Up
          updatedAbilities = updatedAbilities.map(ability => {
            const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
            if (isStandardAbility) {
              return {
                ...ability,
                value: Math.floor(ability.value * 1.1) // 10% Verbesserung
              };
            }
            return ability;
          });
        }

        set((state) => ({
          companions: {
            ...state.companions,
            discovered: state.companions.discovered.map(c => 
              c.id === companionId 
                ? { 
                    ...c, 
                    isTraining: false,
                    trainingStartTime: undefined,
                    trainingDuration: undefined,
                    level: newNormalLevel,
                    experience: finalNormalExperience,
                    abilities: updatedAbilities
                  }
                : c
            )
          }
        }));
      },

      startCompanionJourney: (companionId, journeyType, durationHours, area) => {
        const state = get();
        const companion = state.companions.discovered.find(c => c.id === companionId);
        
        if (!companion || companion.isTraining || companion.isOnJourney) {
          return false;
        }

        // Prüfe ob Begleiter aktiv ist
        const isActive = state.companions.active.some(c => c && c.id === companionId);
        if (isActive) {
          return false; // Aktive Begleiter können nicht auf Reise
        }

        const cost = durationHours * 100; // 100 Münzen pro Stunde
        if (!state.spendCoins(cost)) {
          return false;
        }

        set((state) => ({
          companions: {
            ...state.companions,
            discovered: state.companions.discovered.map(c => 
              c.id === companionId 
                ? { 
                    ...c, 
                    isOnJourney: true,
                    journeyType,
                    journeyStartTime: new Date().toISOString(),
                    journeyDuration: durationHours,
                    journeyArea: area,
                    journeyStartLevel: area ? state.gardenAreas[area].level : undefined
                  }
                : c
            )
          }
        }));

        return true;
      },

      completeCompanionJourney: (companionId) => {
        const state = get();
        const companion = state.companions.discovered.find(c => c.id === companionId);
        
        if (!companion || !companion.isOnJourney) {
          return;
        }

        const duration = companion.journeyDuration || 0;
        const companionLevel = state.getCompanionLevel(companion);
        const levelMultiplier = 1 + (companionLevel - 1) * 0.1;

        // Belohnungen basierend auf Reise-Typ
        switch (companion.journeyType) {
          case 'treasure':
            const coins = Math.floor(duration * 500 * levelMultiplier);
            const gems = Math.floor(duration * 10 * levelMultiplier);
            state.addCoins(coins);
            state.addGems(gems);
            break;
          
          case 'experience':
            const xp = Math.floor(duration * 50 * levelMultiplier);
            state.addExperience(xp);
            break;
          
          case 'renovation':
            if (companion.journeyArea) {
              // Automatische Renovierung des gewählten Bereichs
              const area = state.gardenAreas[companion.journeyArea];
              
              // Begleiter-Effizienz basierend auf Seltenheit
              const efficiency = companion.type === 'legendary' ? 2 : 
                               companion.type === 'epic' ? 1.5 : 
                               companion.type === 'rare' ? 1.2 : 1;
              
              const upgradesToDo = Math.min(
                Math.floor(duration * efficiency), 
                area.maxLevel - area.level
              );
              
              for (let i = 0; i < upgradesToDo; i++) {
                state.renovateArea(companion.journeyArea);
              }
              
              // Zusätzliche Belohnung für erfolgreiche Renovierung
              if (upgradesToDo > 0) {
                const bonusCoins = upgradesToDo * 100 * efficiency;
                const bonusXP = upgradesToDo * 25 * efficiency;
                state.addCoins(bonusCoins);
                state.addExperience(bonusXP);
              }
            }
            break;
        }

        // Begleiter erhält auch Erfahrung von der Reise
        const companionXP = duration * 10 * levelMultiplier;
        const currentNormalLevel = companion.level || 1;
        const currentNormalExperience = companion.experience || 0;
        const newNormalExperience = currentNormalExperience + companionXP;
        let newNormalLevel = currentNormalLevel;
        let finalNormalExperience = newNormalExperience;

        // Prüfe ob Level-Up möglich ist (kontinuierlich bis alle Level erreicht sind)
        let updatedAbilities = companion.abilities;
        while (finalNormalExperience >= (newNormalLevel * 100)) {
          finalNormalExperience = finalNormalExperience - (newNormalLevel * 100);
          newNormalLevel++;
          
          // Verbessere Standard-Fähigkeiten beim Level-Up
          updatedAbilities = updatedAbilities.map(ability => {
            const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
            if (isStandardAbility) {
              return {
                ...ability,
                value: Math.floor(ability.value * 1.1) // 10% Verbesserung
              };
            }
            return ability;
          });
        }

        set((state) => ({
          companions: {
            ...state.companions,
            discovered: state.companions.discovered.map(c => 
              c.id === companionId 
                ? { 
                    ...c, 
                    isOnJourney: false,
                    journeyType: undefined,
                    journeyStartTime: undefined,
                    journeyDuration: undefined,
                    journeyArea: undefined,
                    journeyStartLevel: undefined,
                    level: newNormalLevel,
                    experience: finalNormalExperience,
                    abilities: updatedAbilities
                  }
                : c
            )
          }
        }));
      },

      updateCompanionActivities: () => {
        const state = get();
        const now = new Date();

        state.companions.discovered.forEach(companion => {
          // Prüfe Training
          if (companion.isTraining && companion.trainingStartTime && companion.trainingDuration) {
            const startTime = new Date(companion.trainingStartTime);
            const endTime = new Date(startTime.getTime() + companion.trainingDuration * 60 * 1000);
            
            if (now >= endTime) {
              state.completeCompanionTraining(companion.id);
            }
          }

          // Prüfe Reisen und kontinuierliche Renovierung
          if (companion.isOnJourney && companion.journeyStartTime && companion.journeyDuration) {
            const startTime = new Date(companion.journeyStartTime);
            const endTime = new Date(startTime.getTime() + companion.journeyDuration * 60 * 60 * 1000);
            
            if (now >= endTime) {
              state.completeCompanionJourney(companion.id);
            } else if (companion.journeyType === 'renovation' && companion.journeyArea) {
              // Kontinuierliche Renovierung während der Reise
              const elapsedHours = (now.getTime() - startTime.getTime()) / (1000 * 60 * 60);
              const area = state.gardenAreas[companion.journeyArea];
              
              // Begleiter-Effizienz basierend auf Seltenheit
              const efficiency = companion.type === 'legendary' ? 2 : 
                               companion.type === 'epic' ? 1.5 : 
                               companion.type === 'rare' ? 1.2 : 1;
              
              // Berechne wie viele Level der Begleiter bis jetzt geschafft haben sollte
              const expectedLevels = Math.floor(elapsedHours * efficiency);
              const currentProgress = area.level - (companion.journeyStartLevel || area.level);
              
              // Wenn der Begleiter hinterherhinkt, hole Level nach
              if (expectedLevels > currentProgress && area.level < area.maxLevel) {
                const levelsToAdd = Math.min(
                  expectedLevels - currentProgress,
                  area.maxLevel - area.level
                );
                
                for (let i = 0; i < levelsToAdd; i++) {
                  state.renovateArea(companion.journeyArea);
                }
                
                // Belohnung für kontinuierliche Arbeit (reduziert)
                if (levelsToAdd > 0) {
                  const bonusCoins = levelsToAdd * 50 * efficiency;
                  const bonusXP = levelsToAdd * 10 * efficiency;
                  state.addCoins(bonusCoins);
                  state.addExperience(bonusXP);
                }
              }
            }
          }
        });
      },

      // Premium Auto-Klicker
      togglePremiumAutoClicker: () => {
        const state = get();
        
        if (!state.premiumAutoClicker.isActive) {
          // Auto-Klicker einschalten - prüfe ob genug Diamanten vorhanden
          const totalCostPerSecond = state.premiumAutoClicker.clicksPerSecond * 1000;
          if (state.gems >= totalCostPerSecond) {
            set((state) => ({
              premiumAutoClicker: {
                ...state.premiumAutoClicker,
                isActive: true,
                lastTickTime: Date.now(),
                costPerSecond: totalCostPerSecond
              }
            }));
          }
        } else {
          // Auto-Klicker ausschalten
          set((state) => ({
            premiumAutoClicker: {
              ...state.premiumAutoClicker,
              isActive: false
            }
          }));
        }
      },

      updatePremiumAutoClicker: () => {
        const state = get();
        
        if (!state.premiumAutoClicker.isActive) return;
        
        const now = Date.now();
        const timeDiff = now - state.premiumAutoClicker.lastTickTime;
        
        // Berechne wie viele Sekunden vergangen sind
        const secondsPassed = timeDiff / 1000;
        
        // Level-basierte Optimierung: Ab Level 1000 nur 2 Klicks pro Sekunde (kompakt)
        const isLevel1000Plus = state.playerLevel >= 1000;
        const effectiveClicksPerSecond = isLevel1000Plus ? 2 : state.premiumAutoClicker.clicksPerSecond;
        
        // Optimierte Update-Intervalle basierend auf Level
        const getMinUpdateTime = () => {
          if (isLevel1000Plus) return 1.0; // Ab Level 1000: 1 Sekunde
          if (effectiveClicksPerSecond >= 20) return 0.2;    // Schnell: 200ms
          if (effectiveClicksPerSecond >= 10) return 0.5;    // Mittel: 500ms
          return 1.0; // Langsam: 1s
        };
        
        const minUpdateTime = getMinUpdateTime();
        
        if (secondsPassed >= minUpdateTime) {
          // Level-basierte Kostenberechnung
          const baseCostPerClick = 1000;
          const speedDiscount = Math.min(0.5, effectiveClicksPerSecond / 25); // Bis zu 50% Rabatt
          
          // Ab Level 1000: 1% aller Diamanten pro Klick
          const costPerClick = isLevel1000Plus 
            ? Math.floor(state.gems * 0.01) // 1% aller Diamanten
            : Math.floor(baseCostPerClick * (1 - speedDiscount)); // Normale Kosten
          
          const totalCostPerSecond = effectiveClicksPerSecond * costPerClick;
          const diamondCost = Math.floor(secondsPassed * totalCostPerSecond);
          
          // Prüfe VORHER ob genug Diamanten da sind
          if (state.gems < diamondCost) {
            // Nicht genug Diamanten - schalte Auto-Klicker sofort aus
            set((state) => ({
              premiumAutoClicker: {
                ...state.premiumAutoClicker,
                isActive: false
              }
            }));
            return;
          }
          
          // Optimierte Klick-Berechnung - nur 2 Klicks bei hohen Levels (kompakt)
          const totalClicksToPerform = isLevel1000Plus 
            ? Math.floor(secondsPassed * 2) // Nur 2 Klicks pro Sekunde
            : Math.floor(secondsPassed * effectiveClicksPerSecond);
          
          // Führe Klicks aus (maximal 2 bei hohen Levels)
          if (totalClicksToPerform > 0) {
            const clicksToExecute = isLevel1000Plus ? 2 : Math.min(totalClicksToPerform, 100);
            
            for (let i = 0; i < clicksToExecute; i++) {
              state.incrementClick();
            }
          }
          
          // Bezahle die Diamanten
          state.spendGems(diamondCost);
          
          // Update last tick time
          set((state) => ({
            premiumAutoClicker: {
              ...state.premiumAutoClicker,
              lastTickTime: now
            }
          }));
          
          // Intelligente Sicherheitsprüfung nach dem Update
          const currentState = get();
          const nextSecondCost = effectiveClicksPerSecond * costPerClick;
          if (currentState.gems < nextSecondCost) {
            set((state) => ({
              premiumAutoClicker: {
                ...state.premiumAutoClicker,
                isActive: false
              }
            }));
          }
        }
      },

      setPremiumAutoClickerSpeed: (clicksPerSecond) => {
        set((state) => {
          // Level-basierte Geschwindigkeitsbegrenzung
          const isLevel1000Plus = state.playerLevel >= 1000;
          const maxSpeed = isLevel1000Plus ? 2 : 25; // Kompakt: nur 2 Klicks pro Sekunde
          const clampedSpeed = Math.max(1, Math.min(maxSpeed, clicksPerSecond));
          
          // Level-basierte Kostenberechnung
          const baseCostPerClick = 1000;
          const speedDiscount = Math.min(0.5, clampedSpeed / 25); // Bis zu 50% Rabatt
          
          // Ab Level 1000: 1% aller Diamanten pro Klick
          const costPerClick = isLevel1000Plus 
            ? Math.floor(state.gems * 0.01) // 1% aller Diamanten
            : Math.floor(baseCostPerClick * (1 - speedDiscount)); // Normale Kosten
          
          const totalCostPerSecond = clampedSpeed * costPerClick;
          
          // Wenn der Auto-Clicker aktiv ist, prüfe ob genug Diamanten für die neue Geschwindigkeit vorhanden sind
          if (state.premiumAutoClicker.isActive && state.gems < totalCostPerSecond) {
            // Nicht genug Diamanten - schalte Auto-Clicker aus
            return {
              premiumAutoClicker: {
                ...state.premiumAutoClicker,
                clicksPerSecond: clampedSpeed,
                costPerSecond: totalCostPerSecond,
                isActive: false
              }
            };
          }
          
          return {
            premiumAutoClicker: {
              ...state.premiumAutoClicker,
              clicksPerSecond: clampedSpeed,
              costPerSecond: totalCostPerSecond
            }
          };
        });
      },

      setGlobalMultiplier: (multiplier) => {
        set((state) => {
          const newMultipliers = { ...state.globalMultipliers };
          
          // Stapelt Multiplikatoren (multipliziert sie)
          if (newMultipliers.totalMultiplier) {
            newMultipliers.totalMultiplier *= multiplier;
          } else {
            newMultipliers.totalMultiplier = multiplier;
          }
          
          return {
            globalMultipliers: newMultipliers
          };
        });
      },

      // Opfer-System für göttliche Begleiter
      sacrificeCompanion: (companionId: string, multiplier: number = 2) => {
        set((state) => {
          // Finde den Begleiter
          const companion = state.companions.discovered.find(c => c.id === companionId);
          if (!companion) return state;
          
          // Prüfe ob es ein göttlicher Begleiter ist
          const isPremiumShop = companion.id.startsWith('premium_') && companion.abilities.length >= 3;
          const isUpgradedDivine = companion.id.startsWith('upgrade_') && 
                                   companion.name.startsWith('★') && 
                                   companion.name.endsWith('★') &&
                                   companion.abilities.length >= 4;
          
          // Prüfe ob es ein mystischer göttlicher legendärer Begleiter ist
          const isMysticDivine = companion.name.startsWith('★★★') && 
                                 companion.name.endsWith('★★★') &&
                                 companion.abilities.length >= 4;
          
          // Prüfe ob es ein göttlich legendärer Begleiter ist (★ Name ★)
          const isGodlyLegendary = companion.name.startsWith('★') && 
                                   companion.name.endsWith('★') &&
                                   companion.name !== companion.name.replace(/★/g, '') && // Enthält Sterne
                                   companion.abilities.length >= 4;
          
          // Nur göttliche Begleiter können geopfert werden
          if (!isPremiumShop && !isUpgradedDivine && !isMysticDivine && !isGodlyLegendary) {
            console.log('Sacrifice failed - not a divine companion:', {
              companionId,
              companionName: companion.name,
              companionType: companion.type,
              isPremiumShop,
              isUpgradedDivine,
              isMysticDivine,
              isGodlyLegendary,
              startsWithPremium: companion.id.startsWith('premium_'),
              startsWithUpgrade: companion.id.startsWith('upgrade_'),
              hasStars: companion.name.startsWith('★') && companion.name.endsWith('★'),
              hasMysticStars: companion.name.includes('★★★'),
              abilitiesLength: companion.abilities.length
            });
            return state;
          }
          
          console.log('Sacrificing companion:', {
            companionId,
            companionName: companion.name,
            isPremiumShop,
            isUpgradedDivine,
            isMysticDivine,
            isGodlyLegendary
          });
          
          // Entferne Begleiter aus der Collection
          const newDiscovered = state.companions.discovered.filter(c => c.id !== companionId);
          
          // Entferne Begleiter aus aktiven Slots falls vorhanden
          const newActive = state.companions.active.filter(c => c && c.id !== companionId);
          
          // Erhöhe globalen Multiplikator um den angegebenen Multiplikator
          const newMultipliers = { ...state.globalMultipliers };
          newMultipliers.totalMultiplier = (newMultipliers.totalMultiplier || 1) * multiplier;
          newMultipliers.sacrificeCount = (newMultipliers.sacrificeCount || 0) + 1;
          
          return {
            companions: {
              ...state.companions,
              discovered: newDiscovered,
              active: newActive
            },
            globalMultipliers: newMultipliers
          };
        });
      },

      // Spiel komplett zurücksetzen
      resetGame: () => {
        // Lösche den gespeicherten Zustand
        localStorage.removeItem('garden-clicker-storage');
        // Lade die Seite neu, um den initialen Zustand zu laden
        window.location.reload();
      },

      // Arena-Kampf-System
      updateCompanionBattleStats: (companionId: string, battleLevel: number, battleExperience: number) => {
        set((state) => {
          const companion = state.companions.discovered.find(c => c.id === companionId);
          if (!companion) return state;

          const currentBattleLevel = companion.battleLevel || 1;
          const newBattleLevel = battleLevel;
          
          // Verbessere Kampf-Fähigkeiten beim Battle Level-Up
          let updatedAbilities = companion.abilities;
          if (newBattleLevel > currentBattleLevel) {
            updatedAbilities = companion.abilities.map(ability => {
              // Nur Kampf-Fähigkeiten verbessern
              const isBattleAbility = ['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
              if (isBattleAbility) {
                return {
                  ...ability,
                  value: Math.floor(ability.value * 1.15) // 15% Verbesserung pro Battle Level
                };
              }
              return ability;
            });
          }

          const updatedCompanion = { 
            ...companion, 
            battleLevel, 
            battleExperience,
            abilities: updatedAbilities
          };

          const updatedDiscovered = state.companions.discovered.map(c => 
            c.id === companionId ? updatedCompanion : c
          );
          
          const updatedActive = state.companions.active.map(c => 
            c && c.id === companionId ? updatedCompanion : c
          );
          
          return {
            companions: {
              ...state.companions,
              discovered: updatedDiscovered,
              active: updatedActive
            }
          };
        });
      },

      updateCompanionNormalStats: (companionId: string, level: number, experience: number) => {
        set((state) => {
          const companion = state.companions.discovered.find(c => c.id === companionId);
          if (!companion) return state;

          const currentLevel = companion.level || 1;
          const newLevel = level;
          
          // Verbessere Fähigkeiten beim Level-Up (nur Standard-Fähigkeiten, nicht Kampf-Fähigkeiten)
          let updatedAbilities = companion.abilities;
          if (newLevel > currentLevel) {
            updatedAbilities = companion.abilities.map(ability => {
              // Nur Standard-Fähigkeiten verbessern, nicht Kampf-Fähigkeiten
              const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
              if (isStandardAbility) {
                return {
                  ...ability,
                  value: Math.floor(ability.value * 1.1) // 10% Verbesserung pro Level
                };
              }
              return ability;
            });
          }

          const updatedCompanion = { 
            ...companion, 
            level, 
            experience,
            abilities: updatedAbilities
          };

          const updatedDiscovered = state.companions.discovered.map(c => 
            c.id === companionId ? updatedCompanion : c
          );
          
          const updatedActive = state.companions.active.map(c => 
            c && c.id === companionId ? updatedCompanion : c
          );
          
          return {
            companions: {
              ...state.companions,
              discovered: updatedDiscovered,
              active: updatedActive
            }
          };
        });
      },

      addCompanionAbility: (companionId: string, ability: CompanionAbility) => {
        set((state) => {
          const updatedDiscovered = state.companions.discovered.map(companion => 
            companion.id === companionId 
              ? { ...companion, abilities: [...companion.abilities, ability] }
              : companion
          );
          
          const updatedActive = state.companions.active.map(companion => 
            companion && companion.id === companionId 
              ? { ...companion, abilities: [...companion.abilities, ability] }
              : companion
          );
          
          return {
            companions: {
              ...state.companions,
              discovered: updatedDiscovered,
              active: updatedActive
            }
          };
        });
      },

      // Musik ein/ausschalten
      toggleMusic: () => {
        set((state) => ({
          settings: {
            ...state.settings,
            musicEnabled: !state.settings.musicEnabled
          }
        }));
      },
      
      // Spieler-Aktivität aktualisieren
      updatePlayerActivity: () => {
        set((state) => ({
          lastActivityTime: Date.now(),
        }));
      },

      // Offline-Einkommen zurücksetzen
      clearOfflineIncome: () => {
        set((state) => ({
          offlineIncome: 0,
          offlineGems: 0,
          offlineCompanion: null,
          offlineEndTime: Date.now(), // Aktualisiere Endzeitpunkt beim Zurücksetzen
        }));
      },
      
      // Video-Werbung System
      startVideoReward: () => {
        const state = get();
        if (!state.canWatchVideo()) {
          return false;
        }
        
        set((state) => ({
          videoRewards: {
            ...state.videoRewards,
            isActive: true,
          }
        }));
        
        return true;
      },
      
      completeVideoReward: () => {
        const state = get();
        const now = new Date();
        const newAccumulatedTime = Math.min(
          state.videoRewards.accumulatedTime + 10, // 10 Minuten pro Video
          120 // Maximum 2 Stunden
        );
        
        set((state) => ({
          videoRewards: {
            ...state.videoRewards,
            isActive: false,
            accumulatedTime: newAccumulatedTime,
            lastVideoTime: now.toISOString(),
          }
        }));
      },
      
      // Aktualisiere angesammelte Zeit basierend auf verstrichener Zeit
      updateVideoRewardTime: () => {
        const state = get();
        if (state.videoRewards.accumulatedTime > 0) {
          const now = new Date();
          const lastVideo = state.videoRewards.lastVideoTime ? new Date(state.videoRewards.lastVideoTime) : now;
          const minutesSinceLastVideo = (now.getTime() - lastVideo.getTime()) / (1000 * 60);
          
          // Reduziere angesammelte Zeit um verstrichene Minuten
          const newAccumulatedTime = Math.max(0, state.videoRewards.accumulatedTime - minutesSinceLastVideo);
          
          if (newAccumulatedTime !== state.videoRewards.accumulatedTime) {
            set((state) => ({
              videoRewards: {
                ...state.videoRewards,
                accumulatedTime: newAccumulatedTime,
                lastVideoTime: now.toISOString(),
              }
            }));
          }
        }
      },
      
      canWatchVideo: () => {
        const state = get();
        if (state.videoRewards.isActive) {
          return false;
        }
        
        // Kann nicht mehr ansehen wenn Maximum erreicht
        if (state.videoRewards.accumulatedTime >= 120) {
          return false;
        }
        
        // Cooldown nur aktiv wenn Maximum erreicht ist
        if (state.videoRewards.accumulatedTime >= 120 && state.videoRewards.lastVideoTime) {
          const lastVideo = new Date(state.videoRewards.lastVideoTime);
          const now = new Date();
          const minutesSinceLastVideo = (now.getTime() - lastVideo.getTime()) / (1000 * 60);
          
          if (minutesSinceLastVideo < state.videoRewards.videoCooldown) {
            return false;
          }
        }
        
        return true;
      },
      
      getVideoRewardTimeRemaining: () => {
        const state = get();
        
        // Kein Cooldown wenn Maximum noch nicht erreicht
        if (state.videoRewards.accumulatedTime < 120) {
          return 0;
        }
        
        if (!state.videoRewards.lastVideoTime) {
          return 0;
        }
        
        const lastVideo = new Date(state.videoRewards.lastVideoTime);
        const now = new Date();
        const minutesSinceLastVideo = (now.getTime() - lastVideo.getTime()) / (1000 * 60);
        const remaining = Math.max(0, state.videoRewards.videoCooldown - minutesSinceLastVideo);
        
        return Math.ceil(remaining);
      },
      
      getAccumulatedVideoTime: () => {
        const state = get();
        return state.videoRewards.accumulatedTime;
      },
      
      getMaxVideoTime: () => {
        return 120; // 2 Stunden Maximum
      },

      // Leaderboard System
      submitToLeaderboard: async (username: string) => {
        const state = get();
        
        try {
          // Importiere API-Funktionen dynamisch
          const { apiRequest } = await import('../config/api');
          
          const response = await apiRequest('/leaderboard/submit', {
            method: 'POST',
            body: JSON.stringify({
              username,
              coins: state.coins,
              gems: state.gems,
              level: state.playerLevel,
              prestige_level: state.prestige.level,
              arena_level: state.companions.arena.level
            })
          });

          const data = await response.json();
          return data.success;
        } catch (error) {
          console.error('Leaderboard Submit Fehler:', error);
          return false;
        }
      },

      getLeaderboardRank: async (username: string, sortBy: string = 'level') => {
        try {
          // Importiere API-Funktionen dynamisch
          const { apiRequest } = await import('../config/api');
          
          const response = await apiRequest(`/leaderboard/rank/${username}?sort=${sortBy}`);
          const data = await response.json();
          
          if (data.success) {
            return data.rank;
          }
          return null;
        } catch (error) {
          console.error('Leaderboard Rank Fehler:', error);
          return null;
        }
      },
      
      // Prozedurale Werkzeug-Generierung
      generateProceduralTool: () => {
        const toolTypes = [
          { 
            prefix: 'Mystischer', 
            suffix: 'Zauberer', 
            effect: 'Mystische Kräfte verstärken alle Gartenbereiche',
            baseEffect: 0.2, // +20% Gartenbereich-Bonus pro Level
            effectType: 'gardenBonus'
          },
          { 
            prefix: 'Kosmischer', 
            suffix: 'Strahler', 
            effect: 'Kosmische Energie beschleunigt alle Prozesse',
            baseEffect: 0.15, // +15% Geschwindigkeit pro Level
            effectType: 'speedMultiplier'
          },
          { 
            prefix: 'Elementarer', 
            suffix: 'Manipulator', 
            effect: 'Elementare Kräfte verstärken alle Upgrades',
            baseEffect: 0.25, // +25% Upgrade-Effektivität pro Level
            effectType: 'upgradeMultiplier'
          },
          { 
            prefix: 'Temporaler', 
            suffix: 'Beschleuniger', 
            effect: 'Zeitmanipulation erhöht alle Einkommen',
            baseEffect: 0.3, // +30% Einkommen pro Level
            effectType: 'incomeMultiplier'
          },
          { 
            prefix: 'Quanten', 
            suffix: 'Stabilisator', 
            effect: 'Quanteneffekte verstärken alle Multiplikatoren',
            baseEffect: 0.1, // +10% alle Multiplikatoren pro Level
            effectType: 'globalMultiplier'
          },
          { 
            prefix: 'Dimensionaler', 
            suffix: 'Portal', 
            effect: 'Dimensionsportale erzeugen zusätzliche Ressourcen',
            baseEffect: 0.5, // +0.5 Münzen/Sek pro Level
            effectType: 'coinPerSecond'
          },
          { 
            prefix: 'Astraler', 
            suffix: 'Kanalisator', 
            effect: 'Astralenergie verstärkt alle Begleiter-Boni',
            baseEffect: 0.2, // +20% Begleiter-Bonus pro Level
            effectType: 'companionBonus'
          },
          { 
            prefix: 'Etherealer', 
            suffix: 'Verdichter', 
            effect: 'Ethereale Kräfte komprimieren alle Kosten',
            baseEffect: 0.05, // -5% Kosten pro Level
            effectType: 'costReduction'
          },
          { 
            prefix: 'Kryonischer', 
            suffix: 'Konservator', 
            effect: 'Kryonische Technologie bewahrt alle Boni',
            baseEffect: 0.1, // +10% Bonus-Dauer pro Level
            effectType: 'bonusDuration'
          },
          { 
            prefix: 'Plasmischer', 
            suffix: 'Reaktor', 
            effect: 'Plasmische Energie erzeugt explosive Boni',
            baseEffect: 0.4, // +40% kritische Chance pro Level
            effectType: 'criticalChance'
          },
          { 
            prefix: 'Gravitonischer', 
            suffix: 'Kompressor', 
            effect: 'Gravitonische Felder verstärken alle Effekte',
            baseEffect: 0.15, // +15% alle Effekte pro Level
            effectType: 'effectMultiplier'
          },
          { 
            prefix: 'Neutronischer', 
            suffix: 'Katalysator', 
            effect: 'Neutronische Strahlung beschleunigt alle Prozesse',
            baseEffect: 0.2, // +20% Prozess-Geschwindigkeit pro Level
            effectType: 'processSpeed'
          },
          { 
            prefix: 'Photonischer', 
            suffix: 'Amplifikator', 
            effect: 'Photonische Wellen verstärken alle Multiplikatoren',
            baseEffect: 0.1, // +10% Multiplikator-Stärke pro Level
            effectType: 'multiplierStrength'
          },
          { 
            prefix: 'Magnetischer', 
            suffix: 'Induktor', 
            effect: 'Magnetische Felder ziehen zusätzliche Ressourcen an',
            baseEffect: 0.3, // +30% Ressourcen-Anziehung pro Level
            effectType: 'resourceAttraction'
          },
          { 
            prefix: 'Thermischer', 
            suffix: 'Regulator', 
            effect: 'Thermische Energie optimiert alle Systeme',
            baseEffect: 0.25, // +25% System-Optimierung pro Level
            effectType: 'systemOptimization'
          },
          { 
            prefix: 'Akustischer', 
            suffix: 'Resonator', 
            effect: 'Akustische Wellen synchronisieren alle Boni',
            baseEffect: 0.2, // +20% Bonus-Synchronisation pro Level
            effectType: 'bonusSync'
          },
          { 
            prefix: 'Optischer', 
            suffix: 'Prisma', 
            effect: 'Optische Effekte vervielfachen alle Einkommen',
            baseEffect: 0.35, // +35% Einkommen-Vervielfachung pro Level
            effectType: 'incomeMultiplier'
          },
          { 
            prefix: 'Chemischer', 
            suffix: 'Katalysator', 
            effect: 'Chemische Reaktionen verstärken alle Upgrades',
            baseEffect: 0.3, // +30% Upgrade-Verstärkung pro Level
            effectType: 'upgradeMultiplier'
          },
          { 
            prefix: 'Biologischer', 
            suffix: 'Mutator', 
            effect: 'Biologische Prozesse beschleunigen alle Wachstum',
            baseEffect: 0.4, // +40% Wachstums-Beschleunigung pro Level
            effectType: 'growthSpeed'
          },
          { 
            prefix: 'Mechanischer', 
            suffix: 'Optimierer', 
            effect: 'Mechanische Präzision maximiert alle Effizienz',
            baseEffect: 0.2, // +20% Effizienz pro Level
            effectType: 'efficiency'
          },
        ];
        
        const randomType = toolTypes[Math.floor(Math.random() * toolTypes.length)];
        const prestigeLevel = get().prestige.level;
        const baseCost = Math.floor(1000 * Math.pow(1.5, prestigeLevel));
        
        return {
          key: `procedural_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: `${randomType.prefix} ${randomType.suffix}`,
          description: `Ein prozedural generiertes Werkzeug mit einzigartigen Eigenschaften.`,
          baseCost: baseCost,
          effect: randomType.effect,
          baseEffect: randomType.baseEffect,
          effectType: randomType.effectType,
        };
      },
      
      addProceduralTool: (tool) => {
        set((state) => ({
          upgrades: {
            ...state.upgrades,
            [tool.key]: 0, // Startet bei Level 0
          },
          proceduralTools: {
            ...state.proceduralTools,
            [tool.key]: {
              name: tool.name,
              description: tool.description,
              baseCost: tool.baseCost,
              effect: tool.effect,
              discoveredAt: new Date().toISOString(),
              baseEffect: tool.baseEffect,
              effectType: tool.effectType,
            },
          },
        }));
      },

      // Prozedurale Klick-Werkzeug-Generierung
      generateProceduralClickTool: () => {
        const toolTypes = [
          { 
            prefix: 'Blitzschneller', 
            suffix: 'Klicker', 
            effect: 'Erhöht die Klick-Multiplikation enorm',
            baseEffect: 0.5, // +50% pro Level
            effectType: 'clickMultiplier'
          },
          { 
            prefix: 'Kosmischer', 
            suffix: 'Finger', 
            effect: 'Kosmische Klicks bringen Bonusmünzen',
            baseEffect: 2, // +2 Münzen pro Klick pro Level
            effectType: 'clickBonus'
          },
          { 
            prefix: 'Elementarer', 
            suffix: 'Tipp', 
            effect: 'Elementare Klicks erhöhen den Multiplikator',
            baseEffect: 0.3, // +30% pro Level
            effectType: 'clickMultiplier'
          },
          { 
            prefix: 'Temporaler', 
            suffix: 'Drücker', 
            effect: 'Zeitmanipulation für mehr Klicks pro Sekunde',
            baseEffect: 0.1, // +10% Auto-Click-Power pro Level
            effectType: 'autoClickPower'
          },
          { 
            prefix: 'Quanten', 
            suffix: 'Impuls', 
            effect: 'Quanten-Klicks wirken mehrfach',
            baseEffect: 0.2, // +20% Chance auf doppelte Klicks pro Level
            effectType: 'doubleClickChance'
          },
          { 
            prefix: 'Photonischer', 
            suffix: 'Touch', 
            effect: 'Photonen-Klicks geben Extra-Belohnungen',
            baseEffect: 0.15, // +15% Edelstein-Chance pro Level
            effectType: 'gemChance'
          },
          { 
            prefix: 'Magnetischer', 
            suffix: 'Tapper', 
            effect: 'Magnetische Klicks ziehen Münzen an',
            baseEffect: 1, // +1 Münze pro Klick pro Level
            effectType: 'clickBonus'
          },
          { 
            prefix: 'Thermischer', 
            suffix: 'Klopfer', 
            effect: 'Hitze-Klicks erhöhen die Klick-Power',
            baseEffect: 0.4, // +40% Klick-Power pro Level
            effectType: 'clickPower'
          },
          { 
            prefix: 'Akustischer', 
            suffix: 'Taktgeber', 
            effect: 'Klicks im Rhythmus geben Bonus',
            baseEffect: 0.25, // +25% XP pro Klick pro Level
            effectType: 'xpBonus'
          },
          { 
            prefix: 'Optischer', 
            suffix: 'Reflektor', 
            effect: 'Optische Klicks vervielfachen den Ertrag',
            baseEffect: 0.35, // +35% Gesamt-Multiplikator pro Level
            effectType: 'totalMultiplier'
          },
        ];
        
        const randomType = toolTypes[Math.floor(Math.random() * toolTypes.length)];
        const prestigeLevel = get().prestige.level;
        const baseCost = Math.floor(1500 * Math.pow(1.6, prestigeLevel));
        
        return {
          key: `proceduralClick_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: `${randomType.prefix} ${randomType.suffix}`,
          description: `Ein prozedural generiertes Klick-Werkzeug mit einzigartigen Eigenschaften.`,
          baseCost: baseCost,
          effect: randomType.effect,
          baseEffect: randomType.baseEffect,
          effectType: randomType.effectType,
        };
      },
      
      addProceduralClickTool: (tool) => {
        set((state) => ({
          upgrades: {
            ...state.upgrades,
            [tool.key]: 0, // Startet bei Level 0
          },
          proceduralClickTools: {
            ...state.proceduralClickTools,
            [tool.key]: {
              name: tool.name,
              description: tool.description,
              baseCost: tool.baseCost,
              effect: tool.effect,
              discoveredAt: new Date().toISOString(),
              baseEffect: tool.baseEffect,
              effectType: tool.effectType,
            },
          },
        }));
      },

      // Arena-System Funktionen
      getArenaLevel: () => {
        const state = get();
        return state.companions.arena?.level || 1;
      },

      setArenaLevel: (level: number) => {
        set((state) => ({
          companions: {
            ...state.companions,
            arena: {
              ...state.companions.arena,
              level: level,
            },
          },
        }));
      },

      getArenaDifficultyMultiplier: () => {
        const state = get();
        return state.companions.arena?.difficultyMultiplier || 1.0;
      },

      getArenaResetCount: () => {
        const state = get();
        return state.companions.arena?.resetCount || 0;
      },

      canResetArena: () => {
        const state = get();
        return state.companions.arena?.level >= 50;
      },

      resetArena: (selectedCompanionIds?: string[]) => {
        const state = get();
        if (!state.canResetArena()) return;

        const currentMultiplier = state.companions.arena?.difficultyMultiplier || 1.0;
        const currentResetCount = state.companions.arena?.resetCount || 0;
        
        // Erhöhe Schwierigkeit um das 10-fache pro Reset
        const newMultiplier = currentMultiplier * 10.0;
        const newResetCount = currentResetCount + 1;

        console.log('🔄 Arena Reset - Ausgewählte Begleiter:', selectedCompanionIds);

        // Level nur die ausgewählten Begleiter um 25 Level auf (alle Stats)
        const updatedDiscovered = state.companions.discovered.map(companion => {
          // Prüfe ob der Begleiter im ausgewählten Team ist
          if (selectedCompanionIds && selectedCompanionIds.includes(companion.id)) {
            const currentBattleLevel = companion.battleLevel || 1;
            const currentLevel = companion.level || 1;
            const newBattleLevel = currentBattleLevel + 25;
            const newLevel = currentLevel + 25;
            
            // Prüfe ob es ein mystisch göttlich legendärer Begleiter ist (★★★)
            const isMysticDivine = companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
            
            console.log(`📈 ${companion.name}: Battle Level ${currentBattleLevel} → ${newBattleLevel}, Normal Level ${currentLevel} → ${newLevel}${isMysticDivine ? ' (Mystisch Göttlich)' : ''}`);
            
            return {
              ...companion,
              battleLevel: newBattleLevel,
              battleExperience: 0, // Reset Battle-Erfahrung
              level: newLevel,
              experience: 0, // Reset normale Erfahrung
            };
          }
          // Begleiter nicht im Team - keine Änderung
          return companion;
        });

        const updatedActive = state.companions.active.map(companion => {
          if (!companion) return companion;
          // Prüfe ob der Begleiter im ausgewählten Team ist
          if (selectedCompanionIds && selectedCompanionIds.includes(companion.id)) {
            const currentBattleLevel = companion.battleLevel || 1;
            const currentLevel = companion.level || 1;
            const newBattleLevel = currentBattleLevel + 25;
            const newLevel = currentLevel + 25;
            
            // Prüfe ob es ein mystisch göttlich legendärer Begleiter ist (★★★)
            const isMysticDivine = companion.name.startsWith('★★★') && companion.name.endsWith('★★★');
            
            console.log(`📈 ${companion.name} (Active): Battle Level ${currentBattleLevel} → ${newBattleLevel}, Normal Level ${currentLevel} → ${newLevel}${isMysticDivine ? ' (Mystisch Göttlich)' : ''}`);
            
            return {
              ...companion,
              battleLevel: newBattleLevel,
              battleExperience: 0, // Reset Battle-Erfahrung
              level: newLevel,
              experience: 0, // Reset normale Erfahrung
            };
          }
          // Begleiter nicht im Team - keine Änderung
          return companion;
        });

        console.log('💾 Arena Reset - Speichere Änderungen...');

        set((state) => ({
          companions: {
            ...state.companions,
            discovered: updatedDiscovered,
            active: updatedActive,
            arena: {
              ...state.companions.arena,
              level: 1, // Zurück zu Level 1
              difficultyMultiplier: newMultiplier,
              resetCount: newResetCount,
            },
          },
        }));

        console.log('✅ Arena Reset - Änderungen gespeichert');
      },

      // Training-System Funktionen
      getMaxTrainingCapacity: () => {
        const state = get();
        // Ab Level 1000: 30 Begleiter gleichzeitig trainieren können
        return state.playerLevel >= 1000 ? 30 : 10;
      },

      addToTraining: (companionId: string) => {
        const state = get();
        const maxCapacity = state.getMaxTrainingCapacity();
        if (state.companions.training.length < maxCapacity && !state.companions.training.includes(companionId)) {
          set((state) => ({
            companions: {
              ...state.companions,
              training: [...state.companions.training, companionId]
            }
          }));
        }
      },

      removeFromTraining: (companionId: string) => {
        set((state) => ({
          companions: {
            ...state.companions,
            training: state.companions.training.filter(id => id !== companionId),
            discovered: state.companions.discovered.map(c =>
              c.id === companionId
                ? {
                    ...c,
                    isTraining: false,
                    trainingStartTime: undefined,
                    trainingDuration: undefined
                  }
                : c
            )
          }
        }));
      },

      getTrainingCost: () => {
        const state = get();
        let totalCost = 0;
        
        state.companions.training.forEach(companionId => {
          const companion = state.companions.discovered.find(c => c.id === companionId);
          if (companion) {
            // Basis-Kosten: 1000 Münzen pro Sekunde
            const baseCost = 1000;
            // Level-Skalierung: Jedes Level macht es 50% teurer (vorher 20%)
            const levelMultiplier = 1 + (companion.battleLevel || 1) * 0.5;
            const companionCost = Math.floor(baseCost * levelMultiplier);
            totalCost += companionCost;
          }
        });
        
        return totalCost;
      },

      canAffordTraining: () => {
        const state = get();
        return state.coins >= state.getTrainingCost();
      },

      updateTraining: () => {
        const state = get();
        if (state.companions.training.length === 0 || !state.canAffordTraining()) return;

        // Kosten abziehen
        state.addCoins(-state.getTrainingCost());
        
        // Forschung aktualisieren
        state.updateResearch();
        
        // Schmiede-System aktualisieren
        state.updateMining();
        state.updateArmorCrafting();

        // Optimierte Begleiter-Training mit Memory-Optimierung
        const trainingCompanions = state.companions.training;
        const discoveredCompanions = state.companions.discovered;
        
        for (let i = 0; i < trainingCompanions.length; i++) {
          const companionId = trainingCompanions[i];
          const companion = discoveredCompanions.find(c => c.id === companionId);
          if (!companion) continue;

          // Battle Level Training
          const currentBattleLevel = companion.battleLevel || 1;
          const currentBattleExp = companion.battleExperience || 0;
          const battleExpGain = 5; // Reduziert von 50 auf 5 (10x langsamer)
          const newBattleExp = currentBattleExp + battleExpGain;
          const requiredBattleExp = currentBattleLevel * 100;

          let newBattleLevel = currentBattleLevel;
          let finalBattleExp = newBattleExp;

          if (newBattleExp >= requiredBattleExp) {
            newBattleLevel++;
            finalBattleExp = newBattleExp - requiredBattleExp;

            if (Math.random() < 0.1) {
              const battleAbilities = ['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'];
              const existingTypes = companion.abilities.map(a => a.type);
              const availableAbilities = battleAbilities.filter(type => !existingTypes.includes(type as any));
              
              if (availableAbilities.length > 0) {
                const newAbilityType = availableAbilities[Math.floor(Math.random() * availableAbilities.length)];
                let value = Math.floor(Math.random() * 10) + 5;
                
                const newAbility = {
                  type: newAbilityType as any,
                  value,
                  description: `Training-Fähigkeit: ${newAbilityType}`
                };
                
                state.addCompanionAbility(companionId, newAbility);
              }
            }
          }

          // Normales Level Training
          const currentNormalLevel = companion.level || 1;
          const currentNormalExp = companion.experience || 0;
          const normalExpGain = 3; // Reduziert von 30 auf 3 (10x langsamer)
          const newNormalExp = currentNormalExp + normalExpGain;
          const requiredNormalExp = currentNormalLevel * 100;

          let newNormalLevel = currentNormalLevel;
          let finalNormalExp = newNormalExp;

          if (newNormalExp >= requiredNormalExp) {
            newNormalLevel++;
            finalNormalExp = newNormalExp - requiredNormalExp;
          }

          // Update beide Level-Systeme
          state.updateCompanionBattleStats(companionId, newBattleLevel, finalBattleExp);
          state.updateCompanionNormalStats(companionId, newNormalLevel, finalNormalExp);
        }
      },

      // Forschungs-System
      startResearch: (projectId: string) => {
        const state = get();
        const project = state.companions.research.projects[projectId];
        
        if (!project || project.isActive || project.completed) {
          return false;
        }

        // Berechne aktuellen Preis mit Multiplikator
        const completedProjects = Object.values(state.companions.research.projects).filter(p => p.completed).length;
        const priceMultiplier = 1 + (completedProjects * 0.5);
        const currentCost = Math.floor(project.cost * priceMultiplier);

        if (!state.spendCoins(currentCost)) {
          return false;
        }

        set((state) => ({
          companions: {
            ...state.companions,
            research: {
              ...state.companions.research,
              projects: {
                ...state.companions.research.projects,
                [projectId]: {
                  ...project,
                  isActive: true,
                  startTime: new Date().toISOString()
                }
              }
            }
          }
        }));

        return true;
      },

      completeResearch: (projectId: string) => {
        const state = get();
        const project = state.companions.research.projects[projectId];
        
        if (!project || !project.isActive || project.completed) {
          return;
        }

        // Füge den Bonus zu den globalen Boni hinzu
        const globalBonuses = state.companions.research.globalBonuses;
        const newGlobalBonuses = {
          ...globalBonuses,
          [project.bonusType]: globalBonuses[project.bonusType] + project.bonusValue
        };

        set((state) => ({
          companions: {
            ...state.companions,
            research: {
              ...state.companions.research,
              projects: {
                ...state.companions.research.projects,
                [projectId]: {
                  ...project,
                  isActive: false,
                  completed: true
                }
              },
              globalBonuses: newGlobalBonuses
            }
          }
        }));
      },

      updateResearch: () => {
        const state = get();
        const now = new Date();

        Object.keys(state.companions.research.projects).forEach(projectId => {
          const project = state.companions.research.projects[projectId];
          
          if (project.isActive && project.startTime && project.duration) {
            const startTime = new Date(project.startTime);
            const endTime = new Date(startTime.getTime() + project.duration * 60 * 1000);
            
            if (now >= endTime) {
              state.completeResearch(projectId);
            }
          }
        });
      },

      getResearchProjects: () => {
        const state = get();
        const projects = Object.values(state.companions.research.projects);
        
        // Berechne Preis-Multiplikator basierend auf abgeschlossenen Projekten
        const completedProjects = projects.filter(p => p.completed).length;
        const priceMultiplier = 1 + (completedProjects * 0.5); // 50% Preiserhöhung pro abgeschlossenem Projekt
        
        return projects.map(project => ({
          ...project,
          cost: Math.floor(project.cost * priceMultiplier)
        }));
      },

      getGlobalBonuses: () => {
        const state = get();
        // Kombiniere aktuelle Boni mit permanenten Boni aus Resets
        const currentBonuses = state.companions.research.globalBonuses;
        const permanentBonuses = state.companions.research.skillTreeReset.permanentBonuses;
        
        return {
          attack: currentBonuses.attack + permanentBonuses.attack,
          defense: currentBonuses.defense + permanentBonuses.defense,
          hp: currentBonuses.hp + permanentBonuses.hp,
          experience: currentBonuses.experience + permanentBonuses.experience,
          abilityChance: currentBonuses.abilityChance + permanentBonuses.abilityChance,
        };
      },

      // Skill-Tree-Reset-Funktionen
      canResetSkillTree: () => {
        const state = get();
        const projects = Object.values(state.companions.research.projects);
        return projects.every(project => project.completed);
      },

      resetSkillTree: () => {
        const state = get();
        
        if (!state.canResetSkillTree()) {
          return false;
        }

        // Sammle alle aktuellen Boni
        const currentBonuses = state.companions.research.globalBonuses;
        
        // Füge sie zu den permanenten Boni hinzu (100% Bonus)
        const newPermanentBonuses = {
          attack: state.companions.research.skillTreeReset.permanentBonuses.attack + (currentBonuses.attack * 2),
          defense: state.companions.research.skillTreeReset.permanentBonuses.defense + (currentBonuses.defense * 2),
          hp: state.companions.research.skillTreeReset.permanentBonuses.hp + (currentBonuses.hp * 2),
          experience: state.companions.research.skillTreeReset.permanentBonuses.experience + (currentBonuses.experience * 2),
          abilityChance: state.companions.research.skillTreeReset.permanentBonuses.abilityChance + (currentBonuses.abilityChance * 2),
        };

        // Reset alle Projekte
        const resetProjects: { [key: string]: any } = {};
        Object.keys(state.companions.research.projects).forEach(projectId => {
          const project = state.companions.research.projects[projectId];
          resetProjects[projectId] = {
            ...project,
            isActive: false,
            completed: false,
            startTime: undefined,
          };
        });

        set((state) => ({
          companions: {
            ...state.companions,
            research: {
              ...state.companions.research,
              projects: resetProjects,
              globalBonuses: {
                attack: 0,
                defense: 0,
                hp: 0,
                experience: 0,
                abilityChance: 0,
              },
              skillTreeReset: {
                resetCount: state.companions.research.skillTreeReset.resetCount + 1,
                permanentBonuses: newPermanentBonuses,
                lastResetDate: new Date().toISOString(),
              },
            },
          },
        }));

        return true;
      },

      getSkillTreeResetInfo: () => {
        const state = get();
        return {
          resetCount: state.companions.research.skillTreeReset.resetCount,
          permanentBonuses: state.companions.research.skillTreeReset.permanentBonuses,
          lastResetDate: state.companions.research.skillTreeReset.lastResetDate,
          canReset: state.canResetSkillTree(),
        };
      },

      // Schmiede-System
      startMining: (companionId: string, oreType: 'iron' | 'steel' | 'mithril' | 'adamantium', duration: number) => {
        const state = get();
        const companion = state.companions.discovered.find(c => c.id === companionId);
        
        if (!companion || state.isCompanionMining(companionId)) {
          return false;
        }

        // Progressive Erzabbau-Anforderungen
        const oreRequirements = {
          iron: 0, // Eisen kann immer abgebaut werden
          steel: 10000, // 10.000 Eisen für Stahl
          mithril: 50000, // 50.000 Stahl für Mithril
          adamantium: 100000, // 100.000 Mithril für Adamantium
        };

        // Prüfe Anforderungen für höhere Erzsorten (berücksichtigt auch kontinuierliches Mining)
        const virtualOreCounts = state.getOreCounts();
        if (oreType === 'steel' && virtualOreCounts.iron < oreRequirements.steel) {
          return false;
        }
        if (oreType === 'mithril' && virtualOreCounts.steel < oreRequirements.mithril) {
          return false;
        }
        if (oreType === 'adamantium' && virtualOreCounts.mithril < oreRequirements.adamantium) {
          return false;
        }

        set((state) => ({
          companions: {
            ...state.companions,
            forge: {
              ...state.companions.forge,
              mining: {
                ...state.companions.forge.mining,
                activeMiners: {
                  ...state.companions.forge.mining.activeMiners,
                  [companionId]: {
                    startTime: new Date().toISOString(),
                    duration,
                    oreType,
                    efficiency: companion.level || 1,
                  },
                },
              },
            },
          },
        }));

        return true;
      },

      completeMining: (companionId: string) => {
        const state = get();
        const mining = state.companions.forge.mining.activeMiners[companionId];
        
        if (!mining) return;

        // Berechne Erz-Gewinn basierend auf Effizienz und Dauer mit korrekter Erz-Typ-Logik
        const efficiency = mining.efficiency;
        const baseOreGain = 10; // Basis-Erz pro Minute für Eisen
        
        // Berechne die Produktionsrate basierend auf dem Erz-Typ
        let orePerMinute: number;
        switch (mining.oreType) {
          case 'iron':
            orePerMinute = baseOreGain * efficiency; // 10 * Level pro Minute
            break;
          case 'steel':
            orePerMinute = (baseOreGain * efficiency) / 10; // 10x langsamer
            break;
          case 'mithril':
            orePerMinute = (baseOreGain * efficiency) / 100; // 100x langsamer
            break;
          case 'adamantium':
            orePerMinute = (baseOreGain * efficiency) / 1000; // 1000x langsamer
            break;
          default:
            orePerMinute = baseOreGain * efficiency;
        }
        
        const oreGain = Math.floor(orePerMinute * (mining.duration / 60));

        // Füge Erz hinzu und entferne Begleiter aus Mining
        set((state) => {
          const newActiveMiners = { ...state.companions.forge.mining.activeMiners };
          delete newActiveMiners[companionId];
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                ore: {
                  ...state.companions.forge.ore,
                  [mining.oreType]: state.companions.forge.ore[mining.oreType] + oreGain,
                },
                mining: {
                  ...state.companions.forge.mining,
                  activeMiners: newActiveMiners,
                },
              },
            },
          };
        });
      },

      // Neue Funktion: Begleiter sofort aus Mining entfernen (ohne zusätzliches Erz)
      removeFromMining: (companionId: string) => {
        const state = get();
        const mining = state.companions.forge.mining.activeMiners[companionId];
        
        if (!mining) return;

        // Entferne Begleiter aus Mining ohne zusätzliches Erz hinzuzufügen
        // (Erz wird bereits kontinuierlich durch updateContinuousMining hinzugefügt)
        set((state) => {
          const newActiveMiners = { ...state.companions.forge.mining.activeMiners };
          delete newActiveMiners[companionId];
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                mining: {
                  ...state.companions.forge.mining,
                  activeMiners: newActiveMiners,
                },
              },
            },
          };
        });
      },

      updateMining: () => {
        const state = get();
        const now = new Date();
        const companionsToComplete: string[] = [];

        // Sammle alle Begleiter, die fertig sind
        Object.keys(state.companions.forge.mining.activeMiners).forEach(companionId => {
          const mining = state.companions.forge.mining.activeMiners[companionId];
          
          if (mining && mining.startTime && mining.duration) {
            const startTime = new Date(mining.startTime);
            const endTime = new Date(startTime.getTime() + mining.duration * 60 * 1000);
            
            if (now >= endTime) {
              companionsToComplete.push(companionId);
            }
          }
        });

        // Führe alle Completions aus
        companionsToComplete.forEach(companionId => {
          state.completeMining(companionId);
        });
      },

      // Kontinuierliche Erz-Gewinnung (jede Sekunde)
      updateContinuousMining: () => {
        const state = get();
        const now = new Date();
        let oreGained = { iron: 0, steel: 0, mithril: 0, adamantium: 0 };

        // Berechne Erz-Gewinn für alle aktiven Minenarbeiter
        Object.keys(state.companions.forge.mining.activeMiners).forEach(companionId => {
          const mining = state.companions.forge.mining.activeMiners[companionId];
          
          if (mining && mining.startTime) {
            const efficiency = mining.efficiency;
            const baseOreGain = 10; // Basis-Erz pro Minute für Eisen
            
            // Berechne die Produktionsrate basierend auf dem Erz-Typ
            let orePerMinute: number;
            switch (mining.oreType) {
              case 'iron':
                orePerMinute = baseOreGain * efficiency; // 10 * Level pro Minute
                break;
              case 'steel':
                orePerMinute = (baseOreGain * efficiency) / 10; // 10x langsamer
                break;
              case 'mithril':
                orePerMinute = (baseOreGain * efficiency) / 100; // 100x langsamer
                break;
              case 'adamantium':
                orePerMinute = (baseOreGain * efficiency) / 1000; // 1000x langsamer
                break;
              default:
                orePerMinute = baseOreGain * efficiency;
            }
            
            const orePerSecond = orePerMinute / 60; // Konvertiere zu pro Sekunde
            
            // Füge Erz pro Sekunde hinzu
            oreGained[mining.oreType] += orePerSecond;
          }
        });

        // Füge das gewonnene Erz zum tatsächlichen Bestand hinzu und gebe XP an Begleiter
        if (oreGained.iron > 0 || oreGained.steel > 0 || oreGained.mithril > 0 || oreGained.adamantium > 0) {
          set((state) => {
            // Berechne den neuen Erz-Bestand
            const newOre = {
              iron: state.companions.forge.ore.iron + oreGained.iron,
              steel: state.companions.forge.ore.steel + oreGained.steel,
              mithril: state.companions.forge.ore.mithril + oreGained.mithril,
              adamantium: state.companions.forge.ore.adamantium + oreGained.adamantium,
            };

            // Aktualisiere Begleiter mit XP basierend auf Erz-Produktion
            const updatedCompanions = state.companions.discovered.map(companion => {
              const mining = state.companions.forge.mining.activeMiners[companion.id];
              if (mining && mining.startTime) {
                // Berechne XP basierend auf Erz-Typ und Produktionsrate
                let xpGain = 0;
                const baseXpPerSecond = 0.1; // Basis-XP pro Sekunde
                
                switch (mining.oreType) {
                  case 'iron':
                    xpGain = baseXpPerSecond * mining.efficiency; // Standard XP
                    break;
                  case 'steel':
                    xpGain = baseXpPerSecond * mining.efficiency * 10; // 10x mehr XP
                    break;
                  case 'mithril':
                    xpGain = baseXpPerSecond * mining.efficiency * 100; // 100x mehr XP
                    break;
                  case 'adamantium':
                    xpGain = baseXpPerSecond * mining.efficiency * 1000; // 1000x mehr XP
                    break;
                  default:
                    xpGain = baseXpPerSecond * mining.efficiency;
                }

                // Füge XP hinzu und prüfe Level-Up
                const currentLevel = companion.level || 1;
                const currentExperience = companion.experience || 0;
                let newExperience = currentExperience + xpGain;
                let newLevel = currentLevel;
                let finalExperience = newExperience;

                // Prüfe ob Level-Up möglich ist (kontinuierlich bis alle Level erreicht sind)
                let updatedAbilities = companion.abilities;
                while (finalExperience >= (newLevel * 100)) {
                  newLevel++;
                  finalExperience = newExperience - (newLevel * 100);
                  
                  // Verbessere Standard-Fähigkeiten beim Level-Up
                  updatedAbilities = updatedAbilities.map(ability => {
                    const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
                    if (isStandardAbility) {
                      return {
                        ...ability,
                        value: Math.floor(ability.value * 1.1) // 10% Verbesserung
                      };
                    }
                    return ability;
                  });
                }

                return {
                  ...companion,
                  level: newLevel,
                  experience: finalExperience,
                };
              }
              return companion;
            });

            return {
              companions: {
                ...state.companions,
                discovered: updatedCompanions,
                forge: {
                  ...state.companions.forge,
                  ore: newOre,
                  mining: state.companions.forge.mining,
                },
              },
            };
          });
        }
      },

      getMiningProgress: (companionId: string) => {
        const state = get();
        const mining = state.companions.forge.mining.activeMiners[companionId];
        
        if (!mining || !mining.startTime) return 0;
        
        const startTime = new Date(mining.startTime);
        const endTime = new Date(startTime.getTime() + mining.duration * 60 * 1000);
        const now = new Date();
        const totalMs = mining.duration * 60 * 1000;
        const elapsedMs = now.getTime() - startTime.getTime();
        
        return Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
      },

      isCompanionMining: (companionId: string) => {
        const state = get();
        return !!state.companions.forge.mining.activeMiners[companionId];
      },

      craftArmor: (armorType: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
        const state = get();
        
        // Definiere Rüstungs-Kosten und Eigenschaften
        const armorConfig = {
          helmet: { defense: 5, durability: 100, craftingTime: 30 },
          chestplate: { defense: 15, durability: 200, craftingTime: 60 },
          greaves: { defense: 8, durability: 150, craftingTime: 45 },
          boots: { defense: 3, durability: 80, craftingTime: 20 },
          shield: { defense: 10, durability: 120, craftingTime: 40 },
        };

        const materialMultiplier = {
          iron: 1,
          steel: 2,
          mithril: 4,
          adamantium: 8,
        };

        const config = armorConfig[armorType];
        const multiplier = materialMultiplier[material];
        
        // Kosten basierend auf Material - höhere Materialien verwenden nur ihr eigenes Material
        const costs = {
          iron: { iron: 5, steel: 0, mithril: 0, adamantium: 0 },
          steel: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
          mithril: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
          adamantium: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
        };

        const cost = costs[material];
        
        // Bestimme das nächste Level für diese Rüstung
        const existingArmor = Object.values(state.companions.forge.armor || {})
          .filter(armor => armor.type === armorType && armor.material === material)
          .sort((a, b) => (b.level || 0) - (a.level || 0));
        
        const nextLevel = existingArmor.length > 0 ? (existingArmor[0].level || 0) + 1 : 1;
        
        // Berechne Kosten basierend auf Level (jedes Level kostet 50% mehr)
        const levelCostMultiplier = Math.pow(1.5, nextLevel - 1);
        const adjustedCost = {
          iron: Math.ceil(cost.iron * levelCostMultiplier),
          steel: Math.ceil(cost.steel * levelCostMultiplier),
          mithril: Math.ceil(cost.mithril * levelCostMultiplier),
          adamantium: Math.ceil(cost.adamantium * levelCostMultiplier),
        };
        
        // Prüfe ob genug Erz vorhanden (direkt aus dem Store)
        const currentOre = state.companions.forge?.ore || { iron: 0, steel: 0, mithril: 0, adamantium: 0 };
        if (currentOre.iron < adjustedCost.iron ||
            currentOre.steel < adjustedCost.steel ||
            currentOre.mithril < adjustedCost.mithril ||
            currentOre.adamantium < adjustedCost.adamantium) {
          return false;
        }

        // Prüfe ob das Material freigeschaltet ist (direkt aus dem Store)
        const oreRequirements = {
          iron: 0,
          steel: 10000,
          mithril: 50000,
          adamantium: 100000,
        };
        
        let isMaterialUnlocked = true;
        switch (material) {
          case 'steel':
            isMaterialUnlocked = currentOre.iron >= oreRequirements.steel;
            break;
          case 'mithril':
            isMaterialUnlocked = currentOre.steel >= oreRequirements.mithril;
            break;
          case 'adamantium':
            isMaterialUnlocked = currentOre.mithril >= oreRequirements.adamantium;
            break;
        }
        
        if (!isMaterialUnlocked) {
          return false;
        }
        
        // Erstelle Rüstung
        const armorId = `${armorType}_${material}_${Date.now()}`;
        
        // Deutsche Namen für Materialien und Rüstungstypen
        const materialNames = {
          iron: 'Eisen',
          steel: 'Stahl',
          mithril: 'Mithril',
          adamantium: 'Adamantium'
        };
        
        const armorTypeNames = {
          helmet: 'Helm',
          chestplate: 'Brustplatte',
          greaves: 'Beinschienen',
          boots: 'Stiefel',
          shield: 'Schild'
        };
        
        const armorName = `${materialNames[material]} ${armorTypeNames[armorType]} Level ${nextLevel}`;
        
        set((state) => ({
          companions: {
            ...state.companions,
            forge: {
              ...state.companions.forge,
              ore: {
                iron: state.companions.forge.ore.iron - adjustedCost.iron,
                steel: state.companions.forge.ore.steel - adjustedCost.steel,
                mithril: state.companions.forge.ore.mithril - adjustedCost.mithril,
                adamantium: state.companions.forge.ore.adamantium - adjustedCost.adamantium,
              },
              armor: {
                ...state.companions.forge.armor,
                [armorId]: {
                  id: armorId,
                  name: armorName,
                  type: armorType,
                  material,
                  level: nextLevel,
                  defense: config.defense * multiplier * nextLevel,
                  durability: config.durability * multiplier * nextLevel,
                  maxDurability: config.durability * multiplier * nextLevel,
                  isCrafting: true,
                  craftingStartTime: new Date().toISOString(),
                  craftingDuration: config.craftingTime,
                },
              },
            },
          },
        }));

        return true;
      },

      completeArmorCrafting: (armorId: string) => {
        const state = get();
        const armor = state.companions.forge.armor[armorId];
        
        if (!armor || !armor.isCrafting) return;

        set((state) => ({
          companions: {
            ...state.companions,
            forge: {
              ...state.companions.forge,
              armor: {
                ...state.companions.forge.armor,
                [armorId]: {
                  ...armor,
                  isCrafting: false,
                },
              },
            },
          },
        }));
      },

      updateArmorCrafting: () => {
        const state = get();
        if (!state.companions.forge?.armor) {
          return;
        }
        const now = new Date();

        Object.keys(state.companions.forge.armor).forEach(armorId => {
          const armor = state.companions.forge.armor[armorId];
          
          if (armor.isCrafting && armor.craftingStartTime && armor.craftingDuration) {
            const startTime = new Date(armor.craftingStartTime);
            const endTime = new Date(startTime.getTime() + armor.craftingDuration * 60 * 1000);
            
            if (now >= endTime) {
              state.completeArmorCrafting(armorId);
            }
          }
        });
      },

      getArmorCraftingProgress: (armorId: string) => {
        const state = get();
        const armor = state.companions.forge.armor[armorId];
        
        if (!armor || !armor.isCrafting || !armor.craftingStartTime) return 0;
        
        const startTime = new Date(armor.craftingStartTime);
        const endTime = new Date(startTime.getTime() + armor.craftingDuration * 60 * 1000);
        const now = new Date();
        const totalMs = armor.craftingDuration * 60 * 1000;
        const elapsedMs = now.getTime() - startTime.getTime();
        
        return Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100));
      },

      equipArmor: (armorId: string, companionId: string) => {
        const state = get();
        const armor = state.companions.forge.armor[armorId];
        
        if (!armor || armor.isCrafting || armor.equippedBy) {
          return false;
        }

        // Entferne andere Rüstung des gleichen Typs vom Begleiter
        if (state.companions.forge?.armor) {
          Object.keys(state.companions.forge.armor).forEach(id => {
            const existingArmor = state.companions.forge.armor[id];
            if (existingArmor.equippedBy === companionId && existingArmor.type === armor.type) {
              state.unequipArmor(id);
            }
          });
        }

        set((state) => ({
          companions: {
            ...state.companions,
            forge: {
              ...state.companions.forge,
              armor: {
                ...state.companions.forge.armor,
                [armorId]: {
                  ...armor,
                  equippedBy: companionId,
                },
              },
            },
          },
        }));

        return true;
      },

      unequipArmor: (armorId: string) => {
        set((state) => ({
          companions: {
            ...state.companions,
            forge: {
              ...state.companions.forge,
              armor: {
                ...state.companions.forge.armor,
                [armorId]: {
                  ...state.companions.forge.armor[armorId],
                  equippedBy: undefined,
                },
              },
            },
          },
        }));
      },

      getCompanionArmor: (companionId: string) => {
        const state = get();
        if (!state.companions.forge?.armor) {
          return [];
        }
        return Object.values(state.companions.forge.armor).filter(armor => 
          armor.equippedBy === companionId && !armor.isCrafting
        );
      },

      getOreCounts: () => {
        const state = get();
        if (!state.companions.forge?.ore) {
          return { iron: 0, steel: 0, mithril: 0, adamantium: 0 };
        }
        
        // Gib einfach den aktuellen Erz-Bestand zurück
        return state.companions.forge.ore;
      },

      // Haltbarkeits-Reduktion nach Arena-Kämpfen
      reduceArmorDurability: (armorId: string, amount: number) => {
        set((state) => {
          if (!state.companions.forge?.armor?.[armorId]) return state;
          
          const armor = state.companions.forge.armor[armorId];
          const newDurability = Math.max(0, armor.durability - amount);
          
          const newArmor = { ...state.companions.forge.armor };
          newArmor[armorId] = {
            ...armor,
            durability: newDurability,
          };
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                armor: newArmor,
              },
            },
          };
        });
      },

      reduceWeaponDurability: (weaponId: string, amount: number) => {
        set((state) => {
          if (!state.companions.forge?.weapons?.[weaponId]) return state;
          
          const weapon = state.companions.forge.weapons[weaponId];
          const newDurability = Math.max(0, weapon.durability - amount);
          
          const newWeapons = { ...state.companions.forge.weapons };
          newWeapons[weaponId] = {
            ...weapon,
            durability: newDurability,
          };
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                weapons: newWeapons,
              },
            },
          };
        });
      },

      // Reset-Funktionen für Rüstungen und Waffen
      resetArmorLevel: (armorType: 'helmet' | 'chestplate' | 'greaves' | 'boots' | 'shield', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
        set((state) => {
          // Entferne alle Rüstungen dieses Typs und Materials
          const newArmor = { ...state.companions.forge.armor };
          Object.keys(newArmor).forEach(armorId => {
            const armor = newArmor[armorId];
            if (armor.type === armorType && armor.material === material) {
              delete newArmor[armorId];
            }
          });
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                armor: newArmor,
              },
            },
          };
        });
      },

      resetWeaponLevel: (weaponType: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
        set((state) => {
          // Entferne alle Waffen dieses Typs und Materials
          const newWeapons = { ...state.companions.forge.weapons };
          Object.keys(newWeapons).forEach(weaponId => {
            const weapon = newWeapons[weaponId];
            if (weapon.type === weaponType && weapon.material === material) {
              delete newWeapons[weaponId];
            }
          });
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                weapons: newWeapons,
              },
            },
          };
        });
      },

      getCraftableArmor: () => {
        return [
          // Eisen-Rüstungen
          {
            type: 'helmet' as const,
            material: 'iron' as const,
            name: 'Eisen Helm',
            description: 'Grundlegender Eisenhelm',
            defense: 5,
            durability: 100,
            cost: { iron: 5, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 3,
          },
          {
            type: 'chestplate' as const,
            material: 'iron' as const,
            name: 'Eisen Brustplatte',
            description: 'Grundlegende Eisenbrustplatte',
            defense: 15,
            durability: 200,
            cost: { iron: 10, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 6,
          },
          {
            type: 'greaves' as const,
            material: 'iron' as const,
            name: 'Eisen Beinschienen',
            description: 'Grundlegende Eisenbeinschienen',
            defense: 8,
            durability: 150,
            cost: { iron: 8, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 5,
          },
          {
            type: 'boots' as const,
            material: 'iron' as const,
            name: 'Eisen Stiefel',
            description: 'Grundlegende Eisenstiefel',
            defense: 3,
            durability: 80,
            cost: { iron: 3, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 2,
          },
          {
            type: 'shield' as const,
            material: 'iron' as const,
            name: 'Eisen Schild',
            description: 'Grundlegender Eisenschild',
            defense: 10,
            durability: 120,
            cost: { iron: 6, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 4,
          },

          // Stahl-Rüstungen
          {
            type: 'helmet' as const,
            material: 'steel' as const,
            name: 'Stahl Helm',
            description: 'Verstärkter Stahlhelm',
            defense: 12,
            durability: 200,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 5,
          },
          {
            type: 'chestplate' as const,
            material: 'steel' as const,
            name: 'Stahl Brustplatte',
            description: 'Verstärkte Stahlbrustplatte',
            defense: 35,
            durability: 400,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 9,
          },
          {
            type: 'greaves' as const,
            material: 'steel' as const,
            name: 'Stahl Beinschienen',
            description: 'Verstärkte Stahlbeinschienen',
            defense: 20,
            durability: 300,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 7,
          },
          {
            type: 'boots' as const,
            material: 'steel' as const,
            name: 'Stahl Stiefel',
            description: 'Verstärkte Stahlstiefel',
            defense: 8,
            durability: 160,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 3,
          },
          {
            type: 'shield' as const,
            material: 'steel' as const,
            name: 'Stahl Schild',
            description: 'Verstärkter Stahlschild',
            defense: 25,
            durability: 240,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 6,
          },

          // Mithril-Rüstungen
          {
            type: 'helmet' as const,
            material: 'mithril' as const,
            name: 'Mithril Helm',
            description: 'Verzauberter Mithrilhelm',
            defense: 25,
            durability: 400,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 6,
          },
          {
            type: 'chestplate' as const,
            material: 'mithril' as const,
            name: 'Mithril Brustplatte',
            description: 'Verzauberte Mithrilbrustplatte',
            defense: 75,
            durability: 800,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 12,
          },
          {
            type: 'greaves' as const,
            material: 'mithril' as const,
            name: 'Mithril Beinschienen',
            description: 'Verzauberte Mithrilbeinschienen',
            defense: 45,
            durability: 600,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 9,
          },
          {
            type: 'boots' as const,
            material: 'mithril' as const,
            name: 'Mithril Stiefel',
            description: 'Verzauberte Mithrilstiefel',
            defense: 18,
            durability: 320,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 4,
          },
          {
            type: 'shield' as const,
            material: 'mithril' as const,
            name: 'Mithril Schild',
            description: 'Verzauberter Mithrilschild',
            defense: 55,
            durability: 480,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 8,
          },

          // Adamantium-Rüstungen
          {
            type: 'helmet' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Helm',
            description: 'Legendärer Adamantiumhelm',
            defense: 50,
            durability: 800,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 9,
          },
          {
            type: 'chestplate' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Brustplatte',
            description: 'Legendäre Adamantiumbrustplatte',
            defense: 150,
            durability: 1600,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 18,
          },
          {
            type: 'greaves' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Beinschienen',
            description: 'Legendäre Adamantiumbeinschienen',
            defense: 90,
            durability: 1200,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 14,
          },
          {
            type: 'boots' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Stiefel',
            description: 'Legendäre Adamantiumstiefel',
            defense: 35,
            durability: 640,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 6,
          },
          {
            type: 'shield' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Schild',
            description: 'Legendärer Adamantiumschild',
            defense: 110,
            durability: 960,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 120,
          },
        ];
      },

      // Waffen-System Funktionen
      getCraftableWeapons: () => {
        return [
          // Eisen-Waffen
          {
            type: 'sword' as const,
            material: 'iron' as const,
            name: 'Eisen Schwert',
            description: 'Grundlegendes Eisenschwert',
            attack: 15,
            durability: 100,
            cost: { iron: 4, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 3,
          },
          {
            type: 'axe' as const,
            material: 'iron' as const,
            name: 'Eisen Axt',
            description: 'Grundlegende Eisenaxt',
            attack: 20,
            durability: 80,
            cost: { iron: 5, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 3,
          },
          {
            type: 'mace' as const,
            material: 'iron' as const,
            name: 'Eisen Streitkolben',
            description: 'Grundlegender Eisenstreitkolben',
            attack: 18,
            durability: 120,
            cost: { iron: 5, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 3,
          },
          {
            type: 'dagger' as const,
            material: 'iron' as const,
            name: 'Eisen Dolch',
            description: 'Grundlegender Eisendolch',
            attack: 12,
            durability: 60,
            cost: { iron: 3, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 2,
          },
          {
            type: 'bow' as const,
            material: 'iron' as const,
            name: 'Eisen Bogen',
            description: 'Grundlegender Eisenbogen',
            attack: 14,
            durability: 90,
            cost: { iron: 4, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 2,
          },
          {
            type: 'staff' as const,
            material: 'iron' as const,
            name: 'Eisen Stab',
            description: 'Grundlegender Eisenstab',
            attack: 10,
            durability: 150,
            cost: { iron: 3, steel: 0, mithril: 0, adamantium: 0 },
            craftingTime: 2,
          },

          // Stahl-Waffen
          {
            type: 'sword' as const,
            material: 'steel' as const,
            name: 'Stahl Schwert',
            description: 'Verstärktes Stahlschwert',
            attack: 35,
            durability: 200,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 4,
          },
          {
            type: 'axe' as const,
            material: 'steel' as const,
            name: 'Stahl Axt',
            description: 'Verstärkte Stahlaxt',
            attack: 45,
            durability: 160,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 5,
          },
          {
            type: 'mace' as const,
            material: 'steel' as const,
            name: 'Stahl Streitkolben',
            description: 'Verstärkter Stahlstreitkolben',
            attack: 40,
            durability: 240,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 5,
          },
          {
            type: 'dagger' as const,
            material: 'steel' as const,
            name: 'Stahl Dolch',
            description: 'Verstärkter Stahldolch',
            attack: 28,
            durability: 120,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 3,
          },
          {
            type: 'bow' as const,
            material: 'steel' as const,
            name: 'Stahl Bogen',
            description: 'Verstärkter Stahlbogen',
            attack: 32,
            durability: 180,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 4,
          },
          {
            type: 'staff' as const,
            material: 'steel' as const,
            name: 'Stahl Stab',
            description: 'Verstärkter Stahlstab',
            attack: 25,
            durability: 300,
            cost: { iron: 0, steel: 10, mithril: 0, adamantium: 0 },
            craftingTime: 3,
          },

          // Mithril-Waffen
          {
            type: 'sword' as const,
            material: 'mithril' as const,
            name: 'Mithril Schwert',
            description: 'Verzaubertes Mithrilschwert',
            attack: 75,
            durability: 400,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 6,
          },
          {
            type: 'axe' as const,
            material: 'mithril' as const,
            name: 'Mithril Axt',
            description: 'Verzauberte Mithrilaxt',
            attack: 95,
            durability: 320,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 8,
          },
          {
            type: 'mace' as const,
            material: 'mithril' as const,
            name: 'Mithril Streitkolben',
            description: 'Verzauberter Mithrilstreitkolben',
            attack: 85,
            durability: 480,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 7,
          },
          {
            type: 'dagger' as const,
            material: 'mithril' as const,
            name: 'Mithril Dolch',
            description: 'Verzauberter Mithrildolch',
            attack: 60,
            durability: 240,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 4,
          },
          {
            type: 'bow' as const,
            material: 'mithril' as const,
            name: 'Mithril Bogen',
            description: 'Verzauberter Mithrilbogen',
            attack: 68,
            durability: 360,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 6,
          },
          {
            type: 'staff' as const,
            material: 'mithril' as const,
            name: 'Mithril Stab',
            description: 'Verzauberter Mithrilstab',
            attack: 55,
            durability: 600,
            cost: { iron: 0, steel: 0, mithril: 15, adamantium: 0 },
            craftingTime: 5,
          },

          // Adamantium-Waffen
          {
            type: 'sword' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Schwert',
            description: 'Legendäres Adamantiumschwert',
            attack: 150,
            durability: 800,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 9,
          },
          {
            type: 'axe' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Axt',
            description: 'Legendäre Adamantumaxt',
            attack: 190,
            durability: 640,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 12,
          },
          {
            type: 'mace' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Streitkolben',
            description: 'Legendärer Adamantiumstreitkolben',
            attack: 170,
            durability: 960,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 10,
          },
          {
            type: 'dagger' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Dolch',
            description: 'Legendärer Adamantiumdolch',
            attack: 120,
            durability: 480,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 6,
          },
          {
            type: 'bow' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Bogen',
            description: 'Legendärer Adamantiumbogen',
            attack: 136,
            durability: 720,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 8,
          },
          {
            type: 'staff' as const,
            material: 'adamantium' as const,
            name: 'Adamantium Stab',
            description: 'Legendärer Adamantiumstab',
            attack: 110,
            durability: 1200,
            cost: { iron: 0, steel: 0, mithril: 0, adamantium: 30 },
            craftingTime: 7,
          },
        ];
      },

      craftWeapon: (weaponType: 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff', material: 'iron' | 'steel' | 'mithril' | 'adamantium') => {
        const state = get();
        const craftableWeapons = state.getCraftableWeapons();
        const weapon = craftableWeapons.find(w => w.type === weaponType && w.material === material);
        
        if (!weapon) return false;
        
        // Bestimme das nächste Level für diese Waffe
        const existingWeapons = Object.values(state.companions.forge.weapons || {})
          .filter(w => w.type === weaponType && w.material === material)
          .sort((a, b) => (b.level || 0) - (a.level || 0));
        
        const nextLevel = existingWeapons.length > 0 ? (existingWeapons[0].level || 0) + 1 : 1;
        
        // Berechne Kosten basierend auf Level (jedes Level kostet 50% mehr)
        const levelCostMultiplier = Math.pow(1.5, nextLevel - 1);
        const adjustedCost = {
          iron: Math.ceil(weapon.cost.iron * levelCostMultiplier),
          steel: Math.ceil(weapon.cost.steel * levelCostMultiplier),
          mithril: Math.ceil(weapon.cost.mithril * levelCostMultiplier),
          adamantium: Math.ceil(weapon.cost.adamantium * levelCostMultiplier),
        };
        
        // Prüfe ob genug Erz vorhanden ist (direkt aus dem Store)
        const currentOre = state.companions.forge?.ore || { iron: 0, steel: 0, mithril: 0, adamantium: 0 };
        if (currentOre.iron < adjustedCost.iron || 
            currentOre.steel < adjustedCost.steel || 
            currentOre.mithril < adjustedCost.mithril || 
            currentOre.adamantium < adjustedCost.adamantium) {
          return false;
        }

        // Prüfe ob das Material freigeschaltet ist (direkt aus dem Store)
        const oreRequirements = {
          iron: 0,
          steel: 10000,
          mithril: 50000,
          adamantium: 100000,
        };
        
        let isMaterialUnlocked = true;
        switch (weapon.material) {
          case 'steel':
            isMaterialUnlocked = currentOre.iron >= oreRequirements.steel;
            break;
          case 'mithril':
            isMaterialUnlocked = currentOre.steel >= oreRequirements.mithril;
            break;
          case 'adamantium':
            isMaterialUnlocked = currentOre.mithril >= oreRequirements.adamantium;
            break;
        }
        
        if (!isMaterialUnlocked) {
          return false;
        }
        
        // Erstelle Waffen-ID
        const weaponId = `${weaponType}_${material}_${Date.now()}`;
        
        // Starte Schmiede-Prozess
        set((state) => {
          // Ziehe Erz ab
          const newOre = {
            iron: state.companions.forge.ore.iron - adjustedCost.iron,
            steel: state.companions.forge.ore.steel - adjustedCost.steel,
            mithril: state.companions.forge.ore.mithril - adjustedCost.mithril,
            adamantium: state.companions.forge.ore.adamantium - adjustedCost.adamantium,
          };
          
          // Erstelle neue Waffe
          const newWeapon = {
            id: weaponId,
            name: `${weapon.name} Level ${nextLevel}`,
            type: weapon.type,
            material: weapon.material,
            level: nextLevel,
            attack: weapon.attack * nextLevel,
            durability: weapon.durability * nextLevel,
            maxDurability: weapon.durability * nextLevel,
            equippedBy: undefined,
            isCrafting: true,
            craftingStartTime: new Date().toISOString(),
            craftingDuration: weapon.craftingTime,
          };
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                ore: newOre,
                weapons: {
                  ...state.companions.forge.weapons,
                  [weaponId]: newWeapon,
                },
              },
            },
          };
        });
        
        return true;
      },

      completeWeaponCrafting: (weaponId: string) => {
        set((state) => {
          const weapon = state.companions.forge.weapons[weaponId];
          if (!weapon) return state;
          
          const newWeapons = { ...state.companions.forge.weapons };
          newWeapons[weaponId] = {
            ...weapon,
            isCrafting: false,
            craftingStartTime: undefined,
          };
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                weapons: newWeapons,
              },
            },
          };
        });
      },

      updateWeaponCrafting: () => {
        const state = get();
        if (!state.companions.forge?.weapons) {
          return;
        }
        const now = new Date();
        
        Object.keys(state.companions.forge.weapons).forEach(weaponId => {
          const weapon = state.companions.forge.weapons[weaponId];
          
          if (weapon.isCrafting && weapon.craftingStartTime) {
            const startTime = new Date(weapon.craftingStartTime);
            const elapsed = now.getTime() - startTime.getTime();
            const totalDuration = weapon.craftingDuration * 60 * 1000; // Minuten zu Millisekunden
            
            if (elapsed >= totalDuration) {
              state.completeWeaponCrafting(weaponId);
            }
          }
        });
      },

      getWeaponCraftingProgress: (weaponId: string) => {
        const state = get();
        const weapon = state.companions.forge.weapons[weaponId];
        
        if (!weapon || !weapon.isCrafting || !weapon.craftingStartTime) return 0;
        
        const startTime = new Date(weapon.craftingStartTime);
        const now = new Date();
        const elapsed = now.getTime() - startTime.getTime();
        const totalDuration = weapon.craftingDuration * 60 * 1000; // Minuten zu Millisekunden
        
        return Math.min(100, (elapsed / totalDuration) * 100);
      },

      equipWeapon: (weaponId: string, companionId: string) => {
        const state = get();
        const weapon = state.companions.forge.weapons[weaponId];
        const companion = state.companions.discovered.find(c => c.id === companionId);
        
        if (!weapon || !companion || weapon.equippedBy) return false;
        
        set((state) => {
          const newWeapons = { ...state.companions.forge.weapons };
          newWeapons[weaponId] = {
            ...weapon,
            equippedBy: companionId,
          };
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                weapons: newWeapons,
              },
            },
          };
        });
        
        return true;
      },

      unequipWeapon: (weaponId: string) => {
        set((state) => {
          const weapon = state.companions.forge.weapons[weaponId];
          if (!weapon) return state;
          
          const newWeapons = { ...state.companions.forge.weapons };
          newWeapons[weaponId] = {
            ...weapon,
            equippedBy: undefined,
          };
          
          return {
            companions: {
              ...state.companions,
              forge: {
                ...state.companions.forge,
                weapons: newWeapons,
              },
            },
          };
        });
      },

      // Gegenstände gegen Erfahrung eintauschen
      getItemExperienceValue: (item: any, itemType: 'armor' | 'weapon') => {
        const baseValue = itemType === 'armor' ? item.defense : item.attack;
        const materialMultiplier: { [key: string]: number } = {
          iron: 1,
          steel: 2,
          mithril: 4,
          adamantium: 8,
        };
        const levelMultiplier = item.level || 1;
        const durabilityMultiplier = (item.durability || 100) / (item.maxDurability || 100);
        
        return Math.floor(baseValue * materialMultiplier[item.material] * levelMultiplier * durabilityMultiplier * 10);
      },

      addCompanionExperience: (companionId: string, experience: number) => {
        set((state) => {
          const companion = state.companions.discovered.find(c => c.id === companionId);
          if (!companion) return state;

          const currentLevel = companion.level || 1;
          const currentExperience = companion.experience || 0;
          const newExperience = currentExperience + experience;
          let newLevel = currentLevel;
          let finalExperience = newExperience;

          // Prüfe ob Level-Up möglich ist (kontinuierlich bis alle Level erreicht sind)
          let updatedAbilities = companion.abilities;
          while (finalExperience >= (newLevel * 100)) {
            finalExperience = finalExperience - (newLevel * 100);
            newLevel++;
            
            // Verbessere Standard-Fähigkeiten beim Level-Up
            updatedAbilities = updatedAbilities.map(ability => {
              const isStandardAbility = !['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
              if (isStandardAbility) {
                return {
                  ...ability,
                  value: Math.floor(ability.value * 1.1) // 10% Verbesserung
                };
              }
              return ability;
            });
          }

          const updatedCompanion = { 
            ...companion, 
            level: newLevel,
            experience: finalExperience,
            abilities: updatedAbilities
          };

          const updatedDiscovered = state.companions.discovered.map(c => 
            c.id === companionId ? updatedCompanion : c
          );
          
          const updatedActive = state.companions.active.map(c => 
            c && c.id === companionId ? updatedCompanion : c
          );
          
          return {
            companions: {
              ...state.companions,
              discovered: updatedDiscovered,
              active: updatedActive
            }
          };
        });
      },

      addCompanionBattleExperience: (companionId: string, battleExperience: number) => {
        set((state) => {
          const companion = state.companions.discovered.find(c => c.id === companionId);
          if (!companion) return state;

          const currentBattleLevel = companion.battleLevel || 1;
          const currentBattleExperience = companion.battleExperience || 0;
          const newBattleExperience = currentBattleExperience + battleExperience;
          let newBattleLevel = currentBattleLevel;
          let finalBattleExperience = newBattleExperience;

          // Prüfe ob Battle Level-Up möglich ist (kontinuierlich bis alle Level erreicht sind)
          let updatedAbilities = companion.abilities;
          while (finalBattleExperience >= (newBattleLevel * 100)) {
            finalBattleExperience = finalBattleExperience - (newBattleLevel * 100);
            newBattleLevel++;
            
            // Verbessere Kampf-Fähigkeiten beim Battle Level-Up
            updatedAbilities = updatedAbilities.map(ability => {
              const isBattleAbility = ['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(ability.type);
              if (isBattleAbility) {
                return {
                  ...ability,
                  value: Math.floor(ability.value * 1.15) // 15% Verbesserung pro Battle Level
                };
              }
              return ability;
            });
          }

          const updatedCompanion = { 
            ...companion, 
            battleLevel: newBattleLevel,
            battleExperience: finalBattleExperience,
            abilities: updatedAbilities
          };

          const updatedDiscovered = state.companions.discovered.map(c => 
            c.id === companionId ? updatedCompanion : c
          );
          
          const updatedActive = state.companions.active.map(c => 
            c && c.id === companionId ? updatedCompanion : c
          );
          
          return {
            companions: {
              ...state.companions,
              discovered: updatedDiscovered,
              active: updatedActive
            }
          };
        });
      },

      tradeItemForExperience: (itemId: string, itemType: 'armor' | 'weapon', companionId: string, xpType: 'normal' | 'arena' = 'normal') => {
        const state = get();
        
        // Hole das Item
        let item;
        if (itemType === 'armor') {
          item = state.companions.forge.armor[itemId];
        } else {
          item = state.companions.forge.weapons[itemId];
        }
        
        if (!item || item.equippedBy) {
          return false; // Item nicht gefunden oder ausgerüstet
        }
        
        // Berechne Erfahrungswert
        const experienceValue = state.getItemExperienceValue(item, itemType);
        
        // Füge Erfahrung zum Begleiter hinzu (basierend auf XP-Typ)
        if (xpType === 'arena') {
          state.addCompanionBattleExperience(companionId, experienceValue);
        } else {
          state.addCompanionExperience(companionId, experienceValue);
        }
        
        // Entferne das Item
        set((state) => {
          if (itemType === 'armor') {
            const newArmor = { ...state.companions.forge.armor };
            delete newArmor[itemId];
            return {
              companions: {
                ...state.companions,
                forge: {
                  ...state.companions.forge,
                  armor: newArmor,
                },
              },
            };
          } else {
            const newWeapons = { ...state.companions.forge.weapons };
            delete newWeapons[itemId];
            return {
              companions: {
                ...state.companions,
                forge: {
                  ...state.companions.forge,
                  weapons: newWeapons,
                },
              },
            };
          }
        });
        
        return true;
      },

      getCompanionWeapons: (companionId: string) => {
        const state = get();
        if (!state.companions.forge?.weapons) {
          return [];
        }
        return Object.values(state.companions.forge.weapons)
          .filter(weapon => weapon.equippedBy === companionId)
          .map(weapon => ({
            id: weapon.id,
            name: weapon.name,
            type: weapon.type,
            material: weapon.material,
            attack: weapon.attack,
            durability: weapon.durability,
            maxDurability: weapon.maxDurability,
          }));
      },

      // Eroberungs-System Funktionen
      startConquest: (companionId: string, area: string, duration: number) => {
        const state = get();
        const companion = state.companions.discovered.find(c => c.id === companionId);
        
        if (!companion) return false;
        
        // Prüfe ob Begleiter bereits auf Eroberung ist
        if (state.companions.conquest.activeConquests[companionId]) {
          return false;
        }
        
        // Finde heraus, ob der Begleiter aktiv ist und entferne ihn aus dem aktiven Slot
        const newActiveCompanions = state.companions.active.filter(active => active?.id !== companionId);
        
        // Starte Eroberung
        set((state) => ({
          companions: {
            ...state.companions,
            active: newActiveCompanions,
            conquest: {
              activeConquests: {
                ...state.companions.conquest.activeConquests,
                [companionId]: {
                  startTime: new Date().toISOString(),
                  duration: duration,
                  area: area,
                },
              },
              territoryConquestCounts: state.companions.conquest.territoryConquestCounts,
            },
          },
        }));
        
        return true;
      },
      
      completeConquest: (companionId: string) => {
        const state = get();
        const conquest = state.companions.conquest.activeConquests[companionId];
        
        if (!conquest) return;
        
        // Belohnungen basierend auf dem eroberten Gebiet
        const rewards = {
          coins: Math.floor(Math.random() * 10000) + 5000,
          gems: Math.floor(Math.random() * 50) + 25,
          experience: Math.floor(Math.random() * 500) + 250,
        };
        
        // Füge Belohnungen hinzu
        state.addCoins(rewards.coins);
        state.addGems(rewards.gems);
        state.addExperience(rewards.experience);
        
        // Bestimme welcher Gartenbereich verbessert wird basierend auf dem eroberten Gebiet
        const territoryToGardenMapping: { [key: string]: { area: keyof typeof state.gardenAreas, increase: number } } = {
          'Grüne Wiesen': { area: 'lawn', increase: 25 },
          'Blumenwiese': { area: 'flowerBed', increase: 30 },
          'Kristallsee': { area: 'pond', increase: 35 },
          'Steinterrasse': { area: 'terrace', increase: 40 },
          'Kristallgewächshaus': { area: 'greenhouse', increase: 45 },
          'Uralter Brunnen': { area: 'fountain', increase: 50 },
          'Dunkler Wald': { area: 'orchard', increase: 55 },
          'Fruchtbare Felder': { area: 'veggiePatch', increase: 60 },
          'Rosental': { area: 'roseGarden', increase: 65 },
          'Zen-Tempel': { area: 'japaneseGarden', increase: 70 },
          'Mystischer Sumpf': { area: 'wildflowerMeadow', increase: 75 },
          'Steinige Berge': { area: 'sculptureGarden', increase: 80 },
          'Verzauberter Hain': { area: 'enchantedGrove', increase: 85 },
          'Kristallhöhle': { area: 'crystalCave', increase: 90 },
          'Mondschein-Garten': { area: 'moonlightGarden', increase: 95 },
          'Drachenhort': { area: 'dragonLair', increase: 100 },
          'Goldmine': { area: 'goldMine', increase: 120 },
          'Sandwüste': { area: 'treasureVault', increase: 140 },
          'Alchemie-Turm': { area: 'alchemyLab', increase: 160 },
          'Feuervulkan': { area: 'magicForge', increase: 180 },
        };
        
        const territoryMapping = territoryToGardenMapping[conquest.area];
        if (territoryMapping) {
          // Erhöhe das maximale Level des entsprechenden Gartenbereichs
          set((state) => ({
            gardenAreas: {
              ...state.gardenAreas,
              [territoryMapping.area]: {
                ...state.gardenAreas[territoryMapping.area],
                maxLevel: state.gardenAreas[territoryMapping.area].maxLevel + territoryMapping.increase,
              },
            },
          }));
          
          console.log(`🌱 ${conquest.area} erobert! ${territoryMapping.area} Max-Level um ${territoryMapping.increase} erhöht!`);
        }
        
        // Entferne Eroberung und erhöhe Zähler
        const newActiveConquests = { ...state.companions.conquest.activeConquests };
        delete newActiveConquests[companionId];
        
        // Erhöhe den Eroberungszähler für dieses Territorium
        const newTerritoryConquestCounts = { ...state.companions.conquest.territoryConquestCounts };
        newTerritoryConquestCounts[conquest.area] = (newTerritoryConquestCounts[conquest.area] || 0) + 1;
        
        set((state) => ({
          companions: {
            ...state.companions,
            conquest: {
              activeConquests: newActiveConquests,
              territoryConquestCounts: newTerritoryConquestCounts,
            },
          },
        }));
      },
      
      getConquestTimeRemaining: (companionId: string) => {
        const state = get();
        const conquest = state.companions.conquest.activeConquests[companionId];
        
        if (!conquest) return 0;
        
        const startTime = new Date(conquest.startTime).getTime();
        const now = new Date().getTime();
        const elapsed = now - startTime;
        const totalDuration = conquest.duration * 60 * 1000; // Minuten zu Millisekunden
        
        return Math.max(0, totalDuration - elapsed);
      },
      
      isCompanionOnConquest: (companionId: string) => {
        const state = get();
        return !!state.companions.conquest.activeConquests[companionId];
      },
      
      updateConquests: () => {
        const state = get();
        const now = new Date().getTime();
        
        Object.keys(state.companions.conquest.activeConquests).forEach(companionId => {
          const conquest = state.companions.conquest.activeConquests[companionId];
          const startTime = new Date(conquest.startTime).getTime();
          const elapsed = now - startTime;
          const totalDuration = conquest.duration * 60 * 1000; // Minuten zu Millisekunden
          
          if (elapsed >= totalDuration) {
            // Eroberung ist abgeschlossen
            state.completeConquest(companionId);
          }
        });
      },

      // Börse-System Funktionen
      addStockToPortfolio: (stockId, shares, currency) => {
        const state = get();
        const existingPosition = state.stockMarket.portfolio.find(
          position => position.stockId === stockId && position.currency === currency
        );

        if (existingPosition) {
          // Aktualisiere bestehende Position
          set((state) => ({
            stockMarket: {
              ...state.stockMarket,
              portfolio: state.stockMarket.portfolio.map(position =>
                position.id === existingPosition.id
                  ? { ...position, shares: position.shares + shares }
                  : position
              )
            }
          }));
        } else {
          // Füge neue Position hinzu
          const newPosition = {
            id: `portfolio_${Date.now()}_${Math.random()}`,
            stockId,
            shares,
            currency
          };
          set((state) => ({
            stockMarket: {
              ...state.stockMarket,
              portfolio: [...state.stockMarket.portfolio, newPosition]
            }
          }));
        }

        // Aktualisiere globale Anteile
        const currentTotal = state.stockMarket.globalMarket.totalSharesOwned[stockId] || 0;
        const newTotal = currentTotal + shares;
        
        set((state) => ({
          stockMarket: {
            ...state.stockMarket,
            globalMarket: {
              ...state.stockMarket.globalMarket,
              totalSharesOwned: {
                ...state.stockMarket.globalMarket.totalSharesOwned,
                [stockId]: newTotal
              },
              marketActivity: [...state.stockMarket.globalMarket.marketActivity, {
                id: `activity_${Date.now()}_${Math.random()}`,
                stockId,
                type: 'buy' as const,
                amount: shares,
                price: 0,
                timestamp: new Date().toISOString(),
                currency,
                playerType: 'real' as const
              }]
            }
          }
        }));
      },

      removeStockFromPortfolio: (stockId, shares, currency) => {
        const state = get();
        const existingPosition = state.stockMarket.portfolio.find(
          position => position.stockId === stockId && position.currency === currency
        );

        if (existingPosition) {
          const newShares = existingPosition.shares - shares;
          if (newShares <= 0) {
            // Entferne Position komplett
            set((state) => ({
              stockMarket: {
                ...state.stockMarket,
                portfolio: state.stockMarket.portfolio.filter(
                  position => !(position.stockId === stockId && position.currency === currency)
                )
              }
            }));
          } else {
            // Aktualisiere Position
            set((state) => ({
              stockMarket: {
                ...state.stockMarket,
                portfolio: state.stockMarket.portfolio.map(position =>
                  position.id === existingPosition.id
                    ? { ...position, shares: newShares }
                    : position
                )
              }
            }));
          }

          // Aktualisiere globale Anteile
          const currentTotal = state.stockMarket.globalMarket.totalSharesOwned[stockId] || 0;
          const newTotal = Math.max(0, currentTotal - shares);
          
          set((state) => ({
            stockMarket: {
              ...state.stockMarket,
              globalMarket: {
                ...state.stockMarket.globalMarket,
                totalSharesOwned: {
                  ...state.stockMarket.globalMarket.totalSharesOwned,
                  [stockId]: newTotal
                },
                marketActivity: [...state.stockMarket.globalMarket.marketActivity, {
                  id: `activity_${Date.now()}_${Math.random()}`,
                  stockId,
                  type: 'sell' as const,
                  amount: shares,
                  price: 0,
                  timestamp: new Date().toISOString(),
                  currency,
                  playerType: 'real' as const
                }]
              }
            }
          }));
        }
      },

      addTradeToHistory: (stockId, type, amount, price, currency) => {
        const newTrade = {
          id: `trade_${Date.now()}_${Math.random()}`,
          stockId,
          type,
          amount,
          price,
          timestamp: new Date().toISOString(),
          currency
        };

        set((state) => {
          return {
            stockMarket: {
              ...state.stockMarket,
              tradeHistory: [...state.stockMarket.tradeHistory, newTrade]
            }
          };
        });
      },

      getStockPortfolio: () => {
        const state = get();
        return state.stockMarket.portfolio;
      },

      getStockTradeHistory: () => {
        const state = get();
        return state.stockMarket.tradeHistory;
      },

      // Globale Aktienmarkt-Funktionen
      getGlobalSharesOwned: (stockId: string) => {
        const state = get();
        return state.stockMarket.globalMarket.totalSharesOwned[stockId] || 0;
      },

      updateGlobalSharesOwned: (stockId: string, amount: number, type: 'buy' | 'sell') => {
        set((state) => {
          const currentTotal = state.stockMarket.globalMarket.totalSharesOwned[stockId] || 0;
          const newTotal = type === 'buy' 
            ? currentTotal + amount 
            : Math.max(0, currentTotal - amount);
          
          return {
            stockMarket: {
              ...state.stockMarket,
              globalMarket: {
                ...state.stockMarket.globalMarket,
                totalSharesOwned: {
                  ...state.stockMarket.globalMarket.totalSharesOwned,
                  [stockId]: newTotal
                }
              }
            }
          };
        });
      },

      addGlobalMarketActivity: (stockId: string, type: 'buy' | 'sell', amount: number, price: number, currency: 'coins' | 'gems', playerType: 'real' | 'virtual') => {
        const newActivity = {
          id: `activity_${Date.now()}_${Math.random()}`,
          stockId,
          type,
          amount,
          price,
          timestamp: new Date().toISOString(),
          currency,
          playerType
        };

        set((state) => {
          return {
            stockMarket: {
              ...state.stockMarket,
              globalMarket: {
                ...state.stockMarket.globalMarket,
                marketActivity: [...state.stockMarket.globalMarket.marketActivity, newActivity]
              }
            }
          };
        });
      },

      getVirtualPlayers: () => {
        const state = get();
        return state.stockMarket.globalMarket.virtualPlayers;
      },

      addVirtualPlayer: (player: {
        id: string;
        name: string;
        portfolio: Array<{
          stockId: string;
          shares: number;
          currency: 'coins' | 'gems';
        }>;
        lastTradeTime: string;
        budget: {
          coins: number;
          gems: number;
        };
        tradingStyle: 'aggressive' | 'conservative';
        riskTolerance: number;
      }) => {
        set((state) => ({
          stockMarket: {
            ...state.stockMarket,
            globalMarket: {
              ...state.stockMarket.globalMarket,
              virtualPlayers: [...state.stockMarket.globalMarket.virtualPlayers, player]
            }
          }
        }));
      },

      updateVirtualPlayerPortfolio: (playerId: string, stockId: string, shares: number, currency: 'coins' | 'gems') => {
        set((state) => ({
          stockMarket: {
            ...state.stockMarket,
            globalMarket: {
              ...state.stockMarket.globalMarket,
              virtualPlayers: state.stockMarket.globalMarket.virtualPlayers.map(player => {
                if (player.id === playerId) {
                  const existingPosition = player.portfolio.find(pos => pos.stockId === stockId && pos.currency === currency);
                  if (existingPosition) {
                    return {
                      ...player,
                      portfolio: player.portfolio.map(pos =>
                        pos.stockId === stockId && pos.currency === currency
                          ? { ...pos, shares: pos.shares + shares }
                          : pos
                      ),
                      lastTradeTime: new Date().toISOString()
                    };
                  } else {
                    return {
                      ...player,
                      portfolio: [...player.portfolio, { stockId, shares, currency }],
                      lastTradeTime: new Date().toISOString()
                    };
                  }
                }
                return player;
              })
            }
          }
        }));
      },

      // Aktienpreis-Hilfsfunktionen
      updateStockPrice: (stockId: string, priceData: {
        currentPrice: number;
        previousPrice: number;
        change: number;
        changePercent: number;
        volume: number;
        high24h: number;
        low24h: number;
        marketCap: number;
        priceHistory: Array<{ timestamp: number; price: number }>;
      }) => {
        set((state) => ({
          stockMarket: {
            ...state.stockMarket,
            stockPrices: {
              ...state.stockMarket.stockPrices,
              [stockId]: {
                ...priceData,
                lastUpdate: Date.now(),
                volatility: Math.abs(priceData.changePercent) / 100,
                trend: priceData.changePercent > 0.5 ? 'up' : priceData.changePercent < -0.5 ? 'down' : 'stable'
              }
            }
          }
        }));
      },

      getStockPrice: (stockId: string) => {
        const state = get();
        return state.stockMarket.stockPrices[stockId] || null;
      },

      getAllStockPrices: () => {
        const state = get();
        return state.stockMarket.stockPrices;
      },

      initializeStockPrices: (stockData: Array<{
        id: string;
        currentPrice: number;
        previousPrice: number;
        change: number;
        changePercent: number;
        volume: number;
        high24h: number;
        low24h: number;
        marketCap: number;
        priceHistory: Array<{ timestamp: number; price: number }>;
      }>) => {
        const now = Date.now();
        const stockPrices: { [stockId: string]: any } = {};
        
        stockData.forEach(stock => {
          stockPrices[stock.id] = {
            currentPrice: stock.currentPrice,
            previousPrice: stock.previousPrice,
            change: stock.change,
            changePercent: stock.changePercent,
            volume: stock.volume,
            high24h: stock.high24h,
            low24h: stock.low24h,
            marketCap: stock.marketCap,
            priceHistory: stock.priceHistory,
            lastUpdate: now,
            volatility: Math.abs(stock.changePercent) / 100,
            trend: stock.changePercent > 0.5 ? 'up' : stock.changePercent < -0.5 ? 'down' : 'stable'
          };
        });

        set((state) => ({
          stockMarket: {
            ...state.stockMarket,
            stockPrices
          }
        }));
      }
    }),
    {
      name: 'garden-clicker-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Migration: Stelle sicher, dass alle Gartenbereiche existieren
          const missingAreas: any = {};
          Object.keys(initialGardenAreas).forEach(areaKey => {
            if (!state.gardenAreas[areaKey as keyof typeof initialGardenAreas]) {
              missingAreas[areaKey] = initialGardenAreas[areaKey as keyof typeof initialGardenAreas];
            }
          });
          
          if (Object.keys(missingAreas).length > 0) {
            state.gardenAreas = { ...state.gardenAreas, ...missingAreas };
          }
          
          // Migration: Stelle sicher, dass alle Upgrades existieren
          const defaultUpgrades = {
            magicSeeds: 0,
            gardenGnome: 0,
            rainMaker: 0,
            composter: 0,
            goldDetector: 0,
            timeAccelerator: 0,
            luckCharm: 0,
            autoHarvester: 0,
            megaMultiplier: 0,
            crystalGenerator: 0,
            quantumShovel: 0,
            gardenBot: 0,
            plantWhisperer: 0,
            seedVault: 0,
            soilAnalyzer: 0,
            weatherStation: 0,
            beeHive: 0,
            treeFarm: 0,
            flowerPower: 0,
            herbGarden: 0,
            butterflyGarden: 0,
            rockGarden: 0,
            waterFeature: 0,
            gardenLighting: 0,
          };
          
          Object.keys(defaultUpgrades).forEach(upgradeKey => {
            if (state.upgrades[upgradeKey as keyof typeof defaultUpgrades] === undefined) {
              state.upgrades[upgradeKey as keyof typeof defaultUpgrades] = 0;
            }
          });
          
          // Migration: Stelle sicher, dass achievements existiert
          if (!state.achievements) {
            state.achievements = {
              totalClicks: 0,
              totalCoinsEarned: 0,
              areasRenovated: 0,
              matchThreeWins: 0,
              upgradesPurchased: 0,
              powerUpsUsed: 0,
              perfectMatchThreeGames: 0,
              dailyStreakRecord: 0,
              totalExperienceGained: 0,
            };
          }
          
          // Migration: Stelle sicher, dass alle achievement-Felder existieren
          const defaultAchievements = {
            totalClicks: 0,
            totalCoinsEarned: 0,
            areasRenovated: 0,
            matchThreeWins: 0,
            upgradesPurchased: 0,
            powerUpsUsed: 0,
            perfectMatchThreeGames: 0,
            dailyStreakRecord: 0,
            totalExperienceGained: 0,
          };
          
                     Object.keys(defaultAchievements).forEach(achievementKey => {
             if (state.achievements[achievementKey as keyof typeof defaultAchievements] === undefined) {
               state.achievements[achievementKey as keyof typeof defaultAchievements] = 0;
             }
           });
           
           // Migration: Stelle sicher, dass powerUps existiert
           if (!state.powerUps) {
             state.powerUps = {
               goldRush: { isActive: false, timeLeft: 0, endTime: undefined },
               speedBoost: { isActive: false, timeLeft: 0, endTime: undefined },
               luckyCharm: { isActive: false, timeLeft: 0, endTime: undefined },
               megaMultiplier: { isActive: false, timeLeft: 0, endTime: undefined },
               gemStorm: { isActive: false, timeLeft: 0, endTime: undefined },
               timeWarp: { isActive: false, timeLeft: 0, endTime: undefined },
               gardenBlessing: { isActive: false, timeLeft: 0, endTime: undefined },
               clickFrenzy: { isActive: false, timeLeft: 0, endTime: undefined },
               diamondMidas: { isActive: false, timeLeft: 0, endTime: undefined },
               infiniteEnergy: { isActive: false, timeLeft: 0, endTime: undefined },
               cosmicBoost: { isActive: false, timeLeft: 0, endTime: undefined },
               dragonPower: { isActive: false, timeLeft: 0, endTime: undefined },
               experienceRush: { isActive: false, timeLeft: 0, endTime: undefined },
             };
           }
           
                        // Migration: Stelle sicher, dass alle Power-Up-Felder existieren
             const defaultPowerUps = {
               megaMultiplier: { isActive: false, timeLeft: 0, endTime: undefined },
               gemStorm: { isActive: false, timeLeft: 0, endTime: undefined },
               timeWarp: { isActive: false, timeLeft: 0, endTime: undefined },
               gardenBlessing: { isActive: false, timeLeft: 0, endTime: undefined },
               clickFrenzy: { isActive: false, timeLeft: 0, endTime: undefined },
               diamondMidas: { isActive: false, timeLeft: 0, endTime: undefined },
               infiniteEnergy: { isActive: false, timeLeft: 0, endTime: undefined },
               cosmicBoost: { isActive: false, timeLeft: 0, endTime: undefined },
               dragonPower: { isActive: false, timeLeft: 0, endTime: undefined },
               experienceRush: { isActive: false, timeLeft: 0, endTime: undefined },
             };
             
             // Migration: Füge endTime zu bestehenden Power-Ups hinzu
             Object.keys(state.powerUps).forEach(key => {
               const powerUp = state.powerUps[key as keyof typeof state.powerUps];
               if (powerUp && typeof powerUp === 'object' && !('endTime' in powerUp)) {
                 (powerUp as any).endTime = undefined;
               }
             });
           
           Object.keys(defaultPowerUps).forEach(powerUpKey => {
             if (!state.powerUps[powerUpKey as keyof typeof defaultPowerUps]) {
               state.powerUps[powerUpKey as keyof typeof defaultPowerUps] = defaultPowerUps[powerUpKey as keyof typeof defaultPowerUps];
             }
           });
           

           
           // Migration: Stelle sicher, dass companions existiert
           if (!state.companions) {
             state.companions = {
               discovered: [],
               active: [],
               pendingCards: null,
               freeDrawLastUsed: null,
               freeDrawAvailable: true,
               freePremiumSpinLastUsed: null,
               freePremiumSpinAvailable: true,
               freePremiumSpinActive: false,
               training: [],
               research: {
                 // Forschungs-Projekte
                 projects: {},
                 // Globale Boni (additiv zu allen Begleitern)
                 globalBonuses: {
                   attack: 0,
                   defense: 0,
                   hp: 0,
                   experience: 0,
                   abilityChance: 0,
                 },
                 // Skill-Tree-Reset-System
                 skillTreeReset: {
                   resetCount: 0,
                   permanentBonuses: {
                     attack: 0,
                     defense: 0,
                     hp: 0,
                     experience: 0,
                     abilityChance: 0,
                   },
                   lastResetDate: null,
                 },
               },
               forge: {
                 // Erz-Ressourcen
                 ore: {
                   iron: 0,
                   steel: 0,
                   mithril: 0,
                   adamantium: 0,
                 },
                 // Rüstungen
                 armor: {},
                 // Waffen
                 weapons: {},
                 // Erz-Abbau
                 mining: {
                   activeMiners: {},
                 },
               },
               arena: {
                 level: 1,
                 difficultyMultiplier: 1.0,
                 resetCount: 0,
               },
               conquest: {
                 activeConquests: {},
                 territoryConquestCounts: {},
               },
             };
           }
           
           // Migration: Füge neue Begleiter-Felder hinzu
           if (state.companions.freeDrawLastUsed === undefined) {
             state.companions.freeDrawLastUsed = null;
           }
           if (state.companions.freeDrawAvailable === undefined) {
             state.companions.freeDrawAvailable = true;
           }
           if (state.companions.freePremiumSpinLastUsed === undefined) {
             state.companions.freePremiumSpinLastUsed = null;
           }
           if (state.companions.freePremiumSpinAvailable === undefined) {
             state.companions.freePremiumSpinAvailable = true;
           }
           if (state.companions.freePremiumSpinActive === undefined) {
             state.companions.freePremiumSpinActive = false;
           }
           
           // Zusätzliche Migration für freePremiumSpinActive
           if (!state.companions.hasOwnProperty('freePremiumSpinActive')) {
             state.companions.freePremiumSpinActive = false;
           }
           
           // Migration: Füge training-Array hinzu
           if (!state.companions.training) {
             state.companions.training = [];
           }
           
           // Migration: Füge arena-Objekt hinzu
           if (!state.companions.arena) {
             state.companions.arena = {
               level: 1,
               difficultyMultiplier: 1.0,
               resetCount: 0,
             };
           }
           
           // Migration: Konvertiere alte single-companion zu array
           if (state.companions.active && !Array.isArray(state.companions.active)) {
             state.companions.active = [state.companions.active as any];
           } else if (!state.companions.active) {
             state.companions.active = [];
           }
           
           // Migration: Füge neue Begleiter-Felder hinzu
           state.companions.discovered = state.companions.discovered.map(companion => ({
             ...companion,
             level: companion.level || 1,
             experience: companion.experience || 0,
             isTraining: companion.isTraining || false,
             isOnJourney: companion.isOnJourney || false
           }));
           
           // Migration: Stelle sicher, dass matchThreeGame existiert
           if (!state.matchThreeGame) {
             state.matchThreeGame = {
               isUnlocked: false,
               currentLevel: 1,
               completedLevels: [],
               highScore: 0,
               consecutiveWins: 0,
               powerUpsUnlocked: [],
             };
           }
           
           // Migration: Stelle sicher, dass prestige existiert
           if (!state.prestige) {
             state.prestige = {
               level: 0,
               points: 0,
               totalPoints: 0,
               permanentBonuses: {
                 clickMultiplier: 0,
                 incomeMultiplier: 0,
                 xpMultiplier: 0,
                 startingCoins: 0,
                 gemChance: 0,
                 autoClickerEfficiency: 0,
                 upgradeDiscount: 0,
                 gardenEfficiency: 0,
               },
               lastPrestigeDate: '',
               totalPrestiges: 0,
               megaPrestige: {
                 level: 0,
                 totalPointsSpent: 0,
                 globalMultiplier: 1,
                 lastMegaPrestigeDate: '',
               },
             };
           }
           
           // Migration: Stelle sicher, dass megaPrestige existiert
           if (!state.prestige.megaPrestige) {
             state.prestige.megaPrestige = {
               level: 0,
               totalPointsSpent: 0,
               globalMultiplier: 1,
               lastMegaPrestigeDate: '',
             };
           }
           
           // Migration: Stelle sicher, dass premiumAutoClicker existiert
           if (!state.premiumAutoClicker) {
             state.premiumAutoClicker = {
               isActive: false,
               costPerSecond: 1000,
               lastTickTime: Date.now(),
               clicksPerSecond: 1,
             };
           }
           
           // Migration: Füge clicksPerSecond hinzu falls es fehlt
           if (state.premiumAutoClicker && state.premiumAutoClicker.clicksPerSecond === undefined) {
             state.premiumAutoClicker.clicksPerSecond = 1;
           }
           
           // Migration: Begrenze clicksPerSecond auf 25 für bestehende Saves
           if (state.premiumAutoClicker && state.premiumAutoClicker.clicksPerSecond > 25) {
             state.premiumAutoClicker.clicksPerSecond = 25;
             state.premiumAutoClicker.costPerSecond = 25 * 1000;
           }
           
           // Migration: Stelle sicher, dass globalMultipliers existiert
           if (!state.globalMultipliers) {
             state.globalMultipliers = {
               totalMultiplier: 1,
               sacrificeCount: 0,
             };
           }
           
           // Migration: Füge neue globalMultipliers Felder hinzu
           if (state.globalMultipliers.totalMultiplier === undefined) {
             state.globalMultipliers.totalMultiplier = 1;
           }
           if (state.globalMultipliers.sacrificeCount === undefined) {
             state.globalMultipliers.sacrificeCount = 0;
           }

           // Migration: Stelle sicher, dass settings existiert
           if (!state.settings) {
             state.settings = {
               musicEnabled: false,
               soundEnabled: true,
               autoSaveEnabled: true,
               notifications: true,
               performanceMonitorEnabled: false,
             };
           }

           // Migration: Füge musicEnabled hinzu falls es fehlt
           if (state.settings.musicEnabled === undefined) {
             state.settings.musicEnabled = false;
           }

           // Migration: Füge conquest hinzu falls es fehlt
           if (!state.companions.conquest) {
             state.companions.conquest = {
               activeConquests: {},
               territoryConquestCounts: {},
             };
           }
           
           // Migration: Füge territoryConquestCounts hinzu falls es fehlt
           if (!state.companions.conquest.territoryConquestCounts) {
             state.companions.conquest.territoryConquestCounts = {};
           }
           
           // Migration: Füge Offline-Einkommen System hinzu
           if (state.lastSaveTime === undefined) {
             state.lastSaveTime = Date.now();
           }
           if (state.offlineEndTime === undefined) {
             state.offlineEndTime = Date.now();
           }
           if (state.lastActivityTime === undefined) {
             state.lastActivityTime = Date.now();
           }
           if (state.offlineIncome === undefined) {
             state.offlineIncome = 0;
           }
           if (state.offlineGems === undefined) {
             state.offlineGems = 0;
           }
           if (state.offlineCompanion === undefined) {
             state.offlineCompanion = null;
           }
           
           // Migration: Füge proceduralTools hinzu
           if (!state.proceduralTools) {
             state.proceduralTools = {};
           }
           
           // Migration: Füge Forschungs-System hinzu
           if (!state.companions.research) {
             state.companions.research = {
               projects: {
                 // === KAMPF UPGRADES ===
                 'attack_boost_1': {
                   id: 'attack_boost_1',
                   name: 'Grundangriff',
                   description: 'Erhöht den Angriff aller Begleiter',
                   cost: 1000,
                   duration: 5,
                   isActive: false,
                   bonusType: 'attack',
                   bonusValue: 5,
                   completed: false,
                 },
                 'attack_boost_2': {
                   id: 'attack_boost_2',
                   name: 'Verstärkter Angriff',
                   description: 'Weiterer Angriffsbonus',
                   cost: 2500,
                   duration: 10,
                   isActive: false,
                   bonusType: 'attack',
                   bonusValue: 10,
                   completed: false,
                 },
                 'attack_boost_3': {
                   id: 'attack_boost_3',
                   name: 'Angriffs-Verstärkung III',
                   description: 'Erhöht den Angriff aller Begleiter um weitere 50',
                   cost: 500000,
                   duration: 120,
                   isActive: false,
                   bonusType: 'attack',
                   bonusValue: 50,
                   completed: false,
                 },
                 'defense_boost_1': {
                   id: 'defense_boost_1',
                   name: 'Grundverteidigung',
                   description: 'Erhöht die Verteidigung aller Begleiter',
                   cost: 1000,
                   duration: 5,
                   isActive: false,
                   bonusType: 'defense',
                   bonusValue: 5,
                   completed: false,
                 },
                 'hp_boost_1': {
                   id: 'hp_boost_1',
                   name: 'Vitalität',
                   description: 'Erhöht die Gesundheit aller Begleiter',
                   cost: 1000,
                   duration: 5,
                   isActive: false,
                   bonusType: 'hp',
                   bonusValue: 10,
                   completed: false,
                 },

                 'defense_boost_2': {
                   id: 'defense_boost_2',
                   name: 'Verstärkte Verteidigung',
                   description: 'Weiterer Verteidigungsbonus',
                   cost: 2500,
                   duration: 10,
                   isActive: false,
                   bonusType: 'defense',
                   bonusValue: 10,
                   completed: false,
                 },
                 'hp_boost_2': {
                   id: 'hp_boost_2',
                   name: 'Erweiterte Vitalität',
                   description: 'Weiterer Gesundheitsbonus',
                   cost: 2500,
                   duration: 10,
                   isActive: false,
                   bonusType: 'hp',
                   bonusValue: 20,
                   completed: false,
                 },
                 'experience_boost_1': {
                   id: 'experience_boost_1',
                   name: 'Lerntempo',
                   description: 'Begleiter erhalten mehr Erfahrung',
                   cost: 5000,
                   duration: 15,
                   isActive: false,
                   bonusType: 'experience',
                   bonusValue: 25,
                   completed: false,
                 },
                 'ability_chance_1': {
                   id: 'ability_chance_1',
                   name: 'Fähigkeitsmeisterung',
                   description: 'Erhöht die Chance auf Spezialfähigkeiten',
                   cost: 5000,
                   duration: 15,
                   isActive: false,
                   bonusType: 'abilityChance',
                   bonusValue: 15,
                   completed: false,
                 },
                 'combat_mastery': {
                   id: 'combat_mastery',
                   name: 'Kampfmeisterschaft',
                   description: 'Ultimative Kampffähigkeiten',
                   cost: 10000,
                   duration: 20,
                   isActive: false,
                   bonusType: 'attack',
                   bonusValue: 25,
                   completed: false,
                 },
                 'meditation_mastery': {
                   id: 'meditation_mastery',
                   name: 'Meditations-Meisterschaft',
                   description: 'Spirituelle Erleuchtung',
                   cost: 8000,
                   duration: 18,
                   isActive: false,
                   bonusType: 'experience',
                   bonusValue: 50,
                   completed: false,
                 },
                 'arena_mastery': {
                   id: 'arena_mastery',
                   name: 'Arena-Meisterschaft',
                   description: 'Perfekte Kampftechniken',
                   cost: 12000,
                   duration: 25,
                   isActive: false,
                   bonusType: 'defense',
                   bonusValue: 30,
                   completed: false,
                 },
                 'divine_bond': {
                   id: 'divine_bond',
                   name: 'Göttliche Verbindung',
                   description: 'Transzendentale Macht',
                   cost: 20000,
                   duration: 30,
                   isActive: false,
                   bonusType: 'abilityChance',
                   bonusValue: 35,
                   completed: false,
                 },
                 'ancient_wisdom': {
                   id: 'ancient_wisdom',
                   name: 'Uralte Weisheit',
                   description: 'Wissen der Ahnen',
                   cost: 25000,
                   duration: 35,
                   isActive: false,
                   bonusType: 'experience',
                   bonusValue: 100,
                   completed: false,
                 },
                 'transcendence': {
                   id: 'transcendence',
                   name: 'Transzendenz',
                   description: 'Überwindung aller Grenzen',
                   cost: 50000,
                   duration: 60,
                   isActive: false,
                   bonusType: 'attack',
                   bonusValue: 100,
                   completed: false,
                 },


                 // === ERFAHRUNGS UPGRADES ===
                 'experience_boost_2': {
                   id: 'experience_boost_2',
                   name: 'Erfahrungs-Boost II',
                   description: 'Erhöht die Erfahrungsgewinnung aller Begleiter um weitere 40%',
                   cost: 300000,
                   duration: 120,
                   isActive: false,
                   bonusType: 'experience',
                   bonusValue: 40,
                   completed: false,
                 },
                 'experience_boost_3': {
                   id: 'experience_boost_3',
                   name: 'Erfahrungs-Boost III',
                   description: 'Erhöht die Erfahrungsgewinnung aller Begleiter um weitere 75%',
                   cost: 1000000,
                   duration: 240,
                   isActive: false,
                   bonusType: 'experience',
                   bonusValue: 75,
                   completed: false,
                 },


               },
                               globalBonuses: {
                  attack: 0,
                  defense: 0,
                  hp: 0,
                  experience: 0,
                  abilityChance: 0,
                },
                skillTreeReset: {
                  resetCount: 0,
                  permanentBonuses: {
                    attack: 0,
                    defense: 0,
                    hp: 0,
                    experience: 0,
                    abilityChance: 0,
                  },
                  lastResetDate: null,
                },
              };
            }

            // Migration: Erweitere Forschungs-System mit allen Skill-Tree Projekten
            if (state.companions.research) {
                             // Definiere alle Projekte aus dem Skill-Tree
               const allSkillTreeProjects: { [key: string]: {
                 id: string;
                 name: string;
                 description: string;
                 cost: number;
                 duration: number;
                 isActive: boolean;
                 bonusType: 'attack' | 'defense' | 'hp' | 'experience' | 'abilityChance';
                 bonusValue: number;
                 completed: boolean;
               }} = {
                // Tier 1 - Grundlagen
                'attack_boost_1': {
                  id: 'attack_boost_1',
                  name: 'Grundangriff',
                  description: 'Erhöht den Angriff aller Begleiter',
                  cost: 1000,
                  duration: 5,
                  isActive: false,
                  bonusType: 'attack',
                  bonusValue: 5,
                  completed: false,
                },
                'defense_boost_1': {
                  id: 'defense_boost_1',
                  name: 'Grundverteidigung',
                  description: 'Erhöht die Verteidigung aller Begleiter',
                  cost: 1000,
                  duration: 5,
                  isActive: false,
                  bonusType: 'defense',
                  bonusValue: 5,
                  completed: false,
                },
                'hp_boost_1': {
                  id: 'hp_boost_1',
                  name: 'Vitalität',
                  description: 'Erhöht die Gesundheit aller Begleiter',
                  cost: 1000,
                  duration: 5,
                  isActive: false,
                  bonusType: 'hp',
                  bonusValue: 10,
                  completed: false,
                },
                // Tier 2 - Fortgeschrittene Grundlagen
                'attack_boost_2': {
                  id: 'attack_boost_2',
                  name: 'Verstärkter Angriff',
                  description: 'Weiterer Angriffsbonus',
                  cost: 2500,
                  duration: 10,
                  isActive: false,
                  bonusType: 'attack',
                  bonusValue: 10,
                  completed: false,
                },
                'defense_boost_2': {
                  id: 'defense_boost_2',
                  name: 'Verstärkte Verteidigung',
                  description: 'Weiterer Verteidigungsbonus',
                  cost: 2500,
                  duration: 10,
                  isActive: false,
                  bonusType: 'defense',
                  bonusValue: 10,
                  completed: false,
                },
                'hp_boost_2': {
                  id: 'hp_boost_2',
                  name: 'Erweiterte Vitalität',
                  description: 'Weiterer Gesundheitsbonus',
                  cost: 2500,
                  duration: 10,
                  isActive: false,
                  bonusType: 'hp',
                  bonusValue: 20,
                  completed: false,
                },
                // Tier 3 - Spezialisierungen
                'experience_boost_1': {
                  id: 'experience_boost_1',
                  name: 'Lerntempo',
                  description: 'Begleiter erhalten mehr Erfahrung',
                  cost: 5000,
                  duration: 15,
                  isActive: false,
                  bonusType: 'experience',
                  bonusValue: 25,
                  completed: false,
                },
                'ability_chance_1': {
                  id: 'ability_chance_1',
                  name: 'Fähigkeitsmeisterung',
                  description: 'Erhöht die Chance auf Spezialfähigkeiten',
                  cost: 5000,
                  duration: 15,
                  isActive: false,
                  bonusType: 'abilityChance',
                  bonusValue: 15,
                  completed: false,
                },
                // Tier 4 - Kampfmeisterschaft
                'combat_mastery': {
                  id: 'combat_mastery',
                  name: 'Kampfmeisterschaft',
                  description: 'Ultimative Kampffähigkeiten',
                  cost: 10000,
                  duration: 20,
                  isActive: false,
                  bonusType: 'attack',
                  bonusValue: 25,
                  completed: false,
                },
                'meditation_mastery': {
                  id: 'meditation_mastery',
                  name: 'Meditations-Meisterschaft',
                  description: 'Spirituelle Erleuchtung',
                  cost: 8000,
                  duration: 18,
                  isActive: false,
                  bonusType: 'experience',
                  bonusValue: 50,
                  completed: false,
                },
                'arena_mastery': {
                  id: 'arena_mastery',
                  name: 'Arena-Meisterschaft',
                  description: 'Perfekte Kampftechniken',
                  cost: 12000,
                  duration: 25,
                  isActive: false,
                  bonusType: 'defense',
                  bonusValue: 30,
                  completed: false,
                },
                // Tier 5 - Legendäre Fähigkeiten
                'divine_bond': {
                  id: 'divine_bond',
                  name: 'Göttliche Verbindung',
                  description: 'Transzendentale Macht',
                  cost: 20000,
                  duration: 30,
                  isActive: false,
                  bonusType: 'abilityChance',
                  bonusValue: 35,
                  completed: false,
                },
                'ancient_wisdom': {
                  id: 'ancient_wisdom',
                  name: 'Uralte Weisheit',
                  description: 'Wissen der Ahnen',
                  cost: 25000,
                  duration: 35,
                  isActive: false,
                  bonusType: 'experience',
                  bonusValue: 100,
                  completed: false,
                },
                // Tier 6 - Ultimative Macht
                'transcendence': {
                  id: 'transcendence',
                  name: 'Transzendenz',
                  description: 'Überwindung aller Grenzen',
                  cost: 50000,
                  duration: 60,
                  isActive: false,
                  bonusType: 'attack',
                  bonusValue: 100,
                  completed: false,
                },
                // Tier 7 - Kosmische Kräfte
                'cosmic_awakening': {
                  id: 'cosmic_awakening',
                  name: 'Kosmische Erweckung',
                  description: 'Erwachen kosmischer Kräfte',
                  cost: 75000,
                  duration: 90,
                  isActive: false,
                  bonusType: 'defense',
                  bonusValue: 150,
                  completed: false,
                },
                'eternal_wisdom': {
                  id: 'eternal_wisdom',
                  name: 'Ewige Weisheit',
                  description: 'Wissen der Unsterblichen',
                  cost: 80000,
                  duration: 100,
                  isActive: false,
                  bonusType: 'experience',
                  bonusValue: 200,
                  completed: false,
                },
                'divine_perfection': {
                  id: 'divine_perfection',
                  name: 'Göttliche Vollkommenheit',
                  description: 'Perfektion der Götter',
                  cost: 100000,
                  duration: 120,
                  isActive: false,
                  bonusType: 'abilityChance',
                  bonusValue: 50,
                  completed: false,
                },
                // Tier 8 - Realitätskontrolle
                'reality_bending': {
                  id: 'reality_bending',
                  name: 'Realitätsverzerrung',
                  description: 'Verzerrung der Realität selbst',
                  cost: 150000,
                  duration: 150,
                  isActive: false,
                  bonusType: 'attack',
                  bonusValue: 250,
                  completed: false,
                },
                'time_mastery': {
                  id: 'time_mastery',
                  name: 'Zeit-Meisterschaft',
                  description: 'Kontrolle über die Zeit',
                  cost: 180000,
                  duration: 180,
                  isActive: false,
                  bonusType: 'hp',
                  bonusValue: 500,
                  completed: false,
                },
                'space_manipulation': {
                  id: 'space_manipulation',
                  name: 'Raum-Manipulation',
                  description: 'Kontrolle über den Raum',
                  cost: 200000,
                  duration: 200,
                  isActive: false,
                  bonusType: 'defense',
                  bonusValue: 300,
                  completed: false,
                },
                // Tier 9 - Schöpfungsmagie
                'creation_magic': {
                  id: 'creation_magic',
                  name: 'Schöpfungsmagie',
                  description: 'Magie der Schöpfung',
                  cost: 300000,
                  duration: 250,
                  isActive: false,
                  bonusType: 'attack',
                  bonusValue: 500,
                  completed: false,
                },
                'destruction_magic': {
                  id: 'destruction_magic',
                  name: 'Zerstörungsmagie',
                  description: 'Magie der Zerstörung',
                  cost: 350000,
                  duration: 300,
                  isActive: false,
                  bonusType: 'defense',
                  bonusValue: 600,
                  completed: false,
                },
                'life_magic': {
                  id: 'life_magic',
                  name: 'Lebensmagie',
                  description: 'Magie des Lebens',
                  cost: 400000,
                  duration: 350,
                  isActive: false,
                  bonusType: 'hp',
                  bonusValue: 1000,
                  completed: false,
                },
                // Tier 10 - Göttliche Attribute
                'omnipotence': {
                  id: 'omnipotence',
                  name: 'Allmacht',
                  description: 'Absolute Macht über alles',
                  cost: 500000,
                  duration: 400,
                  isActive: false,
                  bonusType: 'attack',
                  bonusValue: 1000,
                  completed: false,
                },
                'omniscience': {
                  id: 'omniscience',
                  name: 'Allwissenheit',
                  description: 'Wissen über alles',
                  cost: 600000,
                  duration: 450,
                  isActive: false,
                  bonusType: 'experience',
                  bonusValue: 500,
                  completed: false,
                },
                'omnipresence': {
                  id: 'omnipresence',
                  name: 'Allgegenwart',
                  description: 'Gegenwart überall',
                  cost: 700000,
                  duration: 500,
                  isActive: false,
                  bonusType: 'abilityChance',
                  bonusValue: 100,
                  completed: false,
                },
              };

              // Füge fehlende Projekte hinzu, ohne bestehende zu überschreiben
              Object.keys(allSkillTreeProjects).forEach(projectId => {
                if (!state.companions.research.projects[projectId]) {
                  state.companions.research.projects[projectId] = allSkillTreeProjects[projectId as keyof typeof allSkillTreeProjects];
                }
                             });
             }
             
             // Migration: Füge Skill-Tree-Reset-System hinzu
             if (!state.companions.research.skillTreeReset) {
               state.companions.research.skillTreeReset = {
                 resetCount: 0,
                 permanentBonuses: {
                   attack: 0,
                   defense: 0,
                   hp: 0,
                   experience: 0,
                   abilityChance: 0,
                 },
                 lastResetDate: null,
               };
             }
             
             // Migration: Füge Schmiede-System hinzu
            if (!state.companions.forge) {
              state.companions.forge = {
                ore: {
                  iron: 0,
                  steel: 0,
                  mithril: 0,
                  adamantium: 0,
                },
                armor: {},
                weapons: {},
                mining: {
                  activeMiners: {},
                },
              };
            }
           
           // Berechne Offline-Einkommen (mindestens 1 Minute, maximal 12 Stunden)
           const now = Date.now();
           
           // Prüfe ob der Spieler wirklich offline war (mindestens 20 Sekunden Inaktivität)
           const activityTimeDiff = now - state.lastActivityTime;
           const isReallyOffline = activityTimeDiff >= 20000; // 20 Sekunden
           
           const timeDiff = now - state.offlineEndTime; // Verwende offlineEndTime für präzise Berechnung
           const minutesDiff = timeDiff / (1000 * 60); // Minuten
           
           if (minutesDiff >= 1 && isReallyOffline) { // Mindestens 1 Minute offline UND wirklich offline
             // Begrenze auf maximal 12 Stunden (720 Minuten)
             const maxOfflineTime = 12 * 60; // 12 Stunden in Minuten
             const actualMinutesDiff = Math.min(minutesDiff, maxOfflineTime);
             const actualTimeDiff = actualMinutesDiff * 60 * 1000; // Zurück zu Millisekunden
             
             // Verwende den tatsächlichen Wert pro Sekunde aus dem Spiel (mit allen aktiven Boni)
             const actualCoinsPerSecond = state.getTotalCoinsPerSecond ? state.getTotalCoinsPerSecond() : 0;
             
             // Berechne Offline-Einkommen (1.5x der angezeigten Münzen pro Sekunde für bessere Belohnungen)
             const secondsDiff = actualTimeDiff / 1000;
             const offlineEarnings = Math.floor(actualCoinsPerSecond * secondsDiff * 1.5);
             
             // Berechne Offline-Diamanten (basierend auf Crystal Generator und anderen Diamanten-Quellen)
             const gemsPerSecond = (state.upgrades?.crystalGenerator || 0) * 0.1 / 60; // Crystal Generator pro Sekunde
             const offlineGems = Math.floor(gemsPerSecond * secondsDiff * 3); // 3x Multiplikator für Diamanten
             
             // Berechne Begleiter-Chance (0.2% pro Minute offline - nur wenn bereits Begleiter freigeschaltet wurden)
             const minutesOffline = actualMinutesDiff; // Verwende die begrenzte Zeit
             const companionChance = minutesOffline * 0.002; // 0.2% pro Minute
             let earnedCompanion = null;
             
             // Nur Begleiter erhalten, wenn bereits mindestens einer freigeschaltet wurde
             if (state.companions.discovered.length > 0 && Math.random() < companionChance) {
               // Generiere einen zufälligen Begleiter mit verbesserten Wahrscheinlichkeiten
               const companionTypes = ['common', 'rare', 'epic', 'legendary'] as const;
               const weights = [0.5, 0.3, 0.15, 0.05]; // Verbesserte Wahrscheinlichkeiten
               
               let random = Math.random();
               let selectedType: typeof companionTypes[number] = 'common';
               
               for (let i = 0; i < weights.length; i++) {
                 random -= weights[i];
                 if (random <= 0) {
                   selectedType = companionTypes[i];
                   break;
                 }
               }
               
               earnedCompanion = generateRandomCompanion();
               if (earnedCompanion) {
                 earnedCompanion.type = selectedType;
                 // Passe die Fähigkeiten basierend auf dem Typ an
                 earnedCompanion.abilities = earnedCompanion.abilities.map((ability: CompanionAbility) => ({
                   ...ability,
                   value: Math.floor(ability.value * (selectedType === 'legendary' ? 3 : selectedType === 'epic' ? 2 : selectedType === 'rare' ? 1.5 : 1))
                 }));
               }
             }
             
             // Offline-Training für Begleiter in der Kaserne
             let offlineTrainingProgress = 0;
             if (state.companions.training.length > 0) {
               // Berechne Training-Fortschritt basierend auf Offline-Zeit
               const trainingSeconds = Math.min(secondsDiff, 3600); // Maximal 1 Stunde Training pro Offline-Session
               const trainingTicks = Math.floor(trainingSeconds); // Jede Sekunde = 1 Training-Tick
               
               // Simuliere Training für jeden Begleiter in der Kaserne
               state.companions.training.forEach(companionId => {
                 const companion = state.companions.discovered.find(c => c.id === companionId);
                 if (companion) {
                   // Battle Level Training
                   const currentBattleLevel = companion.battleLevel || 1;
                   const currentBattleExp = companion.battleExperience || 0;
                   const battleExpGain = 5 * trainingTicks; // Reduziert von 50 auf 5 XP pro Tick (10x langsamer)
                   const newBattleExp = currentBattleExp + battleExpGain;
                   const requiredBattleExp = currentBattleLevel * 100;

                   let newBattleLevel = currentBattleLevel;
                   let finalBattleExp = newBattleExp;

                   if (newBattleExp >= requiredBattleExp) {
                     newBattleLevel++;
                     finalBattleExp = newBattleExp - requiredBattleExp;
                   }

                   // Normales Level Training
                   const currentNormalLevel = companion.level || 1;
                   const currentNormalExp = companion.experience || 0;
                   const normalExpGain = 3 * trainingTicks; // Reduziert von 30 auf 3 XP pro Tick (10x langsamer)
                   const newNormalExp = currentNormalExp + normalExpGain;
                   const requiredNormalExp = currentNormalLevel * 100;

                   let newNormalLevel = currentNormalLevel;
                   let finalNormalExp = newNormalExp;

                   if (newNormalExp >= requiredNormalExp) {
                     newNormalLevel++;
                     finalNormalExp = newNormalExp - requiredNormalExp;
                   }

                   // Update beide Level-Systeme
                   state.updateCompanionBattleStats(companionId, newBattleLevel, finalBattleExp);
                   state.updateCompanionNormalStats(companionId, newNormalLevel, finalNormalExp);
                   
                   offlineTrainingProgress += trainingTicks;
                 }
               });
             }
             
             // Setze Offline-Werte auch wenn nur Training vorhanden ist
             if (offlineEarnings > 0 || offlineGems > 0 || earnedCompanion || offlineTrainingProgress > 0) {
               state.offlineIncome = offlineEarnings;
               state.offlineGems = offlineGems;
               state.offlineCompanion = earnedCompanion;
               
               // Wenn nur Training vorhanden ist, setze minimales Offline-Einkommen
               if (offlineTrainingProgress > 0 && offlineEarnings === 0 && offlineGems === 0 && !earnedCompanion) {
                 state.offlineIncome = 1; // Minimales Einkommen um Anzeige zu triggern
               }
               
               // Füge Münzen hinzu
               if (offlineEarnings > 0) {
                 state.coins += offlineEarnings;
                 // Aktualisiere Achievements
                 if (state.achievements) {
                   state.achievements.totalCoinsEarned += offlineEarnings;
                 }
               }
               
               // Füge Diamanten hinzu
               if (offlineGems > 0) {
                 state.gems += offlineGems;
               }
               
               // Füge Begleiter hinzu
               if (earnedCompanion) {
                 state.addCompanionToCollection(earnedCompanion);
               }
             }
           }
           
           // Aktualisiere lastSaveTime und offlineEndTime
           state.lastSaveTime = now;
           state.offlineEndTime = now;
        }
      },



    }
  )
);